/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import {
  Download,
  Image as ImageIcon,
  Loader2,
  Plus,
  Trash2,
  RefreshCw,
  Sparkles,
  Type,
  Paintbrush,
  Newspaper,
  Megaphone,
  Trophy,
  Shield,
  Target,
  BookOpen,
  X,
  Maximize2,
  Copy,
  PackageOpen,
  Flame,
  Swords,
  ChevronLeft,
  ChevronRight,
  Users,
  Handshake,
  Rocket,
  TrendingUp,
  Hourglass,
  Film,
  Send,
  Crown,
  ScrollText,
  ChevronUp,
  ChevronDown,
  ArrowLeftRight,
  Heart,
} from "lucide-react";
import * as htmlToImage from "html-to-image";
import JSZip from "jszip";
import { toast } from "react-toastify";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  useGlobalLeaderboard,
  useLeaderboardByGameweek,
  useTopCaptains,
} from "@/hooks/api/useFantasyLeaderboard";
import {
  useGameweeks,
  useGameweekTotw,
  useGameweekEligiblePlayers,
  TOTW_FORMATIONS,
  type TotwFormation,
} from "@/hooks/api/useGameweeks";
import type { FantasyLeaderboardEntry } from "@/types/api.types";
import NewsCard from "./NewsCard";
import LeaderboardCard from "./LeaderboardCard";
import LfsCard from "./LfsCard";
import DailyChallengeCard from "./DailyChallengeCard";
import UserStoryCard from "./UserStoryCard";
import MatchdayCard from "./MatchdayCard";
import TeamOfWeekCard from "./TeamOfWeekCard";
import PartnerPitchCard from "./PartnerPitchCard";
import LeagueLaunchCard from "./LeagueLaunchCard";
import FbkLeagueCard from "./FbkLeagueCard";
import MatchResultCard from "./MatchResultCard";
import CountdownCard from "./CountdownCard";
import MatchStoryCard, { MATCH_STORY_PAGE_COUNT } from "./MatchStoryCard";
import CaptainPicksCard from "./CaptainPicksCard";
import FactsheetCard, {
  SCRIM_DEFAULTS as FACTSHEET_SCRIM_DEFAULTS,
} from "./FactsheetCard";
import BracketCard from "./BracketCard";
import ChampionsCard from "./ChampionsCard";
import ThankYouCard from "./ThankYouCard";
import { useBracket } from "@/hooks/api/usePhases";
import { getNationColors } from "./nationColors";
import { useMatchesAdmin } from "@/hooks/api/useMatches";
import { useMatchEvents } from "@/hooks/api/useEvents";
import { playersService } from "@/api/services/player.service";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import {
  useCreateStory,
  uploadStoryImage,
  type CreateStoryPayload,
} from "@/hooks/api/useStories";
import type { Match, MatchEvent } from "@/types/api.types";
import {
  BackgroundControls,
  ImageUploadField,
  TextStyleControls,
} from "./StyleControls";
import {
  BRAND_COLORS,
  CARD_FORMATS,
  defaultFooterLeft,
  THEMES_PER_CARD_TYPE,
  type CardFormat,
  type CardStyleSettings,
  type CardTheme,
  type CardType,
  type DailyChallengeCardContent,
  type DailyChallengeType,
  type FooterSettings,
  type HeaderSettings,
  type LeaderboardCardContent,
  type LeaderboardEntry,
  type LfsCardContent,
  type LfsNationPick,
  type MatchdayCardContent,
  type MatchdayTheme,
  type NewsCardContent,
  type StoryPage,
  type TeamOfWeekCardContent,
  type TeamOfWeekPlayerTile,
  type TextStyle,
  type UserStoryCardContent,
  type PartnerPitchCardContent,
  type PitchExtraPage,
  type LeagueLaunchCardContent,
  type FbkLeagueCardContent,
  type CountdownCardContent,
  type CountdownTheme,
  type MatchResultCardContent,
  type MatchResultTheme,
  type MatchStoryCardContent,
  type MatchResultGoal,
  type CaptainPicksCardContent,
  type CaptainPick,
  type FactsheetCardContent,
  type FactsheetSlide,
  type FactsheetVariant,
  type BracketCardContent,
  type BracketMatchup,
  type BracketRound,
  BRACKET_ROUND_LABEL,
  BRACKET_ROUND_MATCH_COUNT,
  type ChampionsCardContent,
  type ChampionsVariant,
  type ChampionsNationPreset,
  CHAMPIONS_NATION_PRESETS,
  type ThankYouCardContent,
  type ThankYouTheme,
  type ThankYouStat,
  type LeaderboardScope,
} from "./types";
import { THEME_META, themeSwatch } from "./theme";
import { autoEmbedImage } from "./imageEmbed";

interface CardTypeOption {
  value: CardType;
  label: string;
  description: string;
  icon: React.ElementType;
  iconBg: string;
}

const CARD_TYPES: CardTypeOption[] = [
  {
    value: "NEWS",
    label: "News / Fact",
    description: "Breaking news, did-you-know facts, quick highlights.",
    icon: Newspaper,
    iconBg: "bg-blue-500",
  },
  {
    value: "ANNOUNCEMENT",
    label: "Announcement",
    description: "Feature launches, schedule changes, official notices.",
    icon: Megaphone,
    iconBg: "bg-purple-500",
  },
  {
    value: "LEADERBOARD",
    label: "Leaderboard",
    description: "Top 10 managers for a matchweek with points.",
    icon: Trophy,
    iconBg: "bg-amber-500",
  },
  {
    value: "LFS_RESULT",
    label: "LFS Round",
    description: "Last Fan Standing — survivors, eliminated, top picks.",
    icon: Shield,
    iconBg: "bg-yellow-500",
  },
  {
    value: "DAILY_CHALLENGE",
    label: "Daily Challenge",
    description: "Tease today's challenge and drive plays.",
    icon: Target,
    iconBg: "bg-violet-500",
  },
  {
    value: "USER_STORY",
    label: "Player Story",
    description: "Long-form bio with portrait + atmosphere photo.",
    icon: BookOpen,
    iconBg: "bg-rose-500",
  },
  {
    value: "MATCHDAY",
    label: "Matchday",
    description: "Big poster for a fixture — flags, kickoff, venue, group.",
    icon: Flame,
    iconBg: "bg-orange-500",
  },
  {
    value: "TEAM_OF_WEEK",
    label: "Team of the Week",
    description: "Best XI of a gameweek on a pitch — auto-pulled from points.",
    icon: Users,
    iconBg: "bg-emerald-600",
  },
  {
    value: "PARTNER_PITCH",
    label: "Partner Pitch",
    description: "B2B card to convince a café/shop/academy to run their league.",
    icon: Handshake,
    iconBg: "bg-cyan-600",
  },
  {
    value: "LEAGUE_LAUNCH",
    label: "League Launch",
    description: "Co-branded card a partner posts to invite customers to their league.",
    icon: Rocket,
    iconBg: "bg-pink-600",
  },
  {
    value: "FBK_LEAGUE",
    label: "FBK League Hype",
    description: "FBK's own league post — mystery prizes, social proof, scroll-stopping.",
    icon: Sparkles,
    iconBg: "bg-yellow-500",
  },
  {
    value: "COUNTDOWN",
    label: "Countdown",
    description: "Days-to-kickoff card — mega number, 5 hand-tuned themes from calm to peak hype.",
    icon: Hourglass,
    iconBg: "bg-indigo-600",
  },
  {
    value: "MATCH_RESULT",
    label: "Match Result",
    description: "Post-match recap — 8 bespoke themes from cinematic gold-spotlight to neon synthwave.",
    icon: TrendingUp,
    iconBg: "bg-red-600",
  },
  {
    value: "MATCH_STORY",
    label: "Match Story",
    description: "3-page narrative deck — Cover, Result, Story. Auto-fills from any match + scorer.",
    icon: Film,
    iconBg: "bg-rose-700",
  },
  {
    value: "TOP_SCORERS",
    label: "Top Scorers",
    description: "Top 10 highest-scoring players of a gameweek. Fully editable rows.",
    icon: TrendingUp,
    iconBg: "bg-amber-600",
  },
  {
    value: "CAPTAIN_PICKS",
    label: "Captain Picks",
    description: "Top 10 most-captained players across user squads. Auto-seeds from Top Captains.",
    icon: Crown,
    iconBg: "bg-yellow-600",
  },
  {
    value: "FACTSHEET",
    label: "Factsheet",
    description: "Multi-slide rules/facts carousel — cinematic photo + bold headline + accent word.",
    icon: ScrollText,
    iconBg: "bg-slate-800",
  },
  {
    value: "BRACKET",
    label: "Knockout Bracket",
    description: "FBK-styled tournament bracket. Auto-seeds matchups from the backend phase. Picks any round (R32 → Final).",
    icon: Trophy,
    iconBg: "bg-emerald-700",
  },
  {
    value: "CHAMPIONS",
    label: "Champions",
    description: "Winner celebration poster. One-click swap between finalists — colours, flag, stars and title count all follow.",
    icon: Sparkles,
    iconBg: "bg-amber-500",
  },
  {
    value: "THANK_YOU",
    label: "Thank You",
    description: "Community wrap post. Seeds real stats + a username wall from your data. Four themes.",
    icon: Heart,
    iconBg: "bg-rose-500",
  },
];

const NEWS_PRESETS = ["BREAKING", "DID YOU KNOW", "TOP STORY", "MATCH RECAP"];
const ANNOUNCEMENT_PRESETS = [
  "ANNOUNCEMENT",
  "NEW FEATURE",
  "COMING SOON",
  "UPDATE",
];

const CHALLENGE_TYPES: DailyChallengeType[] = [
  "CAREER PATH",
  "MYSTERY CLUB",
  "HIGHER OR LOWER",
  "WHO AM I",
  "TIC TAC TOE",
  "TRUE OR FALSE",
  "MCQ",
];

const initialNewsContent: NewsCardContent = {
  eyebrow: "BREAKING",
  headline: "England crash out",
  goldHighlight: "in stoppage time.",
  body: "A late winner from the Netherlands sent the tournament favourites home. The fantasy table just got blown open — pick your survivors before midnight.",
  imageUrl: "",
  ctaText: "Play now",
  hashtags: "#WC2026 · #FBK",
};

const initialAnnouncementContent: NewsCardContent = {
  ...initialNewsContent,
  eyebrow: "ANNOUNCEMENT",
  headline: "Survivor leagues",
  goldHighlight: "are now live.",
  body: "Invite-only mini-leagues for Last Fan Standing. Beat your friends, last to survive wins. Set yours up before kickoff.",
  ctaText: "Open the app",
};

const initialLeaderboardContent: LeaderboardCardContent = {
  matchweek: 3,
  entries: [
    { rank: 1, teamName: "Sandeep XI", username: "sandeep", countryIso2: "np", points: 142 },
    { rank: 2, teamName: "Samba Squad", username: "marco", countryIso2: "br", points: 138 },
    { rank: 3, teamName: "Three Lions FC", username: "aisha", countryIso2: "gb", points: 134 },
    { rank: 4, teamName: "Dragon XI", username: "chenw", countryIso2: "cn", points: 128 },
    { rank: 5, teamName: "Atlas Heroes", username: "karim", countryIso2: "ma", points: 121 },
    { rank: 6, teamName: "Furia Roja", username: "sofia", countryIso2: "es", points: 117 },
    { rank: 7, teamName: "Boys In Green", username: "liam", countryIso2: "ie", points: 112 },
    { rank: 8, teamName: "Samurai Blue", username: "hana", countryIso2: "jp", points: 109 },
    { rank: 9, teamName: "La Albiceleste", username: "pablo", countryIso2: "ar", points: 104 },
    { rank: 10, teamName: "Oranje XI", username: "noah", countryIso2: "nl", points: 98 },
  ],
  hashtags: "#WC2026 · #FBK",
};

const initialLfsContent: LfsCardContent = {
  round: 4,
  survivors: 1248,
  eliminated: 312,
  topPicks: [
    { countryIso2: "br", countryName: "Brazil", count: 412 },
    { countryIso2: "ar", countryName: "Argentina", count: 287 },
    { countryIso2: "fr", countryName: "France", count: 198 },
  ],
  hashtags: "#WC2026 · #LFS · #FBK",
};

const initialDailyChallengeContent: DailyChallengeCardContent = {
  challengeType: "CAREER PATH",
  headline: "Can you solve",
  goldHighlight: "today's challenge?",
  subtext: "Trace the player from boyhood club to world stage. One guess, no clues.",
  ctaText: "Play now",
  hashtags: "#WC2026 · #FBK · #DAILYCHALLENGE",
};

const initialUserStoryContent: UserStoryCardContent = {
  pages: [
    {
      eyebrow: "THE STORY OF",
      name: "Lionel",
      goldHighlight: "Messi.",
      tagline: "The boy who refused to be small.",
      body:
        "Rosario, 1987. A boy is born so small the doctors say he'll never compete.\n\nThe diagnosis at eleven: growth hormone deficiency. Treatment costs more than his family can afford.",
      portraitImageUrl: "",
      statsLine: "Born 1987 · Argentina · 821 club goals · WC 2022 winner",
    },
  ],
  hashtags: "#WC2026 · #FBK · #STORY",
};

const BLANK_STORY_PAGE: StoryPage = {
  eyebrow: "CHAPTER",
  name: "",
  body: "",
};

const BLANK_TOTW_PLAYER: TeamOfWeekPlayerTile = {
  name: "—",
  position: "MID",
  points: 0,
};

/** Placeholder pitch with one tile in each position so the editor renders before a gameweek is picked. */
const initialTeamOfWeekContent: TeamOfWeekCardContent = {
  gameweekName: "Matchweek 03",
  eyebrow: "TEAM OF THE WEEK",
  formation: "4-4-2",
  totalPoints: 0,
  players: [
    { ...BLANK_TOTW_PLAYER, position: "FWD", name: "Forward 1" },
    { ...BLANK_TOTW_PLAYER, position: "FWD", name: "Forward 2" },
    { ...BLANK_TOTW_PLAYER, position: "MID", name: "Mid 1" },
    { ...BLANK_TOTW_PLAYER, position: "MID", name: "Mid 2" },
    { ...BLANK_TOTW_PLAYER, position: "MID", name: "Mid 3" },
    { ...BLANK_TOTW_PLAYER, position: "MID", name: "Mid 4" },
    { ...BLANK_TOTW_PLAYER, position: "DEF", name: "Def 1" },
    { ...BLANK_TOTW_PLAYER, position: "DEF", name: "Def 2" },
    { ...BLANK_TOTW_PLAYER, position: "DEF", name: "Def 3" },
    { ...BLANK_TOTW_PLAYER, position: "DEF", name: "Def 4" },
    { ...BLANK_TOTW_PLAYER, position: "GK", name: "Keeper" },
  ],
  hashtags: "#WC2026 · #FBK · #TOTW",
};

const initialPartnerPitchContent: PartnerPitchCardContent = {
  eyebrow: "FBK FOR CAFÉS",
  headline: "Run a Fantasy League",
  goldHighlight: "for your regulars.",
  partnerName: "Blue Café",
  partnerLogoUrl: "",
  partnerColor: "#1E40AF",
  steps: [
    { text: "We set up your league." },
    { text: "You post it on your socials." },
    { text: "Top 3 win your prize each matchweek." },
  ],
  benefits: [
    { text: "Zero setup or running cost" },
    { text: "Weekly repeat customers — every matchweek" },
    { text: "Free social-media content for your page" },
    { text: "Brand exposure inside FBK to football fans" },
  ],
  ctaText: "Reply to start your league",
  theme: "collab",
  hashtags: "#FBKForBusiness · #FBK",
  // Seed a 5-chapter walkthrough so admins see the multi-page shape immediately.
  // Each chapter is editable / removable / reorderable from the form.
  extraPages: [
    {
      eyebrow: "HOW FBK WORKS · 01",
      headline: "How a user",
      goldHighlight: "plays.",
      body:
        "→ Sign up free in 30 seconds\n" +
        "→ Pick 11 players from World Cup nations\n" +
        "→ Choose a captain (scores 2× points)\n" +
        "→ Lock your squad before kickoff\n" +
        "→ Earn points from real-match performance",
    },
    {
      eyebrow: "HOW FBK WORKS · 02",
      headline: "Your private",
      goldHighlight: "league.",
      body:
        "→ You get a 6-letter join code\n" +
        "→ Customers join with that code only\n" +
        "→ They play normal FBK — pick squads, score points\n" +
        "→ Their points appear on YOUR private leaderboard\n" +
        "→ Top 3 every matchweek win your prize",
    },
    {
      eyebrow: "FLEXIBILITY",
      headline: "Pick the format",
      goldHighlight: "that fits.",
      body:
        "→ Top 3 weekly  ·  classic, repeat traffic\n" +
        "→ Tournament champion  ·  single grand prize\n" +
        "→ Milestone reward  ·  anyone scoring 100+\n" +
        "→ Random draw  ·  lottery among all players\n" +
        "→ Watch-party qualifier  ·  drive a real event\n" +
        "→ Bring your own  ·  we power your existing event",
    },
    {
      eyebrow: "WHO DOES WHAT",
      headline: "You hand out",
      goldHighlight: "prizes. We do everything else.",
      body:
        "YOU\n" +
        "→ Decide the prize (voucher / kit / experience)\n" +
        "→ Promote it at your counter\n" +
        "→ Hand prizes to winners\n" +
        "\n" +
        "FBK\n" +
        "→ Builds + hosts the league\n" +
        "→ Tracks every point automatically\n" +
        "→ Sends you the leaderboard every Sunday",
    },
    {
      eyebrow: "LET'S TALK",
      headline: "Tell us your",
      goldHighlight: "World Cup event.",
      body:
        "Free to set up. Free to run. Live for the whole World Cup.\n" +
        "We'll build it around your shop.",
      ctaText: "Reply to this DM",
    },
  ],
};

const initialFbkLeagueContent: FbkLeagueCardContent = {
  eyebrow: "FBK LEAGUE · MATCHWEEK 01",
  headline: "PLAY.\nWIN.",
  goldHighlight: "REPEAT.",
  tagline: "The official FBK League is open. Every matchweek. Real prizes.",
  prizeTitle: "🏆 TOP 3 WIN",
  prizeText: "₹5,000 cash pool\nsplit across the podium",
  mysteryMode: true,
  mysteryRevealAt: "SUNDAY",
  mysteryTeaser: "Something legendary.",
  playerCountLabel: "🔥 12,438 PLAYERS ALREADY IN",
  joinCode: "FBK01",
  ctaText: "Tap to play",
  smallPrint: "Free · Open to every FBK user",
  theme: "spotlight",
  hashtags: "#WC2026 · #FBKLeague",
};

const initialCountdownContent: CountdownCardContent = {
  eyebrow: "WORLD CUP IN",
  days: 247,
  unitLabel: "DAYS",
  subText: "Until the world stops for thirty days.",
  dateLabel: "KICKOFF · 11 JUN 2026",
  ctaText: "Build your XI",
  theme: "signature",
  hashtags: "#WC2026 · #FBK · #Countdown",
};

const initialMatchResultContent: MatchResultCardContent = {
  eyebrow: "MATCHWEEK 1 · GROUP A",
  home: { name: "Mexico", code: "mx" },
  away: { name: "South Africa", code: "za" },
  homeScore: 2,
  awayScore: 0,
  headline: "HOSTS STUN",
  tagline: "Lozano and Vega open the World Cup with a flourish.",
  dateLabel: "FRI · JUN 12",
  venue: "Estadio Azteca",
  goals: [
    { scorer: "Lozano", minute: "23'", side: "home" },
    { scorer: "Vega", minute: "67'", side: "home" },
  ],
  motm: "Hirving Lozano",
  theme: "victory",
  hashtags: "#WC2026 · #MEXSOU · #FBK",
};

const initialTopScorersContent: CaptainPicksCardContent = {
  eyebrow: "TOP SCORERS · MATCHWEEK 3",
  headline: "Top Scorers",
  goldHighlight: "This Week",
  tagline: "The gameweek's biggest hauls.",
  displayMode: "count",
  showCaptainArmband: false,
  picks: [
    { name: "Mbappé", clubName: "Real Madrid", nationCode: "fr", position: "FWD", pickValue: 22 },
    { name: "Haaland", clubName: "Man City", nationCode: "no", position: "FWD", pickValue: 19 },
    { name: "Vinícius Jr.", clubName: "Real Madrid", nationCode: "br", position: "FWD", pickValue: 17 },
    { name: "Salah", clubName: "Liverpool", nationCode: "eg", position: "MID", pickValue: 16 },
    { name: "Bellingham", clubName: "Real Madrid", nationCode: "gb-eng", position: "MID", pickValue: 14 },
    { name: "Messi", clubName: "Inter Miami", nationCode: "ar", position: "FWD", pickValue: 13 },
    { name: "Saka", clubName: "Arsenal", nationCode: "gb-eng", position: "MID", pickValue: 12 },
    { name: "Foden", clubName: "Man City", nationCode: "gb-eng", position: "MID", pickValue: 11 },
    { name: "Yamal", clubName: "Barcelona", nationCode: "es", position: "FWD", pickValue: 10 },
    { name: "Kane", clubName: "Bayern", nationCode: "gb-eng", position: "FWD", pickValue: 9 },
  ],
  totalLabel: "Highest scorers · MW3",
  theme: "signature",
  hashtags: "#WC2026 · #FBK · #TopScorers",
};

const initialThankYouContent: ThankYouCardContent = {
  theme: "gratitude",
  eyebrow: "WORLD CUP 2026 · THAT'S A WRAP",
  headline: "THANK YOU",
  subheadline: "FOR MAKING THIS UNFORGETTABLE",
  message:
    "Every squad you picked, every captain you agonised over, every late transfer before the deadline — you built this. This World Cup will stay with us forever because of you.",
  // Placeholder values; the "Seed from API" button replaces them with real
  // numbers pulled from the dashboard + fantasy leaderboard endpoints.
  stats: [
    { value: "—", label: "Managers" },
    { value: "—", label: "Teams picked" },
    { value: "—", label: "Top score" },
    { value: "—", label: "Avg points" },
  ],
  names: [],
  signature: "— The FBK team",
  hashtags: "#WC2026 · #FBK · #ThankYou",
};

// Pre-seeded to Argentina purely because it has to default to something —
// the form's winner buttons flip the whole card to Spain in one click.
const initialChampionsContent: ChampionsCardContent = {
  variant: "glory",
  nationName: CHAMPIONS_NATION_PRESETS[0].name,
  nationCode: CHAMPIONS_NATION_PRESETS[0].code,
  primaryColor: CHAMPIONS_NATION_PRESETS[0].primary,
  secondaryColor: CHAMPIONS_NATION_PRESETS[0].secondary,
  accentColor: CHAMPIONS_NATION_PRESETS[0].accent,
  starCount: CHAMPIONS_NATION_PRESETS[0].titlesAfterWin,
  titleCountLabel: CHAMPIONS_NATION_PRESETS[0].titleLabel,
  eyebrow: "FIFA WORLD CUP 2026 · FINAL",
  championLabel: "CHAMPIONS",
  scoreLine: "",
  message: "Champions of the world. Every tackle, every goal, every heartbeat — earned.",
  dateLine: "FINAL · METLIFE STADIUM",
  hashtags: "#WC2026 · #FBK · #Champions",
};

const initialBracketContent: BracketCardContent = {
  round: "ROUND_OF_32",
  title: "KO STAGE",
  subtitle: "WC 2026",
  matchups: [],
  hashtags: "#WC2026 · #FBK · #KOStage",
};

const initialFactsheetContent: FactsheetCardContent = {
  cover: {
    variant: "cinematic",
    eyebrow: "WORLD CUP 2026",
    headline: "NEW RULES\nFBK BREAKS DOWN.",
    accentWord: "FBK",
    body: "Every law change shaping the 2026 tournament — explained in nine slides.",
  },
  extraSlides: [
    {
      variant: "newsroom",
      eyebrow: "RULE 01 / 09",
      headline: "THROW-IN COUNTDOWN\n(5 SECONDS)",
      accentWord: "5 SECONDS",
      body: "If a player deliberately delays the restart, the throw-in is awarded to the opposing team.",
    },
    {
      variant: "pill",
      pillLabel: "RULE 02 / 09",
      headline: "CAPTAIN-ONLY\nREFEREE TALKS",
      accentWord: "CAPTAIN-ONLY",
      body: "Only the captain may approach the referee for clarifications. Others get a card.",
    },
  ],
  hashtags: "#WC2026 · #FBK · #Rules",
};

const initialCaptainPicksContent: CaptainPicksCardContent = {
  eyebrow: "MOST SELECTED CAPTAINS",
  headline: "Captain Picks",
  goldHighlight: "Top 10",
  tagline: "Who the league armbanded the most.",
  displayMode: "count",
  showCaptainArmband: true,
  picks: [
    { name: "Kylian Mbappe", clubName: "France", position: "FWD", pickValue: 0 },
    { name: "Lionel Messi", clubName: "Argentina", position: "FWD", pickValue: 0 },
    { name: "Bruno Fernandes", clubName: "Portugal", position: "MID", pickValue: 0 },
    { name: "Erling Haaland", clubName: "Norway", position: "FWD", pickValue: 0 },
    { name: "Harry Kane", clubName: "England", position: "FWD", pickValue: 0 },
    { name: "Michael Olise", clubName: "France", position: "FWD", pickValue: 0 },
    { name: "Payam Niazmand", clubName: "IR Iran", position: "GK", pickValue: 0 },
    { name: "Virgil van Dijk", clubName: "Netherlands", position: "DEF", pickValue: 0 },
    { name: "Jude Bellingham", clubName: "England", position: "MID", pickValue: 0 },
    { name: "Marquinhos", clubName: "Brazil", position: "DEF", pickValue: 0 },
  ],
  totalLabel: "Across all FBK squads",
  theme: "signature",
  hashtags: "#WC2026 · #FBK · #CaptainPick",
};

const initialMatchStoryContent: MatchStoryCardContent = {
  eyebrow: "GROUP A · MATCHDAY 1",
  home: { name: "Mexico", code: "mx" },
  away: { name: "South Africa", code: "za" },
  dateLabel: "FRI · JUN 12",
  venue: "Estadio Azteca · Mexico City",
  coverTagline: "A hero returns. A nation roars. A tournament opens.",
  homeScore: 2,
  awayScore: 0,
  goals: [
    { scorer: "Lozano", minute: "23'", side: "home" },
    { scorer: "Jiménez", minute: "89'", side: "home" },
  ],
  resultHeadline: "Jiménez crowns Mexico's opening night.",
  storyEyebrow: "THE COMEBACK",
  storyHeadline: "Nine months ago",
  storyGoldHighlight: "Raúl Jiménez was in a coma.",
  storyBody:
    "Nine months ago, a fractured skull put him in a coma — doctors weren't sure he'd play again.\n\nTonight, in front of 87,000 at the Azteca, he came off the bench in the 78th minute and headed home Mexico's second.\n\nFootball is silly. Football is everything.",
  featuredPlayerName: "Raúl Jiménez",
  featuredPlayerPhotoUrl: "",
  theme: "spotlight",
  hashtags: "#WC2026 · #MEXSOU · #FBK · #Jimenez",
};

const initialLeagueLaunchContent: LeagueLaunchCardContent = {
  eyebrow: "BLUE CAFÉ · MATCHWEEK 04",
  headline: "PLAY.\nPREDICT.\nWIN.",
  tagline: "The Blue Café Fantasy League is live.",
  partnerName: "Blue Café",
  partnerLogoUrl: "",
  partnerColor: "#1E40AF",
  prizeTitle: "TOP 3 WIN",
  prizeText: "₹500 café voucher\n+ free coffee for a month",
  joinCode: "BLUE04",
  qrImageUrl: "",
  smallPrint: "Free to join · Open only to Blue Café customers",
  theme: "collab",
  hashtags: "#BlueCafeLeague · #FBK",
};

const initialMatchdayContent: MatchdayCardContent = {
  eyebrow: "MATCHDAY 02 · GROUP F",
  home: { name: "Portugal", code: "pt" },
  away: { name: "Germany", code: "de" },
  dateLabel: "15 JUN · SUN",
  kickoffLabel: "21:00 LOCAL",
  venue: "Allianz Arena",
  city: "Munich",
  tagline: "First meeting since the 2014 group stage.",
  theme: "signature",
  hashtags: "#WC2026 · #PORGER · #FBK",
};

/**
 * Build a MatchdayCardContent from a live Match record. Used by the
 * "pick from DB" / "matchweek" source modes.
 */
function matchdayFromMatch(
  match: Match,
  gameweekLabel?: string,
): MatchdayCardContent {
  const kickoff = new Date(match.kickoffTime);
  const validDate = !Number.isNaN(kickoff.getTime());
  const dateLabel = validDate
    ? kickoff
        .toLocaleDateString(undefined, {
          day: "2-digit",
          month: "short",
          weekday: "short",
        })
        .toUpperCase()
        .replace(",", " ·")
    : "TBD";
  const kickoffLabel = validDate
    ? `${kickoff.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })} LOCAL`
    : "";
  const eyebrowPrefix = gameweekLabel
    ? gameweekLabel.toUpperCase()
    : match.phase.replace(/_/g, " ");
  const eyebrow = match.stage
    ? `${eyebrowPrefix} · ${match.stage.toUpperCase()}`
    : eyebrowPrefix;
  return {
    eyebrow,
    home: {
      name: match.homeNation.name,
      code: match.homeNation.code,
      flagUrl: match.homeNation.flagUrl || undefined,
    },
    away: {
      name: match.awayNation.name,
      code: match.awayNation.code,
      flagUrl: match.awayNation.flagUrl || undefined,
    },
    dateLabel,
    kickoffLabel,
    venue: match.venue || "TBD",
    city: undefined,
    theme: "signature",
    hashtags: `#WC2026 · #${match.homeNation.code.toUpperCase()}${match.awayNation.code.toUpperCase()} · #FBK`,
  };
}

type Tab = "content" | "style" | "background";

const TABS: { value: Tab; label: string; icon: React.ElementType }[] = [
  { value: "content", label: "Content", icon: Type },
  { value: "style", label: "Style", icon: Paintbrush },
  { value: "background", label: "Background", icon: ImageIcon },
];

// Every template now supports at least some text styling.

export default function SocialCardsPage() {
  const [cardType, setCardType] = useState<CardType>("NEWS");
  const [tab, setTab] = useState<Tab>("content");
  const [newsContent, setNewsContent] = useState<NewsCardContent>(initialNewsContent);
  const [announcementContent, setAnnouncementContent] = useState<NewsCardContent>(initialAnnouncementContent);
  const [leaderboardContent, setLeaderboardContent] =
    useState<LeaderboardCardContent>(initialLeaderboardContent);
  const [lfsContent, setLfsContent] = useState<LfsCardContent>(initialLfsContent);
  const [dailyChallengeContent, setDailyChallengeContent] =
    useState<DailyChallengeCardContent>(initialDailyChallengeContent);
  const [userStoryContent, setUserStoryContent] =
    useState<UserStoryCardContent>(initialUserStoryContent);
  // Matchday: array of contents (one per match in matchweek mode, length 1 in manual mode)
  const [matchdayContents, setMatchdayContents] = useState<MatchdayCardContent[]>([
    initialMatchdayContent,
  ]);
  const [activeMatchdayIndex, setActiveMatchdayIndex] = useState(0);
  /** "manual" or a gameweekId. Drives autofill + bulk export. */
  const [matchdaySource, setMatchdaySource] = useState<"manual" | number>("manual");

  // Team of the Week: content + which gameweek to pull from
  const [teamOfWeekContent, setTeamOfWeekContent] =
    useState<TeamOfWeekCardContent>(initialTeamOfWeekContent);
  const [totwGameweekId, setTotwGameweekId] = useState<number | null>(null);

  const [partnerPitchContent, setPartnerPitchContent] =
    useState<PartnerPitchCardContent>(initialPartnerPitchContent);
  const [activePartnerPitchPage, setActivePartnerPitchPage] = useState(0);
  const [leagueLaunchContent, setLeagueLaunchContent] =
    useState<LeagueLaunchCardContent>(initialLeagueLaunchContent);
  const [fbkLeagueContent, setFbkLeagueContent] =
    useState<FbkLeagueCardContent>(initialFbkLeagueContent);
  const [countdownContent, setCountdownContent] =
    useState<CountdownCardContent>(initialCountdownContent);
  const [matchResultContent, setMatchResultContent] =
    useState<MatchResultCardContent>(initialMatchResultContent);
  const [matchStoryContent, setMatchStoryContent] =
    useState<MatchStoryCardContent>(initialMatchStoryContent);
  /** Which of the 3 pages of the Match Story deck the editor is viewing. */
  const [activeMatchStoryPage, setActiveMatchStoryPage] = useState(0);
  const [topScorersContent, setTopScorersContent] =
    useState<CaptainPicksCardContent>(initialTopScorersContent);
  const [captainPicksContent, setCaptainPicksContent] =
    useState<CaptainPicksCardContent>(initialCaptainPicksContent);
  const [factsheetContent, setFactsheetContent] =
    useState<FactsheetCardContent>(initialFactsheetContent);
  /** Which slide of the Factsheet deck the editor is viewing. 0 = cover. */
  const [activeFactsheetPage, setActiveFactsheetPage] = useState(0);
  const [bracketContent, setBracketContent] =
    useState<BracketCardContent>(initialBracketContent);
  const [championsContent, setChampionsContent] =
    useState<ChampionsCardContent>(initialChampionsContent);
  const [thankYouContent, setThankYouContent] =
    useState<ThankYouCardContent>(initialThankYouContent);

  // Clamp activeMatchdayIndex if matchdayContents shrinks
  useEffect(() => {
    if (activeMatchdayIndex >= matchdayContents.length) {
      setActiveMatchdayIndex(Math.max(0, matchdayContents.length - 1));
    }
  }, [activeMatchdayIndex, matchdayContents.length]);

  const [downloading, setDownloading] = useState<CardFormat | null>(null);
  const [bulkDownloading, setBulkDownloading] = useState<CardFormat | null>(null);
  const [publishingStory, setPublishingStory] = useState(false);
  const createStoryMutation = useCreateStory();
  const [zoomedFormat, setZoomedFormat] = useState<CardFormat | null>(null);
  const [activeStoryPage, setActiveStoryPage] = useState(0);

  // Clamp activeStoryPage when pages shrink
  useEffect(() => {
    if (activeStoryPage >= userStoryContent.pages.length) {
      setActiveStoryPage(Math.max(0, userStoryContent.pages.length - 1));
    }
  }, [activeStoryPage, userStoryContent.pages.length]);

  // Clamp MatchStory active page (always 3 pages, but stays defensive).
  useEffect(() => {
    if (activeMatchStoryPage >= MATCH_STORY_PAGE_COUNT) {
      setActiveMatchStoryPage(MATCH_STORY_PAGE_COUNT - 1);
    }
  }, [activeMatchStoryPage]);

  // Clamp activePartnerPitchPage when extraPages shrinks
  useEffect(() => {
    const total = 1 + (partnerPitchContent.extraPages?.length ?? 0);
    if (activePartnerPitchPage >= total) {
      setActivePartnerPitchPage(Math.max(0, total - 1));
    }
  }, [activePartnerPitchPage, partnerPitchContent.extraPages]);

  const storyRef = useRef<HTMLDivElement>(null);
  const postRef = useRef<HTMLDivElement>(null);

  // Close zoom on Escape
  useEffect(() => {
    if (!zoomedFormat) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setZoomedFormat(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomedFormat]);

  // ─── Style getter/setter — uniform across templates ────────────────────────
  const getStyle = useCallback((): CardStyleSettings | undefined => {
    switch (cardType) {
      case "NEWS": return newsContent.style;
      case "ANNOUNCEMENT": return announcementContent.style;
      case "LEADERBOARD": return leaderboardContent.style;
      case "LFS_RESULT": return lfsContent.style;
      case "DAILY_CHALLENGE": return dailyChallengeContent.style;
      case "USER_STORY": return userStoryContent.style;
      case "MATCHDAY": return matchdayContents[activeMatchdayIndex]?.style;
      case "TEAM_OF_WEEK": return teamOfWeekContent.style;
      case "PARTNER_PITCH": return partnerPitchContent.style;
      case "LEAGUE_LAUNCH": return leagueLaunchContent.style;
      case "FBK_LEAGUE": return fbkLeagueContent.style;
      case "COUNTDOWN": return countdownContent.style;
      case "MATCH_RESULT": return matchResultContent.style;
      case "MATCH_STORY": return matchStoryContent.style;
      case "TOP_SCORERS": return topScorersContent.style;
      case "CAPTAIN_PICKS": return captainPicksContent.style;
      case "FACTSHEET": return factsheetContent.style;
      case "BRACKET": return bracketContent.style;
      case "CHAMPIONS": return championsContent.style;
      case "THANK_YOU": return thankYouContent.style;
    }
  }, [cardType, newsContent.style, announcementContent.style, leaderboardContent.style, lfsContent.style, dailyChallengeContent.style, userStoryContent.style, matchdayContents, activeMatchdayIndex, teamOfWeekContent.style, partnerPitchContent.style, leagueLaunchContent.style, fbkLeagueContent.style, countdownContent.style, matchResultContent.style, matchStoryContent.style, topScorersContent.style, captainPicksContent.style, factsheetContent.style, bracketContent.style, championsContent.style, thankYouContent.style]);

  const setStyle = useCallback(
    (next: CardStyleSettings | undefined) => {
      switch (cardType) {
        case "NEWS": setNewsContent((c) => ({ ...c, style: next })); break;
        case "ANNOUNCEMENT": setAnnouncementContent((c) => ({ ...c, style: next })); break;
        case "LEADERBOARD": setLeaderboardContent((c) => ({ ...c, style: next })); break;
        case "LFS_RESULT": setLfsContent((c) => ({ ...c, style: next })); break;
        case "DAILY_CHALLENGE": setDailyChallengeContent((c) => ({ ...c, style: next })); break;
        case "USER_STORY": setUserStoryContent((c) => ({ ...c, style: next })); break;
        case "MATCHDAY":
          setMatchdayContents((arr) => {
            const copy = [...arr];
            if (copy[activeMatchdayIndex]) {
              copy[activeMatchdayIndex] = { ...copy[activeMatchdayIndex], style: next };
            }
            return copy;
          });
          break;
        case "TEAM_OF_WEEK": setTeamOfWeekContent((c) => ({ ...c, style: next })); break;
        case "PARTNER_PITCH": setPartnerPitchContent((c) => ({ ...c, style: next })); break;
        case "LEAGUE_LAUNCH": setLeagueLaunchContent((c) => ({ ...c, style: next })); break;
        case "FBK_LEAGUE": setFbkLeagueContent((c) => ({ ...c, style: next })); break;
        case "COUNTDOWN": setCountdownContent((c) => ({ ...c, style: next })); break;
        case "MATCH_RESULT": setMatchResultContent((c) => ({ ...c, style: next })); break;
        case "MATCH_STORY": setMatchStoryContent((c) => ({ ...c, style: next })); break;
        case "TOP_SCORERS": setTopScorersContent((c) => ({ ...c, style: next })); break;
        case "CAPTAIN_PICKS": setCaptainPicksContent((c) => ({ ...c, style: next })); break;
        case "FACTSHEET": setFactsheetContent((c) => ({ ...c, style: next })); break;
        case "BRACKET": setBracketContent((c) => ({ ...c, style: next })); break;
        case "CHAMPIONS": setChampionsContent((c) => ({ ...c, style: next })); break;
        case "THANK_YOU": setThankYouContent((c) => ({ ...c, style: next })); break;
      }
    },
    [cardType, activeMatchdayIndex],
  );

  const captureCurrentNode = async (
    format: CardFormat,
    fileSlug: string,
    opts: { returnBlob?: boolean } = {},
  ): Promise<Blob | null> => {
    const node = format === "story" ? storyRef.current : postRef.current;
    if (!node) return null;
    const spec = CARD_FORMATS[format];
    // pixelRatio: 2 doubles the export resolution.
    //   Story 1080×1920 → 2160×3840 (matches 4K UHD on the long edge)
    //   Post  1080×1350 → 2160×2700
    // Bigger files (~2-4x) but text + edges are crisp, and social platforms
    // re-compress from a higher source so the result looks sharper end-to-end.
    if (opts.returnBlob) {
      const blob = await htmlToImage.toBlob(node, {
        width: spec.width,
        height: spec.height,
        pixelRatio: 2,
        cacheBust: true,
        style: { transform: "scale(1)", transformOrigin: "top left" },
      });
      return blob;
    }
    const dataUrl = await htmlToImage.toPng(node, {
      width: spec.width,
      height: spec.height,
      pixelRatio: 2,
      cacheBust: true,
      style: { transform: "scale(1)", transformOrigin: "top left" },
    });
    const link = document.createElement("a");
    link.download = `fbk-${cardType.toLowerCase()}-${format}-${fileSlug}.png`;
    link.href = dataUrl;
    link.click();
    return null;
  };

  /** Build a zip from a list of (filename, blob) entries and trigger a single download. */
  const downloadZip = async (
    zipName: string,
    entries: Array<{ name: string; blob: Blob }>,
  ) => {
    const zip = new JSZip();
    for (const { name, blob } of entries) zip.file(name, blob);
    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement("a");
    link.download = zipName;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadCard = async (format: CardFormat) => {
    setDownloading(format);
    try {
      const slug = headlineForFilename().slice(0, 40);
      await captureCurrentNode(format, slug);
      toast.success(`${CARD_FORMATS[format].label} downloaded`);
    } catch (err) {
      console.error(err);
      // html-to-image rejects with the underlying <img> error Event when an
      // image fails to load during canvas rasterization — typically a CORS-
      // blocked remote URL. Pull the URL off the Event so the admin knows
      // which image broke instead of seeing "[object Event]".
      let msg: string;
      let isImageError = false;
      if (err instanceof Error) {
        msg = err.message;
      } else if (err && typeof err === "object" && "target" in err) {
        const target = (err as { target: unknown }).target as
          | { src?: string; tagName?: string }
          | null;
        const src = target?.src;
        msg = src
          ? `image failed to load: ${src}`
          : "image failed to load during export";
        isImageError = true;
      } else {
        msg = String(err);
      }
      const isCors =
        isImageError || /tainted|cors|cross[- ]origin|fetch/i.test(msg);
      toast.error(
        isCors
          ? `Export failed — a remote image is CORS-blocked or unreachable${msg.startsWith("image") ? ` (${msg.replace(/^image[^:]+: /, "")})` : ""}. Re-pick the gameweek to re-embed, or use Upload PNG for any custom images.`
          : `Failed to render card: ${msg}`,
      );
    } finally {
      setDownloading(null);
    }
  };

  /** Loops through every chapter of the active User Story, captures each as a
   *  PNG blob, then packages all of them into a single .zip download. */
  const downloadAllChapters = async (format: CardFormat) => {
    if (cardType !== "USER_STORY") return;
    const total = userStoryContent.pages.length;
    if (total === 0) return;
    setBulkDownloading(format);
    const originalPage = activeStoryPage;
    try {
      const entries: Array<{ name: string; blob: Blob }> = [];
      for (let i = 0; i < total; i++) {
        setActiveStoryPage(i);
        // Wait for React to re-render + fonts/images to settle
        await new Promise((r) => setTimeout(r, 650));
        const page = userStoryContent.pages[i];
        const slug = (
          `${page.name || "page"}-${page.goldHighlight || ""}-${i + 1}`
        )
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .slice(0, 40);
        const blob = await captureCurrentNode(format, slug, { returnBlob: true });
        if (blob) {
          entries.push({
            name: `${String(i + 1).padStart(2, "0")}-${slug}.png`,
            blob,
          });
        }
        await new Promise((r) => setTimeout(r, 250));
      }
      const zipName = `fbk-story-${format}-${Date.now()}.zip`;
      await downloadZip(zipName, entries);
      toast.success(`Zipped ${entries.length} chapter${entries.length > 1 ? "s" : ""}`);
    } catch (err) {
      console.error(err);
      toast.error("Bulk export failed. Check console for details.");
    } finally {
      setActiveStoryPage(originalPage);
      setBulkDownloading(null);
    }
  };

  /** Loops through every match in the active matchweek, captures each as a
   *  PNG blob, then packages all of them into a single .zip download. */
  const downloadAllMatchdays = async (format: CardFormat) => {
    if (cardType !== "MATCHDAY") return;
    const total = matchdayContents.length;
    if (total === 0) return;
    setBulkDownloading(format);
    const originalIndex = activeMatchdayIndex;
    try {
      const entries: Array<{ name: string; blob: Blob }> = [];
      for (let i = 0; i < total; i++) {
        setActiveMatchdayIndex(i);
        await new Promise((r) => setTimeout(r, 650));
        const md = matchdayContents[i];
        const slug = `${md.home.code}-vs-${md.away.code}-${i + 1}`
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .slice(0, 40);
        const blob = await captureCurrentNode(format, slug, { returnBlob: true });
        if (blob) {
          entries.push({
            name: `${String(i + 1).padStart(2, "0")}-${slug}.png`,
            blob,
          });
        }
        await new Promise((r) => setTimeout(r, 250));
      }
      const zipName = `fbk-matchday-${format}-${Date.now()}.zip`;
      await downloadZip(zipName, entries);
      toast.success(`Zipped ${entries.length} match${entries.length > 1 ? "es" : ""}`);
    } catch (err) {
      console.error(err);
      toast.error("Bulk export failed. Check console for details.");
    } finally {
      setActiveMatchdayIndex(originalIndex);
      setBulkDownloading(null);
    }
  };

  /** Cover → Result → Story page of the active Match Story deck, all three
   *  PNGs packaged into one .zip the admin can post as an Instagram carousel. */
  const downloadAllMatchStoryPages = async (format: CardFormat) => {
    if (cardType !== "MATCH_STORY") return;
    setBulkDownloading(format);
    const originalPage = activeMatchStoryPage;
    const pageNames = ["cover", "result", "story"];
    try {
      const entries: Array<{ name: string; blob: Blob }> = [];
      for (let i = 0; i < MATCH_STORY_PAGE_COUNT; i++) {
        setActiveMatchStoryPage(i);
        await new Promise((r) => setTimeout(r, 650));
        const slug = `${matchStoryContent.home.code}-vs-${matchStoryContent.away.code}-${pageNames[i]}`
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .slice(0, 50);
        const blob = await captureCurrentNode(format, slug, { returnBlob: true });
        if (blob) {
          entries.push({
            name: `${String(i + 1).padStart(2, "0")}-${slug}.png`,
            blob,
          });
        }
        await new Promise((r) => setTimeout(r, 250));
      }
      const zipName = `fbk-match-story-${format}-${Date.now()}.zip`;
      await downloadZip(zipName, entries);
      toast.success(`Zipped ${entries.length} page${entries.length > 1 ? "s" : ""}`);
    } catch (err) {
      console.error(err);
      toast.error("Bulk export failed. Check console for details.");
    } finally {
      setActiveMatchStoryPage(originalPage);
      setBulkDownloading(null);
    }
  };

  /** Loops through cover + every chapter slide of the active Partner Pitch deck
   *  and packages them all into a single .zip download. */
  const downloadAllPitchSlides = async (format: CardFormat) => {
    if (cardType !== "PARTNER_PITCH") return;
    const total = 1 + (partnerPitchContent.extraPages?.length ?? 0);
    if (total === 0) return;
    setBulkDownloading(format);
    const originalPage = activePartnerPitchPage;
    try {
      const entries: Array<{ name: string; blob: Blob }> = [];
      for (let i = 0; i < total; i++) {
        setActivePartnerPitchPage(i);
        await new Promise((r) => setTimeout(r, 650));
        const partnerSlug = (partnerPitchContent.partnerName || "partner")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .slice(0, 24);
        let slideSlug = "cover";
        if (i > 0) {
          const chapter = partnerPitchContent.extraPages?.[i - 1];
          slideSlug = (chapter?.headline || `slide-${i + 1}`)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 32);
        }
        const blob = await captureCurrentNode(
          format,
          `${partnerSlug}-${slideSlug}`,
          { returnBlob: true },
        );
        if (blob) {
          entries.push({
            name: `${String(i + 1).padStart(2, "0")}-${slideSlug}.png`,
            blob,
          });
        }
        await new Promise((r) => setTimeout(r, 250));
      }
      const zipName = `fbk-pitch-${partnerPitchContent.partnerName || "partner"}-${format}-${Date.now()}.zip`
        .toLowerCase()
        .replace(/[^a-z0-9.-]+/g, "-");
      await downloadZip(zipName, entries);
      toast.success(`Zipped ${entries.length} slide${entries.length > 1 ? "s" : ""}`);
    } catch (err) {
      console.error(err);
      toast.error("Bulk export failed. Check console for details.");
    } finally {
      setActivePartnerPitchPage(originalPage);
      setBulkDownloading(null);
    }
  };

  // ─── Publish-as-Story ────────────────────────────────────────────────────
  // Captures every page of the current card design in Story format (1080×1920),
  // uploads each as a hosted PNG, then creates a draft Story in the /stories
  // system. The same artwork now lives in three places:
  //   1. The mobile app's Stories feed (once admin flips isPublished → true)
  //   2. Downloadable PNGs for manual Instagram/TikTok posting
  //   3. The /stories admin page for editing/review/scheduling

  /**
   * Total page count for the current card. Single-page templates return 1;
   * multi-page decks return their live page count.
   */
  const cardPageCount = (): number => {
    switch (cardType) {
      case "USER_STORY":
        return Math.max(1, userStoryContent.pages.length);
      case "MATCHDAY":
        return Math.max(1, matchdayContents.length);
      case "PARTNER_PITCH":
        return 1 + (partnerPitchContent.extraPages?.length ?? 0);
      case "MATCH_STORY":
        return MATCH_STORY_PAGE_COUNT;
      case "FACTSHEET":
        return 1 + factsheetContent.extraSlides.length;
      default:
        return 1;
    }
  };

  /** Set the active page for whichever multi-page template is selected. */
  const setActiveCardPage = (i: number) => {
    switch (cardType) {
      case "USER_STORY": setActiveStoryPage(i); break;
      case "MATCHDAY": setActiveMatchdayIndex(i); break;
      case "PARTNER_PITCH": setActivePartnerPitchPage(i); break;
      case "MATCH_STORY": setActiveMatchStoryPage(i); break;
      case "FACTSHEET": setActiveFactsheetPage(i); break;
      // single-page templates: no-op
    }
  };

  /** Current active page index for the template (or 0 for single-page). */
  const currentCardPage = (): number => {
    switch (cardType) {
      case "USER_STORY": return activeStoryPage;
      case "MATCHDAY": return activeMatchdayIndex;
      case "PARTNER_PITCH": return activePartnerPitchPage;
      case "MATCH_STORY": return activeMatchStoryPage;
      case "FACTSHEET": return activeFactsheetPage;
      default: return 0;
    }
  };

  /** Map a CardType to a Story category from the /stories category enum. */
  const cardTypeToStoryCategory = (t: CardType): string => {
    switch (t) {
      case "MATCH_RESULT":
      case "MATCH_STORY":
      case "TEAM_OF_WEEK":
      case "TOP_SCORERS":
      case "CAPTAIN_PICKS":
        return "MATCH_RECAP";
      case "USER_STORY":
        return "PLAYER_STORY";
      case "MATCHDAY":
      case "NEWS":
      case "ANNOUNCEMENT":
      case "COUNTDOWN":
      case "PARTNER_PITCH":
      case "LEAGUE_LAUNCH":
      case "FBK_LEAGUE":
      case "FACTSHEET":
      case "BRACKET":
      case "CHAMPIONS":
      case "THANK_YOU":
        return "BREAKING_NEWS";
      case "LEADERBOARD":
      case "LFS_RESULT":
      case "DAILY_CHALLENGE":
      default:
        return "TRIVIA";
    }
  };

  /** Title shown on the Story card in /stories + the mobile feed. */
  const deriveStoryTitle = (): string => {
    switch (cardType) {
      case "NEWS":
      case "ANNOUNCEMENT": {
        const c = cardType === "NEWS" ? newsContent : announcementContent;
        return [c.headline, c.goldHighlight].filter(Boolean).join(" ").trim() ||
          c.eyebrow;
      }
      case "LEADERBOARD": {
        const scope = leaderboardContent.scope ?? "matchweek";
        if (scope === "alltime") return "All Time — Leaderboard";
        if (scope === "custom")
          return `${[leaderboardContent.customTitle, leaderboardContent.customTitleAccent].filter(Boolean).join(" ")} — Leaderboard`;
        return `Matchweek ${leaderboardContent.matchweek} — Leaderboard`;
      }
      case "LFS_RESULT":
        return `LFS Round ${lfsContent.round}`;
      case "DAILY_CHALLENGE":
        return `${dailyChallengeContent.challengeType} — Today's Challenge`;
      case "USER_STORY":
        return `${userStoryContent.pages[0]?.name ?? ""} ${userStoryContent.pages[0]?.goldHighlight ?? ""}`.trim() || "Player Story";
      case "MATCHDAY": {
        const md = matchdayContents[activeMatchdayIndex] ?? matchdayContents[0];
        return md ? `${md.home.name} vs ${md.away.name}` : "Matchday";
      }
      case "TEAM_OF_WEEK":
        return `${teamOfWeekContent.gameweekName} — Team of the Week`;
      case "PARTNER_PITCH":
        return `${partnerPitchContent.partnerName} — FBK League`;
      case "LEAGUE_LAUNCH":
        return `${leagueLaunchContent.partnerName} — League Open`;
      case "FBK_LEAGUE":
        return fbkLeagueContent.eyebrow || "FBK League";
      case "COUNTDOWN":
        return `${countdownContent.days} ${countdownContent.unitLabel || "DAYS"} to kickoff`;
      case "MATCH_RESULT":
        return `${matchResultContent.home.name} ${matchResultContent.homeScore}-${matchResultContent.awayScore} ${matchResultContent.away.name}`;
      case "MATCH_STORY":
        return `${matchStoryContent.home.name} vs ${matchStoryContent.away.name}`;
      case "TOP_SCORERS":
        return `${topScorersContent.headline} — ${topScorersContent.goldHighlight ?? ""}`.trim();
      case "CAPTAIN_PICKS":
        return `${captainPicksContent.headline} — ${captainPicksContent.goldHighlight ?? ""}`.trim();
      case "FACTSHEET":
        return factsheetContent.cover.headline.replace(/\n/g, " ").trim();
      case "BRACKET":
        return `${BRACKET_ROUND_LABEL[bracketContent.round]} · ${bracketContent.title}`;
      case "CHAMPIONS":
        return `${championsContent.nationName} — ${championsContent.championLabel}`;
      case "THANK_YOU":
        return `${thankYouContent.headline} — ${thankYouContent.subheadline ?? "FBK community"}`;
    }
  };

  /**
   * Capture each page → upload → create a draft Story. Always uses Story
   * format (1080×1920) since the mobile carousel renders vertically.
   */
  const publishAsStory = async () => {
    setPublishingStory(true);
    const total = cardPageCount();
    const originalPage = currentCardPage();
    try {
      // 1. Capture every page as a JPEG Blob using the POST format
      // (1080x1350). The mobile app renders the story carousel at post
      // aspect, so uploading the taller 1080x1920 Story crop makes the
      // top and bottom of the card get cropped out. Post crop matches
      // how the mobile app frames these images, so what admin sees in
      // the preview is what users see in the app.
      //
      // File size: JPEG q=0.85 + pixelRatio 1.5 keeps each slide well
      // under the backend's 5MB Multer cap even on complex templates
      // (Captain Picks with 10 photos). The manual Story-PNG / Post-PNG
      // download buttons still give raw 2x PNGs for direct sharing.
      const publishSpec = CARD_FORMATS.post;
      const captureSlide = async (): Promise<Blob | null> => {
        const node = postRef.current;
        if (!node) return null;
        const dataUrl = await htmlToImage.toJpeg(node, {
          width: publishSpec.width,
          height: publishSpec.height,
          pixelRatio: 1.5,
          quality: 0.85,
          cacheBust: true,
          backgroundColor: "#07321A",
          style: { transform: "scale(1)", transformOrigin: "top left" },
        });
        // Convert the data URL to a Blob the multipart upload accepts.
        const res = await fetch(dataUrl);
        return res.blob();
      };

      const blobs: Blob[] = [];
      for (let i = 0; i < total; i++) {
        setActiveCardPage(i);
        // Wait for React + fonts + images to settle (same delay as bulk export)
        await new Promise((r) => setTimeout(r, 650));
        const blob = await captureSlide();
        if (blob) blobs.push(blob);
        await new Promise((r) => setTimeout(r, 200));
      }
      if (blobs.length === 0) {
        toast.error("Nothing to publish — capture failed.");
        return;
      }

      // 2. Upload each blob to the story-image endpoint
      const uploaded = await Promise.all(
        blobs.map(async (blob, i) => {
          const file = new File(
            [blob],
            `${cardType.toLowerCase()}-${Date.now()}-${i + 1}.jpg`,
            { type: "image/jpeg" },
          );
          const { url } = await uploadStoryImage(file);
          return url;
        }),
      );

      // 3. Build payload and create the story (always as draft — admin
      //    reviews in /stories before flipping isPublished)
      const payload: CreateStoryPayload = {
        title: deriveStoryTitle().slice(0, 200) || "Untitled Story",
        category: cardTypeToStoryCategory(cardType),
        coverImage: uploaded[0],
        isPublished: false,
        slides: uploaded.map((url, i) => ({
          imageUrl: url,
          content: "",
          order: i,
        })),
      };
      await createStoryMutation.mutateAsync(payload);

      toast.success(
        `Story created with ${uploaded.length} slide${uploaded.length > 1 ? "s" : ""} — review at /stories before publishing`,
      );
    } catch (err) {
      console.error(err);
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Publish failed: ${msg}`);
    } finally {
      setActiveCardPage(originalPage);
      setPublishingStory(false);
    }
  };

  const headlineForFilename = useCallback(() => {
    const slugify = (s: string) =>
      s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    switch (cardType) {
      case "NEWS": return slugify(newsContent.headline || "news");
      case "ANNOUNCEMENT": return slugify(announcementContent.headline || "announcement");
      case "LEADERBOARD": {
        const scope = leaderboardContent.scope ?? "matchweek";
        if (scope === "alltime") return "all-time-leaderboard";
        if (scope === "custom")
          return slugify(
            `${leaderboardContent.customTitle ?? ""}-${leaderboardContent.customTitleAccent ?? ""}-leaderboard`,
          );
        return `matchweek-${leaderboardContent.matchweek}`;
      }
      case "LFS_RESULT": return `round-${lfsContent.round}`;
      case "DAILY_CHALLENGE": return slugify(dailyChallengeContent.challengeType);
      case "USER_STORY": {
        const page = userStoryContent.pages[activeStoryPage];
        return slugify(`${page?.name || "story"}-${page?.goldHighlight || ""}-${activeStoryPage + 1}`);
      }
      case "MATCHDAY": {
        const md = matchdayContents[activeMatchdayIndex];
        if (!md) return "matchday";
        return slugify(`${md.home.code}-vs-${md.away.code}-${md.dateLabel}`);
      }
      case "TEAM_OF_WEEK":
        return slugify(
          `totw-${teamOfWeekContent.gameweekName}-${teamOfWeekContent.formation}`,
        );
      case "PARTNER_PITCH":
        return slugify(`pitch-${partnerPitchContent.partnerName || "partner"}`);
      case "LEAGUE_LAUNCH":
        return slugify(`league-${leagueLaunchContent.partnerName || "partner"}`);
      case "FBK_LEAGUE":
        return slugify(`fbk-league-${fbkLeagueContent.eyebrow || "matchweek"}`);
      case "COUNTDOWN":
        return slugify(`countdown-${countdownContent.days}d-${countdownContent.theme}`);
      case "MATCH_RESULT":
        return slugify(
          `result-${matchResultContent.home.code}-${matchResultContent.homeScore}-${matchResultContent.awayScore}-${matchResultContent.away.code}-${matchResultContent.theme}`,
        );
      case "MATCH_STORY":
        return slugify(
          `story-${matchStoryContent.home.code}-vs-${matchStoryContent.away.code}-p${activeMatchStoryPage + 1}`,
        );
      case "TOP_SCORERS":
        return slugify(
          `top-scorers-${topScorersContent.eyebrow || "leaderboard"}`,
        );
      case "CAPTAIN_PICKS":
        return slugify(
          `captain-picks-${captainPicksContent.eyebrow || "leaderboard"}`,
        );
      case "FACTSHEET": {
        const slides = [factsheetContent.cover, ...factsheetContent.extraSlides];
        const slide = slides[Math.min(activeFactsheetPage, slides.length - 1)];
        const tag = slide?.pillLabel || slide?.eyebrow || slide?.headline || "factsheet";
        return slugify(`factsheet-${tag}-p${activeFactsheetPage + 1}`);
      }
      case "BRACKET":
        return slugify(`bracket-${bracketContent.round}-${bracketContent.title}`);
      case "CHAMPIONS":
        return slugify(`champions-${championsContent.nationName}`);
      case "THANK_YOU":
        return slugify(`thank-you-${thankYouContent.theme}`);
    }
  }, [cardType, newsContent, announcementContent, leaderboardContent, lfsContent, dailyChallengeContent, userStoryContent, activeStoryPage, matchdayContents, activeMatchdayIndex, teamOfWeekContent, partnerPitchContent, leagueLaunchContent, fbkLeagueContent, countdownContent, matchResultContent, matchStoryContent, activeMatchStoryPage, topScorersContent, captainPicksContent, factsheetContent, activeFactsheetPage, bracketContent, championsContent, thankYouContent]);

  const renderCard = (format: CardFormat, ref: React.Ref<HTMLDivElement> = null) => {
    switch (cardType) {
      case "NEWS":
        return <NewsCard ref={ref} format={format} cardType="NEWS" content={newsContent} />;
      case "ANNOUNCEMENT":
        return <NewsCard ref={ref} format={format} cardType="ANNOUNCEMENT" content={announcementContent} />;
      case "LEADERBOARD":
        return <LeaderboardCard ref={ref} format={format} content={leaderboardContent} />;
      case "LFS_RESULT":
        return <LfsCard ref={ref} format={format} content={lfsContent} />;
      case "DAILY_CHALLENGE":
        return <DailyChallengeCard ref={ref} format={format} content={dailyChallengeContent} />;
      case "USER_STORY":
        return (
          <UserStoryCard
            ref={ref}
            format={format}
            content={userStoryContent}
            pageIndex={activeStoryPage}
          />
        );
      case "MATCHDAY": {
        const md = matchdayContents[activeMatchdayIndex] ?? initialMatchdayContent;
        return <MatchdayCard ref={ref} format={format} content={md} />;
      }
      case "TEAM_OF_WEEK":
        return (
          <TeamOfWeekCard ref={ref} format={format} content={teamOfWeekContent} />
        );
      case "PARTNER_PITCH":
        return (
          <PartnerPitchCard
            ref={ref}
            format={format}
            content={partnerPitchContent}
            pageIndex={activePartnerPitchPage}
          />
        );
      case "LEAGUE_LAUNCH":
        return (
          <LeagueLaunchCard ref={ref} format={format} content={leagueLaunchContent} />
        );
      case "FBK_LEAGUE":
        return (
          <FbkLeagueCard ref={ref} format={format} content={fbkLeagueContent} />
        );
      case "COUNTDOWN":
        return (
          <CountdownCard ref={ref} format={format} content={countdownContent} />
        );
      case "MATCH_RESULT":
        return (
          <MatchResultCard ref={ref} format={format} content={matchResultContent} />
        );
      case "MATCH_STORY":
        return (
          <MatchStoryCard
            ref={ref}
            format={format}
            content={matchStoryContent}
            pageIndex={activeMatchStoryPage}
          />
        );
      case "TOP_SCORERS":
        return (
          <CaptainPicksCard ref={ref} format={format} content={topScorersContent} />
        );
      case "CAPTAIN_PICKS":
        return (
          <CaptainPicksCard ref={ref} format={format} content={captainPicksContent} />
        );
      case "FACTSHEET":
        return (
          <FactsheetCard
            ref={ref}
            format={format}
            content={factsheetContent}
            pageIndex={activeFactsheetPage}
          />
        );
      case "BRACKET":
        return (
          <BracketCard ref={ref} format={format} content={bracketContent} />
        );
      case "CHAMPIONS":
        return (
          <ChampionsCard ref={ref} format={format} content={championsContent} />
        );
      case "THANK_YOU":
        return (
          <ThankYouCard ref={ref} format={format} content={thankYouContent} />
        );
    }
  };

  const currentCardOption = CARD_TYPES.find((c) => c.value === cardType)!;

  return (
    <Layout>
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Social Cards</h1>
        <p className="text-gray-500 mt-1">
          Design branded FBK cards for social media — exports as Story
          (1080×1920) and Post (1080×1350) PNGs.
        </p>
      </div>

      {/* Card type pill grid */}
      <div className="mb-6">
        <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
          Card Template
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {CARD_TYPES.map((c) => {
            const Icon = c.icon;
            const active = cardType === c.value;
            return (
              <button
                key={c.value}
                type="button"
                onClick={() => setCardType(c.value)}
                className={cn(
                  "flex items-center gap-2 p-3 rounded-lg border text-left transition",
                  active
                    ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-600/15"
                    : "border-gray-200 bg-white hover:border-gray-300",
                )}
              >
                <div
                  className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                    c.iconBg,
                  )}
                >
                  <Icon className="h-4 w-4 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm text-gray-900 truncate">
                    {c.label}
                  </div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {c.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* LEFT — TABS + FORM */}
        <div className="col-span-12 lg:col-span-6 xl:col-span-5 space-y-4">
          {/* Tab bar */}
          <div className="bg-white rounded-lg shadow border border-gray-100">
            <div className="flex border-b border-gray-100">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setTab(t.value)}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold transition border-b-2",
                      active
                        ? "text-emerald-700 border-emerald-600"
                        : "text-gray-500 border-transparent hover:text-gray-700",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {t.label}
                  </button>
                );
              })}
            </div>

            <div className="p-5">
              {tab === "content" && (
                <>
                  {(cardType === "NEWS" || cardType === "ANNOUNCEMENT") && (
                    <NewsForm
                      cardType={cardType}
                      content={cardType === "NEWS" ? newsContent : announcementContent}
                      onChange={cardType === "NEWS" ? setNewsContent : setAnnouncementContent}
                    />
                  )}
                  {cardType === "LEADERBOARD" && (
                    <LeaderboardForm content={leaderboardContent} onChange={setLeaderboardContent} />
                  )}
                  {cardType === "LFS_RESULT" && (
                    <LfsForm content={lfsContent} onChange={setLfsContent} />
                  )}
                  {cardType === "DAILY_CHALLENGE" && (
                    <DailyChallengeForm content={dailyChallengeContent} onChange={setDailyChallengeContent} />
                  )}
                  {cardType === "USER_STORY" && (
                    <UserStoryForm
                      content={userStoryContent}
                      onChange={setUserStoryContent}
                      activePageIndex={activeStoryPage}
                      onActivePageChange={setActiveStoryPage}
                    />
                  )}
                  {cardType === "MATCHDAY" && (
                    <MatchdayForm
                      contents={matchdayContents}
                      onChange={setMatchdayContents}
                      activeIndex={activeMatchdayIndex}
                      onActiveIndexChange={setActiveMatchdayIndex}
                      source={matchdaySource}
                      onSourceChange={setMatchdaySource}
                    />
                  )}
                  {cardType === "TEAM_OF_WEEK" && (
                    <TeamOfWeekForm
                      content={teamOfWeekContent}
                      onChange={setTeamOfWeekContent}
                      gameweekId={totwGameweekId}
                      onGameweekIdChange={setTotwGameweekId}
                    />
                  )}
                  {cardType === "PARTNER_PITCH" && (
                    <PartnerPitchForm
                      content={partnerPitchContent}
                      onChange={setPartnerPitchContent}
                      activePageIndex={activePartnerPitchPage}
                      onActivePageChange={setActivePartnerPitchPage}
                    />
                  )}
                  {cardType === "LEAGUE_LAUNCH" && (
                    <LeagueLaunchForm
                      content={leagueLaunchContent}
                      onChange={setLeagueLaunchContent}
                    />
                  )}
                  {cardType === "FBK_LEAGUE" && (
                    <FbkLeagueForm
                      content={fbkLeagueContent}
                      onChange={setFbkLeagueContent}
                    />
                  )}
                  {cardType === "COUNTDOWN" && (
                    <CountdownForm
                      content={countdownContent}
                      onChange={setCountdownContent}
                    />
                  )}
                  {cardType === "MATCH_RESULT" && (
                    <MatchResultForm
                      content={matchResultContent}
                      onChange={setMatchResultContent}
                    />
                  )}
                  {cardType === "MATCH_STORY" && (
                    <MatchStoryForm
                      content={matchStoryContent}
                      onChange={setMatchStoryContent}
                      activePage={activeMatchStoryPage}
                      onActivePageChange={setActiveMatchStoryPage}
                    />
                  )}
                  {cardType === "TOP_SCORERS" && (
                    <CaptainPicksForm
                      mode="top-scorers"
                      content={topScorersContent}
                      onChange={setTopScorersContent}
                    />
                  )}
                  {cardType === "CAPTAIN_PICKS" && (
                    <CaptainPicksForm
                      mode="captain-picks"
                      content={captainPicksContent}
                      onChange={setCaptainPicksContent}
                    />
                  )}
                  {cardType === "FACTSHEET" && (
                    <FactsheetForm
                      content={factsheetContent}
                      onChange={setFactsheetContent}
                      activePage={activeFactsheetPage}
                      onActivePageChange={setActiveFactsheetPage}
                    />
                  )}
                  {cardType === "BRACKET" && (
                    <BracketForm
                      content={bracketContent}
                      onChange={setBracketContent}
                    />
                  )}
                  {cardType === "CHAMPIONS" && (
                    <ChampionsForm
                      content={championsContent}
                      onChange={setChampionsContent}
                    />
                  )}
                  {cardType === "THANK_YOU" && (
                    <ThankYouForm
                      content={thankYouContent}
                      onChange={setThankYouContent}
                    />
                  )}
                </>
              )}

              {tab === "style" && (
                <StyleTab
                  cardType={cardType}
                  style={getStyle()}
                  onChange={setStyle}
                />
              )}

              {tab === "background" && (
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-1">
                      Card background
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Upload an image to overlay the FBK brand gradient. The
                      green base + gold glow + grid stay visible underneath.
                    </p>
                  </div>
                  <BackgroundControls
                    value={getStyle()?.background}
                    onChange={(next) => {
                      const cur = getStyle() ?? {};
                      const merged: CardStyleSettings = { ...cur, background: next };
                      if (!next) delete merged.background;
                      setStyle(Object.keys(merged).length === 0 ? undefined : merged);
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Download */}
          <div className="bg-white rounded-lg shadow p-5 border border-gray-100">
            <div className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">
              Export · {currentCardOption.label}
              {cardType === "USER_STORY" && userStoryContent.pages.length > 1 && (
                <span className="ml-2 text-xs font-semibold text-gray-500 normal-case tracking-normal">
                  · current page {activeStoryPage + 1} of {userStoryContent.pages.length}
                </span>
              )}
              {cardType === "MATCHDAY" && matchdayContents.length > 1 && (
                <span className="ml-2 text-xs font-semibold text-gray-500 normal-case tracking-normal">
                  · current match {activeMatchdayIndex + 1} of {matchdayContents.length}
                </span>
              )}
              {cardType === "PARTNER_PITCH" && (partnerPitchContent.extraPages?.length ?? 0) > 0 && (
                <span className="ml-2 text-xs font-semibold text-gray-500 normal-case tracking-normal">
                  · current slide {activePartnerPitchPage + 1} of {1 + (partnerPitchContent.extraPages?.length ?? 0)}
                </span>
              )}
              {cardType === "MATCH_STORY" && (
                <span className="ml-2 text-xs font-semibold text-gray-500 normal-case tracking-normal">
                  · page {activeMatchStoryPage + 1} of {MATCH_STORY_PAGE_COUNT}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => downloadCard("story")}
                disabled={downloading !== null || bulkDownloading !== null}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {downloading === "story" ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Story PNG
              </Button>
              <Button
                onClick={() => downloadCard("post")}
                disabled={downloading !== null || bulkDownloading !== null}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {downloading === "post" ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Post PNG
              </Button>
            </div>

            {/* Publish-as-Story — captures every page in vertical Story format
                (1080×1920), uploads, and creates a draft Story in /stories.
                Always lands as draft so you can review before flipping live
                on mobile. */}
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Send className="h-3 w-3" />
                Publish to FBK app
                {cardPageCount() > 1 && (
                  <span className="font-normal text-gray-500 normal-case tracking-normal ml-1">
                    · {cardPageCount()} slides
                  </span>
                )}
              </div>
              <Button
                onClick={publishAsStory}
                disabled={
                  downloading !== null ||
                  bulkDownloading !== null ||
                  publishingStory
                }
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              >
                {publishingStory ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Publish as Story
              </Button>
              <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
                Lands as a <b>draft</b> in <code>/stories</code>. Review and
                flip the live toggle to show it in the FBK mobile app feed.
              </p>
            </div>

            {cardType === "USER_STORY" && userStoryContent.pages.length > 1 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <PackageOpen className="h-3 w-3" />
                  Download every chapter
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => downloadAllChapters("story")}
                    disabled={downloading !== null || bulkDownloading !== null}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                  >
                    {bulkDownloading === "story" ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <PackageOpen className="h-4 w-4 mr-2" />
                    )}
                    All · Story
                  </Button>
                  <Button
                    onClick={() => downloadAllChapters("post")}
                    disabled={downloading !== null || bulkDownloading !== null}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                  >
                    {bulkDownloading === "post" ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <PackageOpen className="h-4 w-4 mr-2" />
                    )}
                    All · Post
                  </Button>
                </div>
                <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
                  Cycles through every chapter and saves each as a separate PNG.
                </p>
              </div>
            )}

            {cardType === "PARTNER_PITCH" && (partnerPitchContent.extraPages?.length ?? 0) > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <PackageOpen className="h-3 w-3" />
                  Download every slide
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => downloadAllPitchSlides("story")}
                    disabled={downloading !== null || bulkDownloading !== null}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  >
                    {bulkDownloading === "story" ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <PackageOpen className="h-4 w-4 mr-2" />
                    )}
                    All · Story
                  </Button>
                  <Button
                    onClick={() => downloadAllPitchSlides("post")}
                    disabled={downloading !== null || bulkDownloading !== null}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  >
                    {bulkDownloading === "post" ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <PackageOpen className="h-4 w-4 mr-2" />
                    )}
                    All · Post
                  </Button>
                </div>
                <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
                  Cycles through cover + every chapter and saves all as one .zip.
                </p>
              </div>
            )}

            {cardType === "MATCHDAY" && matchdayContents.length > 1 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <PackageOpen className="h-3 w-3" />
                  Download every match in this matchweek
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => downloadAllMatchdays("story")}
                    disabled={downloading !== null || bulkDownloading !== null}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    {bulkDownloading === "story" ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <PackageOpen className="h-4 w-4 mr-2" />
                    )}
                    All · Story
                  </Button>
                  <Button
                    onClick={() => downloadAllMatchdays("post")}
                    disabled={downloading !== null || bulkDownloading !== null}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    {bulkDownloading === "post" ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <PackageOpen className="h-4 w-4 mr-2" />
                    )}
                    All · Post
                  </Button>
                </div>
                <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
                  Loops through every match in the picked gameweek and saves each as a separate PNG.
                </p>
              </div>
            )}

            {cardType === "MATCH_STORY" && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <PackageOpen className="h-3 w-3" />
                  Download all 3 pages (Cover · Result · Story)
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => downloadAllMatchStoryPages("story")}
                    disabled={downloading !== null || bulkDownloading !== null}
                    className="bg-rose-700 hover:bg-rose-800 text-white"
                  >
                    {bulkDownloading === "story" ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <PackageOpen className="h-4 w-4 mr-2" />
                    )}
                    All · Story
                  </Button>
                  <Button
                    onClick={() => downloadAllMatchStoryPages("post")}
                    disabled={downloading !== null || bulkDownloading !== null}
                    className="bg-rose-700 hover:bg-rose-800 text-white"
                  >
                    {bulkDownloading === "post" ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <PackageOpen className="h-4 w-4 mr-2" />
                    )}
                    All · Post
                  </Button>
                </div>
                <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
                  Captures every page and zips them. Post as an Instagram carousel.
                </p>
              </div>
            )}

            <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">
              Exports at 2× resolution for sharp text:<br />
              Story <span className="font-mono">2160×3840</span> · Post <span className="font-mono">2160×2700</span>
            </p>
          </div>
        </div>

        {/* RIGHT — PREVIEW */}
        <div className="col-span-12 lg:col-span-6 xl:col-span-7">
          <div className="bg-white rounded-lg shadow border border-gray-100 p-5 sticky top-4">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                Live preview
              </div>
              <div className="text-[11px] text-gray-500">
                Full-resolution · scaled to fit
              </div>
            </div>

            <div className="flex flex-wrap gap-8 justify-center items-start">
              <PreviewFrame
                label="Story · 1080×1920"
                format="story"
                onClick={() => setZoomedFormat("story")}
              >
                {renderCard("story", storyRef)}
              </PreviewFrame>
              <PreviewFrame
                label="Post · 1080×1350"
                format="post"
                onClick={() => setZoomedFormat("post")}
              >
                {renderCard("post", postRef)}
              </PreviewFrame>
            </div>
          </div>
        </div>
      </div>

      {zoomedFormat && (
        <ZoomModal
          format={zoomedFormat}
          onClose={() => setZoomedFormat(null)}
        >
          {renderCard(zoomedFormat)}
        </ZoomModal>
      )}
    </Layout>
  );
}

// ─── Zoom modal ──────────────────────────────────────────────────────────────

function ZoomModal({
  format,
  onClose,
  children,
}: {
  format: CardFormat;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const spec = CARD_FORMATS[format];
  // Fit the card to the viewport with some padding for UI chrome
  const scale = useMemo(() => {
    if (typeof window === "undefined") return 0.5;
    const vh = window.innerHeight - 120; // chrome + close button
    const vw = window.innerWidth - 80;
    return Math.min(vh / spec.height, vw / spec.width, 0.9);
  }, [spec.width, spec.height]);

  return (
    <div
      className="fixed inset-0 z-100 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition shadow-lg"
        title="Close (Esc)"
        aria-label="Close zoomed preview"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="absolute top-4 left-4 text-white/70 text-xs font-bold tracking-widest uppercase">
        {format === "story" ? "Story · 1080×1920" : "Post · 1080×1350"}
      </div>

      <div
        className="relative"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: spec.width * scale,
          height: spec.height * scale,
        }}
      >
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            width: spec.width,
            height: spec.height,
            borderRadius: 16 / scale,
            overflow: "hidden",
            boxShadow: `0 ${40 / scale}px ${80 / scale}px rgba(0,0,0,0.6)`,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Style tab ──────────────────────────────────────────────────────────────

function StyleTab({
  cardType,
  style,
  onChange,
}: {
  cardType: CardType;
  style: CardStyleSettings | undefined;
  onChange: (next: CardStyleSettings | undefined) => void;
}) {
  const updateField = <K extends keyof CardStyleSettings>(
    key: K,
    next: CardStyleSettings[K],
  ) => {
    const cur = style ?? {};
    const merged: CardStyleSettings = { ...cur, [key]: next };
    if (next === undefined) delete merged[key];
    onChange(Object.keys(merged).length === 0 ? undefined : merged);
  };

  // Build a per-template list of controllable fields with friendly labels
  type FieldKey =
    | "eyebrow"
    | "headline"
    | "goldHighlight"
    | "tagline"
    | "body"
    | "ctaText"
    | "numbers";

  interface FieldConfig {
    key: FieldKey;
    label: string;
    defaultColor: string;
    sizeMin: number;
    sizeMax: number;
    sizeDefault: number;
    showItalic?: boolean;
    showUnderline?: boolean;
    showAlign?: boolean;
    showBoldToggle?: boolean;
  }

  const FIELDS_BY_TEMPLATE: Record<CardType, FieldConfig[]> = {
    NEWS: [
      { key: "eyebrow", label: "Eyebrow tag", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "Headline", defaultColor: "#FFFFFF", sizeMin: 48, sizeMax: 180, sizeDefault: 124 },
      { key: "goldHighlight", label: "Gold highlight", defaultColor: "#FFD700", sizeMin: 48, sizeMax: 180, sizeDefault: 124 },
      { key: "body", label: "Body text", defaultColor: "rgba(255,255,255,0.85)", sizeMin: 16, sizeMax: 56, sizeDefault: 30 },
      { key: "ctaText", label: "CTA text", defaultColor: "#0A4724", sizeMin: 16, sizeMax: 40, sizeDefault: 26, showItalic: false, showUnderline: false, showAlign: false },
    ],
    ANNOUNCEMENT: [
      { key: "eyebrow", label: "Eyebrow tag", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "Headline", defaultColor: "#FFFFFF", sizeMin: 48, sizeMax: 180, sizeDefault: 124 },
      { key: "goldHighlight", label: "Gold highlight", defaultColor: "#FFD700", sizeMin: 48, sizeMax: 180, sizeDefault: 124 },
      { key: "body", label: "Body text", defaultColor: "rgba(255,255,255,0.85)", sizeMin: 16, sizeMax: 56, sizeDefault: 30 },
      { key: "ctaText", label: "CTA text", defaultColor: "#0A4724", sizeMin: 16, sizeMax: 40, sizeDefault: 26, showItalic: false, showUnderline: false, showAlign: false },
    ],
    DAILY_CHALLENGE: [
      { key: "eyebrow", label: "Eyebrow tag", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "Headline", defaultColor: "#FFFFFF", sizeMin: 48, sizeMax: 180, sizeDefault: 124 },
      { key: "goldHighlight", label: "Gold highlight", defaultColor: "#FFD700", sizeMin: 48, sizeMax: 180, sizeDefault: 124 },
      { key: "body", label: "Subtext", defaultColor: "rgba(255,255,255,0.85)", sizeMin: 16, sizeMax: 56, sizeDefault: 30 },
      { key: "ctaText", label: "CTA text", defaultColor: "#0A4724", sizeMin: 16, sizeMax: 40, sizeDefault: 28, showItalic: false, showUnderline: false, showAlign: false },
    ],
    LEADERBOARD: [
      { key: "eyebrow", label: "Eyebrow (LEADERBOARD)", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "Title (Matchweek N)", defaultColor: "#FFFFFF", sizeMin: 48, sizeMax: 160, sizeDefault: 104 },
      { key: "goldHighlight", label: "Title number (gold)", defaultColor: "#FFD700", sizeMin: 48, sizeMax: 160, sizeDefault: 104 },
      { key: "body", label: "Team names", defaultColor: "#FFFFFF", sizeMin: 16, sizeMax: 48, sizeDefault: 28 },
      { key: "numbers", label: "Points numbers", defaultColor: "#FFFFFF", sizeMin: 18, sizeMax: 64, sizeDefault: 36, showItalic: false, showUnderline: false, showAlign: false },
    ],
    LFS_RESULT: [
      { key: "eyebrow", label: "Eyebrow tag", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "numbers", label: "Stat numbers", defaultColor: "#FFFFFF", sizeMin: 80, sizeMax: 280, sizeDefault: 200, showItalic: false, showUnderline: false, showAlign: false },
      { key: "body", label: "Stat labels & picks", defaultColor: "rgba(255,255,255,0.85)", sizeMin: 14, sizeMax: 48, sizeDefault: 26 },
    ],
    USER_STORY: [
      { key: "eyebrow", label: "Eyebrow (THE STORY OF)", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 22, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "Player name", defaultColor: "#FFFFFF", sizeMin: 60, sizeMax: 240, sizeDefault: 200 },
      { key: "goldHighlight", label: "Last name (gold)", defaultColor: "#FFD700", sizeMin: 60, sizeMax: 240, sizeDefault: 200 },
      { key: "tagline", label: "Tagline / pull-quote", defaultColor: "rgba(255,215,0,0.95)", sizeMin: 18, sizeMax: 72, sizeDefault: 36, showBoldToggle: true },
      { key: "body", label: "Body text", defaultColor: "rgba(255,255,255,0.92)", sizeMin: 14, sizeMax: 48, sizeDefault: 26 },
      { key: "ctaText", label: "CTA text", defaultColor: "#0A4724", sizeMin: 16, sizeMax: 40, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
    ],
    MATCHDAY: [
      { key: "eyebrow", label: "Eyebrow (MATCHDAY · GROUP)", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 22, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "Home nation name", defaultColor: "#FFFFFF", sizeMin: 60, sizeMax: 240, sizeDefault: 168 },
      { key: "goldHighlight", label: "Away nation name (gold)", defaultColor: "#FFD700", sizeMin: 60, sizeMax: 240, sizeDefault: 168 },
      { key: "tagline", label: "Tagline / pull-quote", defaultColor: "rgba(255,215,0,0.95)", sizeMin: 16, sizeMax: 60, sizeDefault: 28, showBoldToggle: true },
      { key: "body", label: "Date / venue strip", defaultColor: "rgba(255,255,255,0.95)", sizeMin: 14, sizeMax: 40, sizeDefault: 24 },
      { key: "ctaText", label: "CTA text", defaultColor: "#0A4724", sizeMin: 16, sizeMax: 40, sizeDefault: 26, showItalic: false, showUnderline: false, showAlign: false },
    ],
    TEAM_OF_WEEK: [
      { key: "eyebrow", label: "Eyebrow (TEAM OF THE WEEK)", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "Gameweek title", defaultColor: "#FFFFFF", sizeMin: 48, sizeMax: 160, sizeDefault: 96 },
      { key: "goldHighlight", label: "Formation pill", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 40, sizeDefault: 26, showItalic: false, showUnderline: false, showAlign: false },
      { key: "tagline", label: "Tagline / pull-quote", defaultColor: "rgba(255,255,255,0.85)", sizeMin: 14, sizeMax: 48, sizeDefault: 26, showBoldToggle: true },
    ],
    PARTNER_PITCH: [
      { key: "eyebrow", label: "Eyebrow (FBK FOR…)", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 22, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "Headline", defaultColor: "#FFFFFF", sizeMin: 48, sizeMax: 200, sizeDefault: 132 },
      { key: "goldHighlight", label: "Closing line (gold)", defaultColor: "#FFD700", sizeMin: 48, sizeMax: 200, sizeDefault: 132 },
      { key: "body", label: "Steps & benefits text", defaultColor: "rgba(255,255,255,0.92)", sizeMin: 14, sizeMax: 48, sizeDefault: 26 },
      { key: "ctaText", label: "CTA pill text", defaultColor: "#0A4724", sizeMin: 16, sizeMax: 40, sizeDefault: 26, showItalic: false, showUnderline: false, showAlign: false },
    ],
    LEAGUE_LAUNCH: [
      { key: "eyebrow", label: "Eyebrow (PARTNER · MATCHWEEK)", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "PLAY. WIN. headline", defaultColor: "#FFFFFF", sizeMin: 60, sizeMax: 240, sizeDefault: 180 },
      { key: "goldHighlight", label: "Closing line (gold) — e.g. SIP.", defaultColor: "#FFD700", sizeMin: 60, sizeMax: 240, sizeDefault: 180 },
      { key: "tagline", label: "Tagline (league is live…)", defaultColor: "rgba(255,255,255,0.85)", sizeMin: 16, sizeMax: 60, sizeDefault: 30, showBoldToggle: true },
    ],
    FBK_LEAGUE: [
      { key: "eyebrow", label: "Eyebrow chip (FBK LEAGUE · MATCHWEEK)", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "PLAY. WIN. headline", defaultColor: "#FFFFFF", sizeMin: 60, sizeMax: 260, sizeDefault: 200 },
      { key: "goldHighlight", label: "Closing line (gold) — e.g. REPEAT.", defaultColor: "#FFD700", sizeMin: 60, sizeMax: 260, sizeDefault: 200 },
      { key: "tagline", label: "Tagline / subhead", defaultColor: "rgba(255,255,255,0.85)", sizeMin: 16, sizeMax: 60, sizeDefault: 30, showBoldToggle: true },
      { key: "ctaText", label: "CTA pill text", defaultColor: "#0A4724", sizeMin: 16, sizeMax: 40, sizeDefault: 28, showItalic: false, showUnderline: false, showAlign: false },
    ],
    COUNTDOWN: [
      { key: "eyebrow", label: "Eyebrow (WORLD CUP IN)", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 32, showItalic: false, showUnderline: false, showAlign: false },
      { key: "body", label: "Subtext line", defaultColor: "rgba(255,255,255,0.85)", sizeMin: 14, sizeMax: 56, sizeDefault: 32 },
      { key: "ctaText", label: "CTA pill text", defaultColor: "#0A4724", sizeMin: 16, sizeMax: 40, sizeDefault: 28, showItalic: false, showUnderline: false, showAlign: false },
    ],
    MATCH_RESULT: [
      { key: "eyebrow", label: "Eyebrow (MATCHWEEK · GROUP)", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "Headline (HOSTS STUN…)", defaultColor: "#FFFFFF", sizeMin: 32, sizeMax: 200, sizeDefault: 96 },
      { key: "tagline", label: "Tagline / hot take", defaultColor: "rgba(255,255,255,0.85)", sizeMin: 14, sizeMax: 56, sizeDefault: 26, showBoldToggle: true },
    ],
    MATCH_STORY: [
      { key: "eyebrow", label: "Eyebrow (THE STORY / GROUP X)", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "Story headline", defaultColor: "#FFFFFF", sizeMin: 48, sizeMax: 200, sizeDefault: 132 },
      { key: "goldHighlight", label: "Story closing line (gold)", defaultColor: "#FFD700", sizeMin: 48, sizeMax: 200, sizeDefault: 132 },
      { key: "tagline", label: "Cover tagline / pull-quote", defaultColor: "rgba(255,255,255,0.85)", sizeMin: 16, sizeMax: 60, sizeDefault: 32, showBoldToggle: true },
      { key: "body", label: "Story body paragraph", defaultColor: "rgba(255,255,255,0.85)", sizeMin: 14, sizeMax: 56, sizeDefault: 30 },
    ],
    TOP_SCORERS: [
      { key: "eyebrow", label: "Eyebrow (TOP SCORERS · MW)", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "Title (Top Scorers)", defaultColor: "#FFFFFF", sizeMin: 48, sizeMax: 180, sizeDefault: 120 },
      { key: "goldHighlight", label: "Gold accent (This Week)", defaultColor: "#FFD700", sizeMin: 48, sizeMax: 180, sizeDefault: 120 },
      { key: "tagline", label: "Tagline / pull-quote", defaultColor: "rgba(255,255,255,0.85)", sizeMin: 14, sizeMax: 48, sizeDefault: 26, showBoldToggle: true },
    ],
    CAPTAIN_PICKS: [
      { key: "eyebrow", label: "Eyebrow (MOST SELECTED CAPTAINS)", defaultColor: "#FFD700", sizeMin: 14, sizeMax: 48, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "Title (Captain Picks)", defaultColor: "#FFFFFF", sizeMin: 48, sizeMax: 180, sizeDefault: 120 },
      { key: "goldHighlight", label: "Gold accent (Top 10)", defaultColor: "#FFD700", sizeMin: 48, sizeMax: 180, sizeDefault: 120 },
      { key: "tagline", label: "Tagline / pull-quote", defaultColor: "rgba(255,255,255,0.85)", sizeMin: 14, sizeMax: 48, sizeDefault: 26, showBoldToggle: true },
    ],
    // Factsheet exposes shared typography styles that apply across every
    // slide in the deck. The four variants (cinematic / newsroom / pill /
    // memorial) each carry their own per-variant defaults inside the card
    // component; the values below let admin override font size, colour,
    // family, bold/italic, etc. via the Style tab.
    //
    // "tagline" is repurposed by the memorial variant as the dates-line
    // slot — the underlying CardStyleSettings.tagline field is unused by
    // Factsheet's other variants, so it's a clean piggyback rather than
    // adding a new schema key just for one variant.
    FACTSHEET: [
      { key: "eyebrow", label: "Eyebrow / top tag", defaultColor: "rgba(255,255,255,0.9)", sizeMin: 12, sizeMax: 56, sizeDefault: 22, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "Headline / name", defaultColor: "#FFFFFF", sizeMin: 48, sizeMax: 220, sizeDefault: 148 },
      { key: "tagline", label: "Dates line (memorial only)", defaultColor: "rgba(255,255,255,0.92)", sizeMin: 12, sizeMax: 56, sizeDefault: 26, showItalic: false, showUnderline: false, showAlign: false },
      { key: "body", label: "Body / tribute text", defaultColor: "rgba(255,255,255,0.9)", sizeMin: 14, sizeMax: 72, sizeDefault: 26, showItalic: true, showBoldToggle: true },
    ],
    // Bracket typography is fixed-by-design (FBK majestic). All editing
    // happens through round picker + matchup rows in the Form tab.
    BRACKET: [],
    // Champions: "tagline" slot styles the nation name (the underlying
    // field is otherwise unused by this template), "headline" styles the
    // big CHAMPIONS wordmark.
    CHAMPIONS: [
      { key: "eyebrow", label: "Eyebrow (WORLD CUP · FINAL)", defaultColor: "rgba(255,255,255,0.92)", sizeMin: 12, sizeMax: 56, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "tagline", label: "Nation name", defaultColor: "#FFFFFF", sizeMin: 32, sizeMax: 160, sizeDefault: 96 },
      { key: "headline", label: "CHAMPIONS wordmark", defaultColor: "#FFD700", sizeMin: 80, sizeMax: 340, sizeDefault: 260 },
      { key: "body", label: "Message text", defaultColor: "rgba(255,255,255,0.92)", sizeMin: 14, sizeMax: 72, sizeDefault: 28, showItalic: true, showBoldToggle: true },
    ],
    // Thank You: "tagline" slot styles the sub-headline under THANK YOU.
    THANK_YOU: [
      { key: "eyebrow", label: "Eyebrow (THAT'S A WRAP)", defaultColor: "#FFD700", sizeMin: 12, sizeMax: 56, sizeDefault: 24, showItalic: false, showUnderline: false, showAlign: false },
      { key: "headline", label: "THANK YOU wordmark", defaultColor: "#FFD700", sizeMin: 60, sizeMax: 320, sizeDefault: 240 },
      { key: "tagline", label: "Sub-headline", defaultColor: "rgba(255,255,255,0.95)", sizeMin: 18, sizeMax: 120, sizeDefault: 54 },
      { key: "body", label: "Message text", defaultColor: "rgba(255,255,255,0.82)", sizeMin: 14, sizeMax: 72, sizeDefault: 28, showItalic: true, showBoldToggle: true },
    ],
  };

  const fields = FIELDS_BY_TEMPLATE[cardType];
  const hasAnyOverride = !!(
    style &&
    Object.keys(style).some((k) => k !== "background" && (style as any)[k] !== undefined)
  );

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-gray-500 leading-relaxed flex-1">
          Customize each text field — font, size, color, italic, alignment.
          Defaults match the FBK brand.
        </p>
        {hasAnyOverride && (
          <button
            type="button"
            onClick={() => {
              const next = { ...(style ?? {}) };
              fields.forEach((f) => delete next[f.key]);
              onChange(Object.keys(next).length === 0 ? undefined : next);
            }}
            className="text-xs text-gray-500 hover:text-red-600 whitespace-nowrap"
          >
            Reset all
          </button>
        )}
      </div>

      {fields.map((f) => (
        <TextStyleControls
          key={f.key}
          label={f.label}
          value={style?.[f.key]}
          onChange={(v) => updateField(f.key, v as TextStyle | undefined)}
          defaultColor={f.defaultColor}
          sizeMin={f.sizeMin}
          sizeMax={f.sizeMax}
          sizeDefault={f.sizeDefault}
          showItalic={f.showItalic}
          showUnderline={f.showUnderline}
          showAlign={f.showAlign}
          showBoldToggle={f.showBoldToggle}
        />
      ))}
    </div>
  );
}

// ─── Forms ───────────────────────────────────────────────────────────────────

function NewsForm({
  cardType,
  content,
  onChange,
}: {
  cardType: "NEWS" | "ANNOUNCEMENT";
  content: NewsCardContent;
  onChange: (next: NewsCardContent) => void;
}) {
  const presets = cardType === "NEWS" ? NEWS_PRESETS : ANNOUNCEMENT_PRESETS;
  const update = <K extends keyof NewsCardContent>(
    key: K,
    value: NewsCardContent[K],
  ) => onChange({ ...content, [key]: value });

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="eyebrow">Eyebrow tag</Label>
        <div className="flex gap-2 mt-1">
          <Input
            id="eyebrow"
            value={content.eyebrow}
            onChange={(e) => update("eyebrow", e.target.value.toUpperCase())}
            className="font-mono uppercase"
          />
          <Select value="" onValueChange={(val) => update("eyebrow", val)}>
            <SelectTrigger className="w-32 shrink-0">
              <SelectValue placeholder="Presets" />
            </SelectTrigger>
            <SelectContent>
              {presets.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="headline">Headline</Label>
        <Textarea
          id="headline"
          rows={2}
          value={content.headline}
          onChange={(e) => update("headline", e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="goldHighlight">Second line (gold) — optional</Label>
        <Input
          id="goldHighlight"
          value={content.goldHighlight || ""}
          onChange={(e) => update("goldHighlight", e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="body">Body — optional</Label>
        <Textarea
          id="body"
          rows={3}
          value={content.body || ""}
          onChange={(e) => update("body", e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <Label>Content image — optional</Label>
        <div className="mt-1">
          <ImageUploadField
            value={content.imageUrl || undefined}
            onChange={(next) => update("imageUrl", next || "")}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="ctaText">CTA text — optional</Label>
        <Input
          id="ctaText"
          value={content.ctaText || ""}
          onChange={(e) => update("ctaText", e.target.value)}
          className="mt-1"
        />
      </div>

      <ThemePicker
        cardType={cardType}
        theme={content.theme}
        monoColor={content.monoColor}
        onThemeChange={(v) => update("theme", v)}
        onMonoColorChange={(v) => update("monoColor", v)}
      />

      <FooterFields
        idPrefix={cardType.toLowerCase()}
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft(cardType)}
        onFooterLeftChange={(v) => update("footerLeft", v)}
        onHashtagsChange={(v) => update("hashtags", v)}
        footer={content.footer}
        onFooterChange={(v) => update("footer", v)}
      />

      <HeaderFields
        idPrefix={cardType.toLowerCase()}
        value={content.header}
        onChange={(v) => update("header", v)}
      />
    </div>
  );
}

// ─── Leaderboard form (with live data source) ────────────────────────────────

type LeaderboardSource = "manual" | "global" | number;

function LeaderboardForm({
  content,
  onChange,
}: {
  content: LeaderboardCardContent;
  onChange: (next: LeaderboardCardContent) => void;
}) {
  const [source, setSource] = useState<LeaderboardSource>("manual");
  const { data: gameweeksData } = useGameweeks();
  const { data: globalLb, isFetching: globalFetching, refetch: refetchGlobal } =
    useGlobalLeaderboard(50, 0);
  const gameweekId = typeof source === "number" ? source : null;
  const { data: gwLb, isFetching: gwFetching, refetch: refetchGameweek } =
    useLeaderboardByGameweek(gameweekId);

  const liveEntries = useMemo<
    (FantasyLeaderboardEntry & { gameweekPoints?: number })[]
  >(() => {
    if (source === "global") return globalLb?.leaderboard ?? [];
    if (typeof source === "number") return gwLb?.leaderboard ?? [];
    return [];
  }, [source, globalLb, gwLb]);

  const isLive = source !== "manual";
  const isFetching =
    (source === "global" && globalFetching) ||
    (typeof source === "number" && gwFetching);

  const pointsFor = (e: FantasyLeaderboardEntry & { gameweekPoints?: number }) =>
    typeof source === "number"
      ? (e.gameweekPoints ?? e.totalPoints ?? 0)
      : (e.totalPoints ?? 0);

  const updateEntry = (idx: number, patch: Partial<LeaderboardEntry>) => {
    const next = [...content.entries];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...content, entries: next });
  };

  const addEntry = () => {
    if (content.entries.length >= 10) return;
    onChange({
      ...content,
      entries: [
        ...content.entries,
        { rank: content.entries.length + 1, teamName: "", username: "", countryIso2: "", points: 0 },
      ],
    });
  };

  const removeEntry = (idx: number) => {
    const next = content.entries
      .filter((_, i) => i !== idx)
      .map((e, i) => ({ ...e, rank: i + 1 }));
    onChange({ ...content, entries: next });
  };

  const handleSourceChange = (nextSource: LeaderboardSource) => {
    setSource(nextSource);
    if (typeof nextSource === "number") {
      // A specific gameweek was picked — mirror its round number into the
      // title and make sure we're in matchweek mode to show it.
      const gw = gameweeksData?.find((g) => g.id === nextSource);
      if (gw)
        onChange({ ...content, matchweek: gw.roundNumber, scope: "matchweek" });
    } else if (nextSource === "global") {
      // Global standings are cumulative, so a "Matchweek N" title would
      // misrepresent them. Flip to All Time automatically.
      onChange({
        ...content,
        scope: "alltime",
        eyebrow:
          !content.eyebrow || content.eyebrow === "LEADERBOARD"
            ? "ALL-TIME LEADERBOARD"
            : content.eyebrow,
      });
    }
  };

  const fillTopTen = () => {
    if (liveEntries.length === 0) {
      toast.error("No live entries available yet");
      return;
    }
    const next = liveEntries.slice(0, 10).map((e, i) => ({
      rank: i + 1,
      teamName: e.teamName || "Untitled team",
      username: e.username || "",
      countryIso2: "",
      points: pointsFor(e),
    }));
    onChange({ ...content, entries: next });
    toast.success(`Filled top ${next.length} from live data`);
  };

  const addUserById = (userIdStr: string) => {
    if (content.entries.length >= 10) {
      toast.warning("Card is at the 10-row max");
      return;
    }
    const userId = Number(userIdStr);
    const user = liveEntries.find((e) => e.userId === userId);
    if (!user) return;
    if (content.entries.some((e) => e.username === user.username)) {
      toast.info(`@${user.username} is already on the card`);
      return;
    }
    onChange({
      ...content,
      entries: [
        ...content.entries,
        {
          rank: content.entries.length + 1,
          teamName: user.teamName || "Untitled team",
          username: user.username || "",
          countryIso2: "",
          points: pointsFor(user),
        },
      ],
    });
  };

  return (
    <div className="space-y-4">
      {/* Live source picker */}
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-emerald-900">Data source</Label>
          {isLive && (
            <button
              type="button"
              onClick={() => (source === "global" ? refetchGlobal() : refetchGameweek())}
              disabled={isFetching}
              className="text-xs text-emerald-700 hover:text-emerald-900 flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={cn("h-3 w-3", isFetching && "animate-spin")} />
              Refresh
            </button>
          )}
        </div>

        <Select
          value={String(source)}
          onValueChange={(val) =>
            handleSourceChange(
              val === "manual" || val === "global"
                ? (val as LeaderboardSource)
                : Number(val),
            )
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">Manual entry</SelectItem>
            <SelectItem value="global">Global · All-time</SelectItem>
            {gameweeksData?.map((gw) => (
              <SelectItem key={gw.id} value={String(gw.id)}>
                Gameweek {gw.roundNumber} — {gw.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {isLive && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-emerald-700" />
              <span className="text-xs text-emerald-800 font-semibold">
                {isFetching
                  ? "Loading…"
                  : `${liveEntries.length} users loaded — sorted by points`}
              </span>
            </div>

            <div className="flex gap-2">
              <Select value="" onValueChange={addUserById} disabled={liveEntries.length === 0}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Pick a user to add…" />
                </SelectTrigger>
                <SelectContent>
                  {liveEntries.slice(0, 50).map((e) => (
                    <SelectItem key={e.userId} value={String(e.userId)}>
                      #{e.rank} · {e.teamName || "Untitled"} (@{e.username}) — {pointsFor(e)} pts
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={fillTopTen}
                disabled={liveEntries.length === 0}
                className="shrink-0 gap-1"
              >
                <Sparkles className="h-3 w-3" />
                Fill top 10
              </Button>
            </div>
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="lb-scope">Period</Label>
        <select
          id="lb-scope"
          value={content.scope ?? "matchweek"}
          onChange={(e) => {
            const scope = e.target.value as LeaderboardScope;
            // Swap the eyebrow to match the period, but only when it is
            // still whatever the previous scope defaulted to — a hand-typed
            // eyebrow is the admin's and must survive the switch.
            const autoEyebrows = ["LEADERBOARD", "ALL-TIME LEADERBOARD"];
            const eyebrowIsUntouched =
              !content.eyebrow || autoEyebrows.includes(content.eyebrow);
            onChange({
              ...content,
              scope,
              eyebrow: eyebrowIsUntouched
                ? scope === "alltime"
                  ? "ALL-TIME LEADERBOARD"
                  : "LEADERBOARD"
                : content.eyebrow,
            });
          }}
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="matchweek">Matchweek — "Matchweek 7"</option>
          <option value="alltime">All Time — "All Time"</option>
          <option value="custom">Custom — your own wording</option>
        </select>
      </div>

      {(content.scope ?? "matchweek") === "matchweek" && (
        <div>
          <Label htmlFor="matchweek">Matchweek</Label>
          <Input
            id="matchweek"
            type="number"
            min={1}
            value={content.matchweek}
            onChange={(e) => onChange({ ...content, matchweek: Number(e.target.value) || 0 })}
            className="mt-1"
          />
        </div>
      )}

      {content.scope === "custom" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="lb-custom-title">Title</Label>
            <Input
              id="lb-custom-title"
              value={content.customTitle ?? ""}
              onChange={(e) => onChange({ ...content, customTitle: e.target.value })}
              placeholder="Group"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="lb-custom-accent">Accent word</Label>
            <Input
              id="lb-custom-accent"
              value={content.customTitleAccent ?? ""}
              onChange={(e) =>
                onChange({ ...content, customTitleAccent: e.target.value })
              }
              placeholder="Stage"
              className="mt-1"
            />
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="lb-eyebrow">Eyebrow</Label>
        <Input
          id="lb-eyebrow"
          value={content.eyebrow ?? ""}
          onChange={(e) => onChange({ ...content, eyebrow: e.target.value })}
          placeholder="LEADERBOARD"
          className="mt-1"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Top 10 entries</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addEntry}
            disabled={content.entries.length >= 10}
            className="gap-1"
          >
            <Plus className="h-3 w-3" />
            Add row
          </Button>
        </div>
        <div className="space-y-2 max-h-140 overflow-y-auto pr-1">
          {content.entries.map((entry, idx) => (
            <div
              key={idx}
              className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2"
            >
              {/* Top row: rank chip + team name + delete */}
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
                  {entry.rank}
                </span>
                <Input
                  className="flex-1 h-8 text-xs font-semibold"
                  placeholder="Team name"
                  value={entry.teamName}
                  onChange={(e) => updateEntry(idx, { teamName: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => removeEntry(idx)}
                  className="text-gray-400 hover:text-red-600 shrink-0 p-1"
                  title="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              {/* Bottom row: username + ISO + points (labeled) */}
              <div className="flex items-center gap-2 pl-9">
                <div className="flex-1 flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider">@</span>
                  <Input
                    className="h-7 text-xs flex-1"
                    placeholder="username"
                    value={entry.username}
                    onChange={(e) => updateEntry(idx, { username: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider">ISO</span>
                  <Input
                    className="h-7 text-xs w-12 font-mono uppercase text-center"
                    placeholder="np"
                    maxLength={2}
                    value={entry.countryIso2 || ""}
                    onChange={(e) =>
                      updateEntry(idx, { countryIso2: e.target.value.toLowerCase() })
                    }
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider">PTS</span>
                  <Input
                    className="h-7 text-xs w-20 text-right"
                    placeholder="0"
                    type="number"
                    value={entry.points}
                    onChange={(e) =>
                      updateEntry(idx, { points: Number(e.target.value) || 0 })
                    }
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          ISO2 codes (e.g. <code>br</code>, <code>gb</code>) show a flag next to the row. Optional.
        </p>
      </div>

      <ThemePicker
        cardType="LEADERBOARD"
        theme={content.theme}
        monoColor={content.monoColor}
        onThemeChange={(v) => onChange({ ...content, theme: v })}
        onMonoColorChange={(v) => onChange({ ...content, monoColor: v })}
      />

      <FooterFields
        idPrefix="lb"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("LEADERBOARD")}
        onFooterLeftChange={(v) => onChange({ ...content, footerLeft: v })}
        onHashtagsChange={(v) => onChange({ ...content, hashtags: v })}
        footer={content.footer}
        onFooterChange={(v) => onChange({ ...content, footer: v })}
      />

      <HeaderFields
        idPrefix="lb"
        value={content.header}
        onChange={(v) => onChange({ ...content, header: v })}
      />
    </div>
  );
}

// ─── LFS form ────────────────────────────────────────────────────────────────

function LfsForm({
  content,
  onChange,
}: {
  content: LfsCardContent;
  onChange: (next: LfsCardContent) => void;
}) {
  const updatePick = (idx: number, patch: Partial<LfsNationPick>) => {
    const next = [...content.topPicks];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...content, topPicks: next });
  };

  const addPick = () => {
    if (content.topPicks.length >= 3) return;
    onChange({
      ...content,
      topPicks: [...content.topPicks, { countryIso2: "", countryName: "", count: 0 }],
    });
  };

  const removePick = (idx: number) => {
    onChange({ ...content, topPicks: content.topPicks.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label htmlFor="lfs-round">Round</Label>
          <Input
            id="lfs-round"
            type="number"
            min={1}
            value={content.round}
            onChange={(e) => onChange({ ...content, round: Number(e.target.value) || 0 })}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="lfs-survivors">Survivors</Label>
          <Input
            id="lfs-survivors"
            type="number"
            min={0}
            value={content.survivors}
            onChange={(e) => onChange({ ...content, survivors: Number(e.target.value) || 0 })}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="lfs-eliminated">Eliminated</Label>
          <Input
            id="lfs-eliminated"
            type="number"
            min={0}
            value={content.eliminated}
            onChange={(e) => onChange({ ...content, eliminated: Number(e.target.value) || 0 })}
            className="mt-1"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Top 3 picked nations</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addPick}
            disabled={content.topPicks.length >= 3}
            className="gap-1"
          >
            <Plus className="h-3 w-3" />
            Add
          </Button>
        </div>
        <div className="space-y-2">
          {content.topPicks.map((pick, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-gray-50 rounded p-2">
              <Input
                className="col-span-3 h-8 text-xs font-mono uppercase"
                placeholder="iso2"
                maxLength={2}
                value={pick.countryIso2}
                onChange={(e) => updatePick(idx, { countryIso2: e.target.value.toLowerCase() })}
              />
              <Input
                className="col-span-5 h-8 text-xs"
                placeholder="Country"
                value={pick.countryName}
                onChange={(e) => updatePick(idx, { countryName: e.target.value })}
              />
              <Input
                className="col-span-3 h-8 text-xs text-right"
                placeholder="count"
                type="number"
                value={pick.count}
                onChange={(e) => updatePick(idx, { count: Number(e.target.value) || 0 })}
              />
              <button
                type="button"
                onClick={() => removePick(idx)}
                className="col-span-1 text-red-500 hover:text-red-700 flex justify-center"
                title="Remove"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <ThemePicker
        cardType="LFS_RESULT"
        theme={content.theme}
        monoColor={content.monoColor}
        onThemeChange={(v) => onChange({ ...content, theme: v })}
        onMonoColorChange={(v) => onChange({ ...content, monoColor: v })}
      />

      <FooterFields
        idPrefix="lfs"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("LFS_RESULT")}
        onFooterLeftChange={(v) => onChange({ ...content, footerLeft: v })}
        onHashtagsChange={(v) => onChange({ ...content, hashtags: v })}
        footer={content.footer}
        onFooterChange={(v) => onChange({ ...content, footer: v })}
      />

      <HeaderFields
        idPrefix="lfs"
        value={content.header}
        onChange={(v) => onChange({ ...content, header: v })}
      />
    </div>
  );
}

// ─── Daily Challenge form ────────────────────────────────────────────────────

function DailyChallengeForm({
  content,
  onChange,
}: {
  content: DailyChallengeCardContent;
  onChange: (next: DailyChallengeCardContent) => void;
}) {
  const update = <K extends keyof DailyChallengeCardContent>(
    key: K,
    value: DailyChallengeCardContent[K],
  ) => onChange({ ...content, [key]: value });

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="dc-type">Challenge type</Label>
        <Select
          value={content.challengeType}
          onValueChange={(val) => update("challengeType", val as DailyChallengeType)}
        >
          <SelectTrigger id="dc-type" className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CHALLENGE_TYPES.map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="dc-headline">Headline</Label>
        <Textarea
          id="dc-headline"
          rows={2}
          value={content.headline}
          onChange={(e) => update("headline", e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="dc-gold">Second line (gold) — optional</Label>
        <Input
          id="dc-gold"
          value={content.goldHighlight || ""}
          onChange={(e) => update("goldHighlight", e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="dc-subtext">Subtext — optional</Label>
        <Textarea
          id="dc-subtext"
          rows={3}
          value={content.subtext || ""}
          onChange={(e) => update("subtext", e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="dc-cta">CTA text — optional</Label>
        <Input
          id="dc-cta"
          value={content.ctaText || ""}
          onChange={(e) => update("ctaText", e.target.value)}
          className="mt-1"
        />
      </div>

      <ThemePicker
        cardType="DAILY_CHALLENGE"
        theme={content.theme}
        monoColor={content.monoColor}
        onThemeChange={(v) => update("theme", v)}
        onMonoColorChange={(v) => update("monoColor", v)}
      />

      <FooterFields
        idPrefix="dc"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("DAILY_CHALLENGE")}
        onFooterLeftChange={(v) => update("footerLeft", v)}
        onHashtagsChange={(v) => update("hashtags", v)}
        footer={content.footer}
        onFooterChange={(v) => update("footer", v)}
      />

      <HeaderFields
        idPrefix="dc"
        value={content.header}
        onChange={(v) => update("header", v)}
      />
    </div>
  );
}

// ─── User Story form ─────────────────────────────────────────────────────────

const STORY_EYEBROW_PRESETS = [
  "THE STORY OF",
  "INSIDE THE LIFE OF",
  "PLAYER PROFILE",
  "FROM THE GROUND UP",
  "THE MAKING OF",
];

function UserStoryForm({
  content,
  onChange,
  activePageIndex,
  onActivePageChange,
}: {
  content: UserStoryCardContent;
  onChange: (next: UserStoryCardContent) => void;
  activePageIndex: number;
  onActivePageChange: (next: number) => void;
}) {
  const pages = content.pages;
  const safeIndex = Math.min(activePageIndex, pages.length - 1);
  const page = pages[safeIndex] ?? BLANK_STORY_PAGE;
  const isCover = safeIndex === 0;

  const updatePage = (patch: Partial<StoryPage>) => {
    const next = [...pages];
    next[safeIndex] = { ...next[safeIndex], ...patch };
    onChange({ ...content, pages: next });
  };

  const addChapter = () => {
    const nextNum = pages.length + 1;
    const newPage: StoryPage = {
      ...BLANK_STORY_PAGE,
      eyebrow: `CHAPTER ${String(nextNum - 1).padStart(2, "0")}`,
    };
    onChange({ ...content, pages: [...pages, newPage] });
    onActivePageChange(pages.length);
  };

  const duplicateChapter = () => {
    const next = [...pages];
    const copy: StoryPage = { ...pages[safeIndex] };
    next.splice(safeIndex + 1, 0, copy);
    onChange({ ...content, pages: next });
    onActivePageChange(safeIndex + 1);
  };

  const deleteChapter = () => {
    if (pages.length <= 1) return;
    const next = pages.filter((_, i) => i !== safeIndex);
    onChange({ ...content, pages: next });
    onActivePageChange(Math.max(0, safeIndex - 1));
  };

  const movePage = (direction: -1 | 1) => {
    const j = safeIndex + direction;
    if (j < 0 || j >= pages.length) return;
    const next = [...pages];
    [next[safeIndex], next[j]] = [next[j], next[safeIndex]];
    onChange({ ...content, pages: next });
    onActivePageChange(j);
  };

  return (
    <div className="space-y-4">
      {/* Chapter tabs */}
      <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-rose-900">
            Chapters · {pages.length}
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addChapter}
            className="gap-1 h-7 text-xs"
          >
            <Plus className="h-3 w-3" />
            Add chapter
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {pages.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onActivePageChange(i)}
              className={cn(
                "px-2.5 py-1.5 rounded-md text-xs font-bold transition flex items-center gap-1.5 min-w-0",
                i === safeIndex
                  ? "bg-rose-600 text-white shadow"
                  : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50",
              )}
              title={p.name || `Page ${i + 1}`}
            >
              <span className="text-[10px] opacity-60">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="truncate max-w-[100px]">
                {i === 0 ? "Cover" : p.name || `Chapter ${i}`}
              </span>
            </button>
          ))}
        </div>

        {pages.length > 1 && (
          <div className="flex items-center gap-1.5 pt-1.5 border-t border-rose-200">
            <button
              type="button"
              onClick={() => movePage(-1)}
              disabled={safeIndex === 0}
              className="text-[11px] text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed px-2 py-1 rounded hover:bg-white"
              title="Move left"
            >
              ← Move
            </button>
            <button
              type="button"
              onClick={() => movePage(1)}
              disabled={safeIndex === pages.length - 1}
              className="text-[11px] text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed px-2 py-1 rounded hover:bg-white"
              title="Move right"
            >
              Move →
            </button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={duplicateChapter}
              className="text-[11px] text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-white flex items-center gap-1"
              title="Duplicate this chapter"
            >
              <Copy className="h-3 w-3" />
              Duplicate
            </button>
            <button
              type="button"
              onClick={deleteChapter}
              className="text-[11px] text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-white flex items-center gap-1"
              title="Delete this chapter"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Page-specific fields */}
      <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        {isCover ? "Cover page" : `Chapter ${safeIndex} content`}
      </div>

      <div>
        <Label htmlFor="us-eyebrow">Eyebrow tag</Label>
        <div className="flex gap-2 mt-1">
          <Input
            id="us-eyebrow"
            value={page.eyebrow}
            onChange={(e) => updatePage({ eyebrow: e.target.value.toUpperCase() })}
            className="font-mono uppercase"
          />
          <Select value="" onValueChange={(val) => updatePage({ eyebrow: val })}>
            <SelectTrigger className="w-32 shrink-0">
              <SelectValue placeholder="Presets" />
            </SelectTrigger>
            <SelectContent>
              {STORY_EYEBROW_PRESETS.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="us-name">
          {isCover ? "First name / first word" : "Chapter title"}
        </Label>
        <Input
          id="us-name"
          value={page.name}
          onChange={(e) => updatePage({ name: e.target.value })}
          placeholder={isCover ? "Lionel" : "The diagnosis"}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="us-gold">
          {isCover ? "Last name (gold) — optional" : "Title gold word — optional"}
        </Label>
        <Input
          id="us-gold"
          value={page.goldHighlight || ""}
          onChange={(e) => updatePage({ goldHighlight: e.target.value })}
          placeholder={isCover ? "Messi." : ""}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="us-tagline">Tagline / pull-quote — optional</Label>
        <Input
          id="us-tagline"
          value={page.tagline || ""}
          onChange={(e) => updatePage({ tagline: e.target.value })}
          placeholder="One line that hooks the reader"
          className="mt-1"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Renders with a gold pull-quote bar. Keep it short — one breath.
        </p>
      </div>

      <div>
        <Label htmlFor="us-body">Body — short and punchy</Label>
        <Textarea
          id="us-body"
          rows={6}
          value={page.body}
          onChange={(e) => updatePage({ body: e.target.value })}
          className="mt-1"
          placeholder="2–3 sentences per chapter. The first letter gets a magazine drop-cap."
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Use double line-breaks between paragraphs. Each chapter = one short moment.
        </p>
      </div>

      <div>
        <Label>Side portrait image</Label>
        <p className="text-[11px] text-gray-500 mt-0.5 mb-1.5">
          Tall portrait works best (3:4 or 2:3). Cutout PNGs cast a true
          silhouette shadow. Each chapter can have its own.
        </p>
        <ImageUploadField
          value={page.portraitImageUrl || undefined}
          onChange={(next) => updatePage({ portraitImageUrl: next || "" })}
        />
      </div>

      <div>
        <Label>Chapter background image — optional</Label>
        <p className="text-[11px] text-gray-500 mt-0.5 mb-1.5">
          Overrides the card-level <b>Background</b> tab image for this chapter
          only. Leave empty to fall back to the card-level background.
        </p>
        <ImageUploadField
          value={page.backgroundImageUrl || undefined}
          onChange={(next) => updatePage({ backgroundImageUrl: next || "" })}
        />
      </div>

      <div>
        <Label htmlFor="us-stats">Stats line — optional</Label>
        <Input
          id="us-stats"
          value={page.statsLine || ""}
          onChange={(e) => updatePage({ statsLine: e.target.value })}
          placeholder="Born 1987 · Argentina · 821 goals"
          className="mt-1 font-mono text-xs"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Usually only on the cover.
        </p>
      </div>

      <div>
        <Label htmlFor="us-cta">CTA text — optional</Label>
        <Input
          id="us-cta"
          value={page.ctaText || ""}
          onChange={(e) => updatePage({ ctaText: e.target.value })}
          placeholder="Read the full story"
          className="mt-1"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Tip: leave blank on intermediate chapters, add only on the final card.
        </p>
      </div>

      <ThemePicker
        cardType="USER_STORY"
        theme={content.theme}
        monoColor={content.monoColor}
        onThemeChange={(v) => onChange({ ...content, theme: v })}
        onMonoColorChange={(v) => onChange({ ...content, monoColor: v })}
      />

      <FooterFields
        idPrefix="us"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("USER_STORY")}
        onFooterLeftChange={(v) => onChange({ ...content, footerLeft: v })}
        onHashtagsChange={(v) => onChange({ ...content, hashtags: v })}
        footer={content.footer}
        onFooterChange={(v) => onChange({ ...content, footer: v })}
      />

      <HeaderFields
        idPrefix="us"
        value={content.header}
        onChange={(v) => onChange({ ...content, header: v })}
      />

      <p className="text-[11px] text-gray-500 leading-relaxed bg-rose-50/50 border border-rose-200 rounded p-2.5">
        <b>Tip:</b> Upload an atmospheric background in the <b>Background</b> tab
        (stadium, hometown, training pitch) for full editorial vibe.
      </p>
    </div>
  );
}

// ─── Matchday form (live match picker + per-match editor) ────────────────────

const MATCHDAY_EYEBROW_PRESETS = [
  "MATCHDAY 01",
  "MATCHDAY 02",
  "MATCHDAY 03",
  "GROUP STAGE",
  "ROUND OF 16",
  "QUARTER FINAL",
  "SEMI FINAL",
  "FINAL",
];

function MatchdayForm({
  contents,
  onChange,
  activeIndex,
  onActiveIndexChange,
  source,
  onSourceChange,
}: {
  contents: MatchdayCardContent[];
  onChange: (next: MatchdayCardContent[]) => void;
  activeIndex: number;
  onActiveIndexChange: (next: number) => void;
  source: "manual" | number;
  onSourceChange: (next: "manual" | number) => void;
}) {
  const safeIndex = Math.min(activeIndex, Math.max(contents.length - 1, 0));
  const md = contents[safeIndex] ?? initialMatchdayContent;

  const { data: gameweeksData } = useGameweeks();
  const { data: matchesPage, isFetching: matchesFetching } = useMatchesAdmin({
    limit: 200,
  });
  const allMatches: Match[] = useMemo(
    () => matchesPage?.matches ?? [],
    [matchesPage],
  );

  // Match count per gameweek for the dropdown labels
  const matchCountByGw = useMemo(() => {
    const map = new Map<number, number>();
    for (const m of allMatches) {
      if (m.gameweekId != null) {
        map.set(m.gameweekId, (map.get(m.gameweekId) ?? 0) + 1);
      }
    }
    return map;
  }, [allMatches]);

  const handleSourceChange = (next: "manual" | number) => {
    onSourceChange(next);
    if (next === "manual") {
      onChange([initialMatchdayContent]);
      onActiveIndexChange(0);
      return;
    }
    const gw = gameweeksData?.find((g) => g.id === next);
    const gwLabel = gw?.name;
    const matchesInGw = allMatches
      .filter((m) => m.gameweekId === next)
      .sort(
        (a, b) =>
          new Date(a.kickoffTime).getTime() - new Date(b.kickoffTime).getTime(),
      );
    if (matchesInGw.length === 0) {
      toast.info("That gameweek has no matches yet — add some in Matches first");
      onChange([{ ...initialMatchdayContent, eyebrow: (gwLabel || "MATCHDAY").toUpperCase() }]);
      onActiveIndexChange(0);
      return;
    }
    onChange(matchesInGw.map((m) => matchdayFromMatch(m, gwLabel)));
    onActiveIndexChange(0);
    toast.success(`Loaded ${matchesInGw.length} match${matchesInGw.length > 1 ? "es" : ""} from ${gwLabel}`);
  };

  const updateMatch = (patch: Partial<MatchdayCardContent>) => {
    const copy = [...contents];
    if (copy[safeIndex]) {
      copy[safeIndex] = { ...copy[safeIndex], ...patch };
    }
    onChange(copy);
  };

  const updateHomeNation = (patch: Partial<MatchdayCardContent["home"]>) =>
    updateMatch({ home: { ...md.home, ...patch } });
  const updateAwayNation = (patch: Partial<MatchdayCardContent["away"]>) =>
    updateMatch({ away: { ...md.away, ...patch } });

  const [embedding, setEmbedding] = useState<"home" | "away" | null>(null);

  /**
   * When admin leaves the flag URL input, try to fetch + embed the image
   * as a data URL. CORS-friendly sources succeed silently; CORS-blocked
   * sources fall back to the raw URL with a warning toast.
   */
  const tryAutoEmbed = async (side: "home" | "away", url: string | undefined) => {
    if (!url) return;
    if (url.startsWith("data:")) return; // already embedded
    setEmbedding(side);
    const result = await autoEmbedImage(url);
    setEmbedding(null);
    if (result.success) {
      if (side === "home") updateHomeNation({ flagUrl: result.dataUrl });
      else updateAwayNation({ flagUrl: result.dataUrl });
      toast.success(`${side === "home" ? "Home" : "Away"} crest embedded`);
    } else {
      toast.warning(
        `${side === "home" ? "Home" : "Away"} crest URL is CORS-blocked — preview works, but export may fail. Use Upload PNG below for reliable export.`,
      );
    }
  };

  const isSplit = md.theme === "split";
  const homeAutoColor = getNationColors(md.home.code).primary;
  const awayAutoColor = getNationColors(md.away.code).primary;
  const homeColorValue = md.homeColor || homeAutoColor;
  const awayColorValue = md.awayColor || awayAutoColor;

  return (
    <div className="space-y-4">
      {/* SOURCE picker */}
      <div className="rounded-lg border border-orange-200 bg-orange-50/40 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-orange-900">Data source</Label>
          {matchesFetching && (
            <span className="text-[11px] text-orange-700 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading matches
            </span>
          )}
        </div>
        <Select
          value={String(source)}
          onValueChange={(val) =>
            handleSourceChange(val === "manual" ? "manual" : Number(val))
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">Manual entry</SelectItem>
            {gameweeksData?.map((gw) => {
              const count = matchCountByGw.get(gw.id) ?? 0;
              return (
                <SelectItem key={gw.id} value={String(gw.id)}>
                  {gw.name} {count > 0 ? `· ${count} matches` : "· no matches"}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {source !== "manual" && contents.length > 1 && (
          <p className="text-[11px] text-orange-800">
            <Sparkles className="h-3 w-3 inline mr-1" />
            {contents.length} matches loaded — flip between them below, or use
            the bulk "Download every match" button in Export.
          </p>
        )}
      </div>

      {/* MATCH NAVIGATOR (when multiple) */}
      {contents.length > 1 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-gray-900">
              Match {safeIndex + 1} of {contents.length}
            </Label>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => onActiveIndexChange(Math.max(0, safeIndex - 1))}
                disabled={safeIndex === 0}
                className="p-1 rounded text-gray-600 hover:bg-white disabled:opacity-30"
                title="Previous match"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() =>
                  onActiveIndexChange(Math.min(contents.length - 1, safeIndex + 1))
                }
                disabled={safeIndex === contents.length - 1}
                className="p-1 rounded text-gray-600 hover:bg-white disabled:opacity-30"
                title="Next match"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {contents.map((c, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onActiveIndexChange(i)}
                className={cn(
                  "px-2 py-1 rounded text-xs font-bold transition flex items-center gap-1.5",
                  i === safeIndex
                    ? "bg-orange-600 text-white shadow"
                    : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50",
                )}
                title={`${c.home.name} vs ${c.away.name}`}
              >
                <span className="font-mono text-[10px] opacity-70">
                  {c.home.code.toUpperCase()}
                </span>
                <Swords className="h-2.5 w-2.5 opacity-50" />
                <span className="font-mono text-[10px] opacity-70">
                  {c.away.code.toUpperCase()}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* THEME toggle */}
      <div className="space-y-2">
        <Label>Visual theme</Label>
        <div className="grid grid-cols-2 gap-2">
          {(
            [
              {
                value: "signature",
                label: "FBK signature",
                desc: "Brand green gradient + gold glow",
                swatch:
                  "linear-gradient(135deg, #0D8C3C 0%, #0D5C2E 60%, #07321A 100%)",
              },
              {
                value: "split",
                label: "Two-tone split",
                desc: "Home/away colors with a thin gold seam",
                swatch: `linear-gradient(90deg, ${homeColorValue} 0% 49.85%, #FFD700 49.85% 50.15%, ${awayColorValue} 50.15% 100%)`,
              },
              {
                value: "spotlight",
                label: "Spotlight",
                desc: "Dark stadium night + gold halo. Big-match vibe.",
                swatch:
                  "radial-gradient(ellipse 70% 60% at 50% 48%, #1c3a25 0%, #000000 100%)",
              },
              {
                value: "pitch",
                label: "Pitch (tactical)",
                desc: "Flat pitch green with chalk-line markings.",
                swatch:
                  "linear-gradient(180deg, #186b32 0%, #0e4a21 100%)",
              },
              {
                value: "monochrome",
                label: "Monochrome",
                desc: "Full-bleed single nation color. Confident solo poster.",
                swatch: homeColorValue,
              },
              {
                value: "aurora",
                label: "Aurora",
                desc: "Smooth home → gold → away blend. Atmospheric.",
                swatch: `radial-gradient(ellipse at 15% 20%, ${homeColorValue} 0%, transparent 55%), radial-gradient(ellipse at 85% 80%, ${awayColorValue} 0%, transparent 55%), radial-gradient(ellipse at 50% 50%, rgba(255,215,0,0.4) 0%, transparent 60%), linear-gradient(135deg, #0D5C2E 0%, #07321A 100%)`,
              },
              {
                value: "editorial",
                label: "Editorial",
                desc: "Cream + serif magazine cover. Sophisticated.",
                swatch: "#F4EDD9",
              },
              {
                value: "halftone",
                label: "Halftone",
                desc: "Two-tone with retro dot pattern. Pop-art / Panini.",
                swatch: `linear-gradient(180deg, ${homeColorValue} 0% 50%, #1A1A1A 50% 100%)`,
              },
              {
                value: "stripes",
                label: "Energy stripes",
                desc: "Diagonal home/away/gold stripes. Sporty motion.",
                swatch: `linear-gradient(135deg, ${homeColorValue} 0% 28%, #FFD700 28% 30%, ${awayColorValue} 30% 70%, #FFD700 70% 72%, ${homeColorValue} 72% 100%)`,
              },
              {
                value: "rivalry",
                label: "Rivalry",
                desc: "Flag halves + giant centre headline. The “Deja Vu?” poster.",
                swatch: `linear-gradient(90deg, ${homeColorValue} 0% 48%, #0a0a0a 48% 52%, ${awayColorValue} 52% 100%)`,
              },
              {
                value: "duel",
                label: "Duel",
                desc: "Cinematic black + dual spotlights + circular portraits.",
                swatch: `radial-gradient(circle at 18% 50%, ${homeColorValue}aa 0%, transparent 60%), radial-gradient(circle at 82% 50%, ${awayColorValue}aa 0%, transparent 60%), #000000`,
              },
              {
                value: "marquee",
                label: "Marquee (fight night)",
                desc: "Diagonal nation blocks + bold slab typography. Boxing-poster.",
                swatch: `linear-gradient(135deg, ${homeColorValue} 0% 32%, #FFD700 32% 34%, #0A0A0A 34% 66%, #FFD700 66% 68%, ${awayColorValue} 68% 100%)`,
              },
            ] as { value: MatchdayTheme; label: string; desc: string; swatch: string }[]
          ).map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => updateMatch({ theme: t.value })}
              className={cn(
                "p-3 rounded-lg border text-left transition flex flex-col gap-2",
                md.theme === t.value
                  ? "border-orange-600 bg-orange-50 ring-2 ring-orange-600/15"
                  : "border-gray-200 bg-white hover:border-gray-300",
              )}
            >
              <div
                className="w-full h-10 rounded border border-gray-300/50"
                style={{ background: t.swatch }}
                aria-hidden
              />
              <div>
                <div className="font-bold text-sm text-gray-900">{t.label}</div>
                <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                  {t.desc}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Monochrome — pick dominant side */}
        {md.theme === "monochrome" && (
          <div className="rounded-lg border border-orange-200 bg-orange-50/40 p-3 space-y-2 mt-2">
            <Label className="text-orange-900 text-xs">Dominant side</Label>
            <div className="grid grid-cols-2 gap-2">
              {(["home", "away"] as const).map((side) => {
                const isActive = (md.monoSide ?? "home") === side;
                const nation = side === "home" ? md.home : md.away;
                return (
                  <button
                    key={side}
                    type="button"
                    onClick={() => updateMatch({ monoSide: side })}
                    className={cn(
                      "p-2 rounded border text-left transition flex items-center gap-2",
                      isActive
                        ? "border-orange-600 bg-white shadow"
                        : "border-gray-200 bg-white hover:border-gray-300",
                    )}
                  >
                    <span
                      className="w-5 h-5 rounded-full border border-gray-300 shrink-0"
                      style={{
                        background:
                          side === "home" ? homeColorValue : awayColorValue,
                      }}
                    />
                    <span className="text-xs font-bold text-gray-900 truncate">
                      {nation.name || (side === "home" ? "Home" : "Away")}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-orange-800">
              The chosen nation's primary color fills the background; their name dominates the headline.
            </p>
          </div>
        )}
      </div>

      {/* EYEBROW */}
      <div>
        <Label htmlFor="md-eyebrow">Eyebrow tag</Label>
        <div className="flex gap-2 mt-1">
          <Input
            id="md-eyebrow"
            value={md.eyebrow}
            onChange={(e) => updateMatch({ eyebrow: e.target.value.toUpperCase() })}
            className="font-mono uppercase"
          />
          <Select value="" onValueChange={(val) => updateMatch({ eyebrow: val })}>
            <SelectTrigger className="w-36 shrink-0">
              <SelectValue placeholder="Presets" />
            </SelectTrigger>
            <SelectContent>
              {MATCHDAY_EYEBROW_PRESETS.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* HOME NATION */}
      <div className="space-y-2">
        <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
          Home nation / club
        </Label>
        <div className="grid grid-cols-3 gap-2">
          <Input
            className="col-span-2"
            placeholder="Name (e.g. Portugal, Chelsea)"
            value={md.home.name}
            onChange={(e) => updateHomeNation({ name: e.target.value })}
          />
          <Input
            placeholder="ISO"
            maxLength={3}
            className="font-mono uppercase text-center"
            value={md.home.code}
            onChange={(e) =>
              updateHomeNation({ code: e.target.value.toLowerCase() })
            }
          />
        </div>
        <div className="relative">
          <Input
            placeholder="Flag/crest URL (auto-embeds on blur if CORS allows)"
            value={
              md.home.flagUrl && !md.home.flagUrl.startsWith("data:")
                ? md.home.flagUrl
                : ""
            }
            onChange={(e) =>
              updateHomeNation({ flagUrl: e.target.value || undefined })
            }
            onBlur={(e) => tryAutoEmbed("home", e.target.value)}
            disabled={embedding === "home"}
          />
          {embedding === "home" && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-500 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Embedding…
            </span>
          )}
        </div>
        <div>
          <p className="text-[11px] text-gray-500 mb-1">
            Or upload PNG (always works, regardless of source CORS):
          </p>
          <ImageUploadField
            value={
              md.home.flagUrl && md.home.flagUrl.startsWith("data:")
                ? md.home.flagUrl
                : undefined
            }
            onChange={(next) => updateHomeNation({ flagUrl: next || undefined })}
            allowUrl={false}
          />
        </div>
      </div>

      {/* AWAY NATION */}
      <div className="space-y-2">
        <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
          Away nation / club
        </Label>
        <div className="grid grid-cols-3 gap-2">
          <Input
            className="col-span-2"
            placeholder="Name (e.g. Germany, Man City)"
            value={md.away.name}
            onChange={(e) => updateAwayNation({ name: e.target.value })}
          />
          <Input
            placeholder="ISO"
            maxLength={3}
            className="font-mono uppercase text-center"
            value={md.away.code}
            onChange={(e) =>
              updateAwayNation({ code: e.target.value.toLowerCase() })
            }
          />
        </div>
        <div className="relative">
          <Input
            placeholder="Flag/crest URL (auto-embeds on blur if CORS allows)"
            value={
              md.away.flagUrl && !md.away.flagUrl.startsWith("data:")
                ? md.away.flagUrl
                : ""
            }
            onChange={(e) =>
              updateAwayNation({ flagUrl: e.target.value || undefined })
            }
            onBlur={(e) => tryAutoEmbed("away", e.target.value)}
            disabled={embedding === "away"}
          />
          {embedding === "away" && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-500 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Embedding…
            </span>
          )}
        </div>
        <div>
          <p className="text-[11px] text-gray-500 mb-1">
            Or upload PNG (always works, regardless of source CORS):
          </p>
          <ImageUploadField
            value={
              md.away.flagUrl && md.away.flagUrl.startsWith("data:")
                ? md.away.flagUrl
                : undefined
            }
            onChange={(next) => updateAwayNation({ flagUrl: next || undefined })}
            allowUrl={false}
          />
        </div>
      </div>

      {/* COLOR pickers — visible for split + monochrome themes */}
      {(isSplit || md.theme === "monochrome") && (
        <div className="rounded-lg border border-orange-200 bg-orange-50/40 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-orange-900">
              {md.theme === "monochrome" ? "Monochrome colors" : "Split theme colors"}
            </Label>
            <button
              type="button"
              onClick={() => updateMatch({ homeColor: undefined, awayColor: undefined })}
              className="text-[11px] text-orange-700 hover:text-orange-900"
            >
              Reset to nation defaults
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-600">Home color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={homeColorValue}
                  onChange={(e) => updateMatch({ homeColor: e.target.value })}
                  className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                />
                <Input
                  value={homeColorValue}
                  onChange={(e) => updateMatch({ homeColor: e.target.value })}
                  className="h-8 text-xs font-mono"
                />
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                Auto: <code>{homeAutoColor}</code> ({md.home.code || "?"})
              </p>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Away color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={awayColorValue}
                  onChange={(e) => updateMatch({ awayColor: e.target.value })}
                  className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                />
                <Input
                  value={awayColorValue}
                  onChange={(e) => updateMatch({ awayColor: e.target.value })}
                  className="h-8 text-xs font-mono"
                />
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                Auto: <code>{awayAutoColor}</code> ({md.away.code || "?"})
              </p>
            </div>
          </div>
        </div>
      )}

      {/* DATE + KICKOFF + VENUE */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="md-date">Date label</Label>
          <Input
            id="md-date"
            value={md.dateLabel}
            onChange={(e) => updateMatch({ dateLabel: e.target.value })}
            placeholder="15 JUN · SUN"
            className="mt-1 font-mono text-xs"
          />
        </div>
        <div>
          <Label htmlFor="md-kickoff">Kickoff label</Label>
          <Input
            id="md-kickoff"
            value={md.kickoffLabel}
            onChange={(e) => updateMatch({ kickoffLabel: e.target.value })}
            placeholder="21:00 LOCAL"
            className="mt-1 font-mono text-xs"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="md-venue">Venue</Label>
          <Input
            id="md-venue"
            value={md.venue}
            onChange={(e) => updateMatch({ venue: e.target.value })}
            placeholder="Allianz Arena"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="md-city">City — optional</Label>
          <Input
            id="md-city"
            value={md.city || ""}
            onChange={(e) => updateMatch({ city: e.target.value || undefined })}
            placeholder="Munich"
            className="mt-1"
          />
        </div>
      </div>

      {/* TAGLINE */}
      <div>
        <Label htmlFor="md-tagline">Tagline / pull-quote — optional</Label>
        <Input
          id="md-tagline"
          value={md.tagline || ""}
          onChange={(e) => updateMatch({ tagline: e.target.value || undefined })}
          placeholder="First meeting since the 2014 group stage."
          className="mt-1"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Renders in italic gold with bar accents either side. Keep it one breath.
          <br />
          <b>Rivalry theme:</b> this becomes the HUGE centre headline ("DEJA VU?").
        </p>
      </div>

      {/* NARRATIVE TAG — only the new fight-poster themes consume this */}
      {(md.theme === "rivalry" || md.theme === "duel" || md.theme === "marquee") && (
        <div>
          <Label htmlFor="md-narrative">Narrative tag — fight-poster themes</Label>
          <Input
            id="md-narrative"
            value={md.narrativeTag || ""}
            onChange={(e) =>
              updateMatch({ narrativeTag: e.target.value || undefined })
            }
            placeholder="2002: SENEGAL STUNNED FRANCE"
            className="mt-1 font-mono uppercase tracking-widest"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Short historical context. Renders as a gold-bordered pill near the
            bottom of the rivalry / duel / marquee posters. Ignored by the
            other 9 themes.
          </p>
        </div>
      )}

      {/* FEATURED PLAYERS */}
      <div className="space-y-2">
        <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
          Featured players — optional
        </Label>
        <p className="text-[11px] text-gray-500">
          One per side. Leave blank to hide the players band entirely.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2 p-3 rounded-lg border border-gray-200 bg-gray-50/50">
            <div className="text-[11px] font-bold text-gray-700 uppercase">
              Home · {md.home.name}
            </div>
            <Input
              placeholder="Player name"
              value={md.featuredHome?.name || ""}
              onChange={(e) => {
                const name = e.target.value;
                if (!name) {
                  updateMatch({ featuredHome: undefined });
                } else {
                  updateMatch({
                    featuredHome: { ...(md.featuredHome ?? {}), name },
                  });
                }
              }}
              className="h-8 text-xs"
            />
            <ImageUploadField
              value={md.featuredHome?.photoUrl || undefined}
              onChange={(next) =>
                updateMatch({
                  featuredHome: next
                    ? { name: md.featuredHome?.name ?? "", photoUrl: next }
                    : md.featuredHome?.name
                      ? { name: md.featuredHome.name }
                      : undefined,
                })
              }
              allowUrl={false}
            />
          </div>
          <div className="space-y-2 p-3 rounded-lg border border-gray-200 bg-gray-50/50">
            <div className="text-[11px] font-bold text-gray-700 uppercase">
              Away · {md.away.name}
            </div>
            <Input
              placeholder="Player name"
              value={md.featuredAway?.name || ""}
              onChange={(e) => {
                const name = e.target.value;
                if (!name) {
                  updateMatch({ featuredAway: undefined });
                } else {
                  updateMatch({
                    featuredAway: { ...(md.featuredAway ?? {}), name },
                  });
                }
              }}
              className="h-8 text-xs"
            />
            <ImageUploadField
              value={md.featuredAway?.photoUrl || undefined}
              onChange={(next) =>
                updateMatch({
                  featuredAway: next
                    ? { name: md.featuredAway?.name ?? "", photoUrl: next }
                    : md.featuredAway?.name
                      ? { name: md.featuredAway.name }
                      : undefined,
                })
              }
              allowUrl={false}
            />
          </div>
        </div>
      </div>

      {/* CTA */}
      <div>
        <Label htmlFor="md-cta">CTA text — optional</Label>
        <Input
          id="md-cta"
          value={md.ctaText || ""}
          onChange={(e) => updateMatch({ ctaText: e.target.value || undefined })}
          placeholder="Pick your team"
          className="mt-1"
        />
      </div>

      <FooterFields
        idPrefix="md"
        footerLeft={md.footerLeft}
        hashtags={md.hashtags}
        defaultFooter={defaultFooterLeft("MATCHDAY")}
        onFooterLeftChange={(v) => updateMatch({ footerLeft: v })}
        onHashtagsChange={(v) => updateMatch({ hashtags: v })}
        footer={md.footer}
        onFooterChange={(v) => updateMatch({ footer: v })}
      />

      <HeaderFields
        idPrefix="md"
        value={md.header}
        onChange={(v) => updateMatch({ header: v })}
      />
    </div>
  );
}

// ─── Team of the Week form (gameweek + formation → auto-load from API) ───────

function TeamOfWeekForm({
  content,
  onChange,
  gameweekId,
  onGameweekIdChange,
}: {
  content: TeamOfWeekCardContent;
  onChange: (next: TeamOfWeekCardContent) => void;
  gameweekId: number | null;
  onGameweekIdChange: (next: number | null) => void;
}) {
  const { data: gameweeksData, isFetching: gwsFetching } = useGameweeks();
  const formation = (TOTW_FORMATIONS.includes(content.formation as TotwFormation)
    ? (content.formation as TotwFormation)
    : "4-4-2") as TotwFormation;
  const { data: totwData, isFetching: totwFetching } = useGameweekTotw(
    gameweekId,
    formation,
  );

  const [embeddingImages, setEmbeddingImages] = useState(false);

  /**
   * Map server TOTW response → 11 player tiles in render order (FWD → MID → DEF → GK).
   *
   * Every remote image (player photo, club crest, nation flag) is fetched and
   * converted to a data URL BEFORE landing in component state. html-to-image
   * cannot rasterize an image whose response is missing CORS headers — the
   * canvas gets tainted and export fails with a bare `[object Event]` rejection.
   * Embedding up-front sidesteps the problem entirely.
   */
  useEffect(() => {
    if (!totwData) return;
    let cancelled = false;

    const order: Record<TeamOfWeekPlayerTile["position"], number> = {
      FWD: 0,
      MID: 1,
      DEF: 2,
      GK: 3,
    };
    const sorted = [...totwData.players].sort((a, b) => {
      const ap = order[a.player.position] ?? 9;
      const bp = order[b.player.position] ?? 9;
      if (ap !== bp) return ap - bp;
      return a.slot.index - b.slot.index;
    });

    /**
     * Try to inline as a data URL. Returns null when the URL is empty,
     * the fetch errors, or the response isn't actually an image (e.g.
     * a player's photo field accidentally holds a page URL — the server
     * returns HTML, not a PNG). Dropping to null lets the card render
     * its initials fallback instead of carrying a broken URL into the
     * export, which would otherwise crash html-to-image on capture.
     */
    const embedOrFallback = async (
      url: string | null | undefined,
    ): Promise<string | null> => {
      if (!url) return null;
      if (url.startsWith("data:")) return url;
      const r = await autoEmbedImage(url);
      if (r.success) return r.dataUrl;
      console.warn("[TOTW form] dropping non-embeddable image:", url, r.reason);
      return null;
    };

    (async () => {
      setEmbeddingImages(true);
      try {
        const tiles = await Promise.all(
          sorted.map(async (s): Promise<TeamOfWeekPlayerTile> => {
            const [photo, clubLogo, nationFlag] = await Promise.all([
              embedOrFallback(s.player.photo),
              embedOrFallback(s.player.club?.logoUrl),
              embedOrFallback(s.player.nation?.flagUrl),
            ]);
            return {
              name: s.player.name,
              position: s.player.position,
              photoUrl: photo,
              clubName: s.player.club?.name,
              clubLogoUrl: clubLogo,
              nationFlagUrl: nationFlag,
              points: s.stats.rawPoints,
              isCaptain: s.isCaptain,
            };
          }),
        );
        if (cancelled) return;
        onChange({
          ...content,
          gameweekName: totwData.gameweek.name,
          formation: totwData.formation,
          totalPoints: totwData.totalPoints,
          players: tiles,
        });
      } finally {
        if (!cancelled) setEmbeddingImages(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totwData]);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-emerald-900">Gameweek</Label>
          {(gwsFetching || totwFetching || embeddingImages) && (
            <span className="text-[11px] text-emerald-700 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              {embeddingImages
                ? "Embedding images"
                : totwFetching
                  ? "Loading XI"
                  : "Loading gameweeks"}
            </span>
          )}
        </div>
        <Select
          value={gameweekId == null ? "" : String(gameweekId)}
          onValueChange={(val) => onGameweekIdChange(val ? Number(val) : null)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Pick a gameweek to auto-fill" />
          </SelectTrigger>
          <SelectContent>
            {gameweeksData?.map((gw) => (
              <SelectItem key={gw.id} value={String(gw.id)}>
                {gw.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-emerald-800 leading-relaxed">
          <Sparkles className="h-3 w-3 inline mr-1" />
          The best XI for the picked gameweek auto-loads from the live points.
          Change formation below to re-shape the same XI.
        </p>
      </div>

      <div>
        <Label htmlFor="totw-formation">Formation</Label>
        <Select
          value={formation}
          onValueChange={(val) =>
            onChange({ ...content, formation: val as TotwFormation })
          }
        >
          <SelectTrigger id="totw-formation" className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TOTW_FORMATIONS.map((f) => (
              <SelectItem key={f} value={f}>
                {f}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="totw-eyebrow">Eyebrow tag</Label>
        <Input
          id="totw-eyebrow"
          value={content.eyebrow ?? "TEAM OF THE WEEK"}
          onChange={(e) => onChange({ ...content, eyebrow: e.target.value })}
          className="mt-1 uppercase tracking-widest"
          placeholder="TEAM OF THE WEEK"
        />
      </div>

      <div>
        <Label htmlFor="totw-title">Title</Label>
        <Input
          id="totw-title"
          value={content.gameweekName}
          onChange={(e) =>
            onChange({ ...content, gameweekName: e.target.value })
          }
          className="mt-1"
          placeholder="Matchweek 03"
        />
      </div>

      <div>
        <Label htmlFor="totw-tagline">Tagline (optional)</Label>
        <Textarea
          id="totw-tagline"
          value={content.tagline ?? ""}
          onChange={(e) => onChange({ ...content, tagline: e.target.value })}
          className="mt-1"
          rows={2}
          placeholder="The XI that lit up the gameweek."
        />
      </div>

      {/* Per-player overrides */}
      <details className="rounded-lg border border-gray-200 bg-gray-50/40 group">
        <summary className="px-3 py-2.5 cursor-pointer flex items-center justify-between text-xs font-bold text-gray-700 uppercase tracking-wider list-none">
          <span>Player tiles ({content.players.length})</span>
          <span className="text-[10px] font-normal text-gray-500 normal-case tracking-normal group-open:hidden">
            Override names, points, captain · click to edit
          </span>
        </summary>
        <div className="border-t border-gray-200 p-3 space-y-2 max-h-96 overflow-y-auto">
          {content.players.map((p, i) => (
            <div
              key={i}
              className="grid grid-cols-12 gap-2 items-center bg-white border border-gray-200 rounded p-2"
            >
              <div className="col-span-1 text-[10px] font-bold text-gray-500 uppercase">
                {p.position}
              </div>
              <Input
                className="col-span-5 h-8 text-xs"
                value={p.name}
                onChange={(e) => {
                  const copy = [...content.players];
                  copy[i] = { ...p, name: e.target.value };
                  onChange({ ...content, players: copy });
                }}
                placeholder="Player name"
              />
              <Input
                className="col-span-3 h-8 text-xs font-mono"
                type="number"
                value={p.points}
                onChange={(e) => {
                  const copy = [...content.players];
                  copy[i] = { ...p, points: Number(e.target.value) || 0 };
                  onChange({ ...content, players: copy });
                }}
                placeholder="Pts"
              />
              <label className="col-span-3 flex items-center gap-1.5 text-[11px] font-semibold text-gray-700">
                <input
                  type="checkbox"
                  className="rounded accent-emerald-600"
                  checked={!!p.isCaptain}
                  onChange={(e) => {
                    // Single captain: untick others when one is set
                    const copy = content.players.map((q, idx) => ({
                      ...q,
                      isCaptain: e.target.checked ? idx === i : false,
                    }));
                    onChange({ ...content, players: copy });
                  }}
                />
                Captain
              </label>
            </div>
          ))}
        </div>
      </details>

      <HeaderFields
        idPrefix="totw"
        value={content.header}
        onChange={(v) => onChange({ ...content, header: v })}
      />

      <FooterFields
        idPrefix="totw"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("TEAM_OF_WEEK")}
        onFooterLeftChange={(v) => onChange({ ...content, footerLeft: v })}
        onHashtagsChange={(v) => onChange({ ...content, hashtags: v })}
        footer={content.footer}
        onFooterChange={(v) => onChange({ ...content, footer: v })}
      />
    </div>
  );
}

// ─── Partner Pitch form (B2B outreach card, multi-page deck) ─────────────────

function PartnerPitchForm({
  content,
  onChange,
  activePageIndex,
  onActivePageChange,
}: {
  content: PartnerPitchCardContent;
  onChange: (next: PartnerPitchCardContent) => void;
  activePageIndex: number;
  onActivePageChange: (next: number) => void;
}) {
  const update = <K extends keyof PartnerPitchCardContent>(
    key: K,
    value: PartnerPitchCardContent[K],
  ) => onChange({ ...content, [key]: value });

  const updateStep = (i: number, text: string) => {
    const copy = [...content.steps];
    copy[i] = { text };
    update("steps", copy);
  };
  const addStep = () => {
    if (content.steps.length >= 3) return;
    update("steps", [...content.steps, { text: "" }]);
  };
  const removeStep = (i: number) =>
    update("steps", content.steps.filter((_, j) => j !== i));

  const updateBenefit = (i: number, text: string) => {
    const copy = [...content.benefits];
    copy[i] = { text };
    update("benefits", copy);
  };
  const addBenefit = () =>
    update("benefits", [...content.benefits, { text: "" }]);
  const removeBenefit = (i: number) =>
    update("benefits", content.benefits.filter((_, j) => j !== i));

  // ─── Chapter (extra pages) helpers ────────────────────────────────────────
  const extras = content.extraPages ?? [];
  const totalPages = 1 + extras.length;
  const safeIndex = Math.min(Math.max(activePageIndex, 0), totalPages - 1);
  const isCover = safeIndex === 0;
  const chapterIdx = safeIndex - 1;
  const chapter = isCover ? undefined : extras[chapterIdx];

  const updateChapter = (patch: Partial<PitchExtraPage>) => {
    if (isCover) return;
    const copy = [...extras];
    copy[chapterIdx] = { ...copy[chapterIdx], ...patch };
    update("extraPages", copy);
  };

  const addChapter = () => {
    const nextNum = extras.length + 1;
    const newPage: PitchExtraPage = {
      eyebrow: `CHAPTER ${String(nextNum).padStart(2, "0")}`,
      headline: "",
      body: "",
    };
    update("extraPages", [...extras, newPage]);
    onActivePageChange(totalPages); // jump to new slide
  };

  const duplicateChapter = () => {
    if (isCover || !chapter) return;
    const copy = [...extras];
    copy.splice(chapterIdx + 1, 0, { ...chapter });
    update("extraPages", copy);
    onActivePageChange(safeIndex + 1);
  };

  const deleteChapter = () => {
    if (isCover) return;
    const copy = extras.filter((_, j) => j !== chapterIdx);
    update("extraPages", copy);
    onActivePageChange(Math.max(0, safeIndex - 1));
  };

  const moveChapter = (direction: -1 | 1) => {
    if (isCover) return;
    const j = chapterIdx + direction;
    if (j < 0 || j >= extras.length) return;
    const copy = [...extras];
    [copy[chapterIdx], copy[j]] = [copy[j], copy[chapterIdx]];
    update("extraPages", copy);
    onActivePageChange(j + 1);
  };

  return (
    <div className="space-y-4">
      {/* ─── Slide tabs (cover + chapters) ────────────────────────────── */}
      <div className="rounded-lg border border-cyan-200 bg-cyan-50/40 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-cyan-900">
            Slides · {totalPages}
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addChapter}
            className="gap-1 h-7 text-xs"
          >
            <Plus className="h-3 w-3" />
            Add chapter
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => onActivePageChange(0)}
            className={cn(
              "px-2.5 py-1.5 rounded-md text-xs font-bold transition flex items-center gap-1.5 min-w-0",
              safeIndex === 0
                ? "bg-cyan-600 text-white shadow"
                : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50",
            )}
          >
            <span className="text-[10px] opacity-60">01</span>
            <span>Cover</span>
          </button>
          {extras.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onActivePageChange(i + 1)}
              className={cn(
                "px-2.5 py-1.5 rounded-md text-xs font-bold transition flex items-center gap-1.5 min-w-0",
                safeIndex === i + 1
                  ? "bg-cyan-600 text-white shadow"
                  : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50",
              )}
              title={p.headline || `Chapter ${i + 1}`}
            >
              <span className="text-[10px] opacity-60">
                {String(i + 2).padStart(2, "0")}
              </span>
              <span className="truncate max-w-[120px]">
                {p.headline || `Chapter ${i + 1}`}
              </span>
            </button>
          ))}
        </div>

        {!isCover && (
          <div className="flex items-center gap-1.5 pt-1.5 border-t border-cyan-200">
            <button
              type="button"
              onClick={() => moveChapter(-1)}
              disabled={chapterIdx === 0}
              className="text-[11px] text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed px-2 py-1 rounded hover:bg-white"
            >
              ← Move
            </button>
            <button
              type="button"
              onClick={() => moveChapter(1)}
              disabled={chapterIdx === extras.length - 1}
              className="text-[11px] text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed px-2 py-1 rounded hover:bg-white"
            >
              Move →
            </button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={duplicateChapter}
              className="text-[11px] text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-white flex items-center gap-1"
            >
              <Copy className="h-3 w-3" />
              Duplicate
            </button>
            <button
              type="button"
              onClick={deleteChapter}
              className="text-[11px] text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-white flex items-center gap-1"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </button>
          </div>
        )}
      </div>

      <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        {isCover ? "Cover slide" : `Chapter ${chapterIdx + 1} content`}
      </div>

      {/* ─── CHAPTER FIELDS (only when a chapter is selected) ─────────── */}
      {!isCover && chapter && (
        <div className="space-y-4">
          <div>
            <Label htmlFor="pp-c-eyebrow">Eyebrow tag</Label>
            <Input
              id="pp-c-eyebrow"
              value={chapter.eyebrow}
              onChange={(e) => updateChapter({ eyebrow: e.target.value.toUpperCase() })}
              className="font-mono uppercase mt-1"
              placeholder="HOW FBK WORKS · 01"
            />
          </div>
          <div>
            <Label htmlFor="pp-c-headline">Headline</Label>
            <Textarea
              id="pp-c-headline"
              rows={2}
              value={chapter.headline}
              onChange={(e) => updateChapter({ headline: e.target.value })}
              className="mt-1"
              placeholder="How a user"
            />
          </div>
          <div>
            <Label htmlFor="pp-c-gold">Gold closing line — optional</Label>
            <Input
              id="pp-c-gold"
              value={chapter.goldHighlight || ""}
              onChange={(e) => updateChapter({ goldHighlight: e.target.value })}
              className="mt-1"
              placeholder="plays."
            />
          </div>
          <div>
            <Label htmlFor="pp-c-body">Body — line breaks render as breaks</Label>
            <Textarea
              id="pp-c-body"
              rows={8}
              value={chapter.body}
              onChange={(e) => updateChapter({ body: e.target.value })}
              className="mt-1 font-mono text-xs"
              placeholder={"→ Sign up free in 30 seconds\n→ Pick 11 players\n→ Choose a captain"}
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Use <code>→</code> or <code>·</code> as bullet prefixes — the renderer keeps them verbatim.
            </p>
          </div>
          <div>
            <Label>Side image — optional</Label>
            <p className="text-[11px] text-gray-500 mt-0.5 mb-1.5">
              Phone screenshot, illustration, or product photo. Floats with shadow.
            </p>
            <ImageUploadField
              value={chapter.imageUrl || undefined}
              onChange={(next) => updateChapter({ imageUrl: next || "" })}
            />
          </div>
          <div>
            <Label>Chapter background image — optional</Label>
            <p className="text-[11px] text-gray-500 mt-0.5 mb-1.5">
              Overrides the card-level Background tab image for this chapter only.
            </p>
            <ImageUploadField
              value={chapter.backgroundImageUrl || undefined}
              onChange={(next) => updateChapter({ backgroundImageUrl: next || "" })}
            />
          </div>
          <div>
            <Label htmlFor="pp-c-cta">CTA pill — optional (usually only on final chapter)</Label>
            <Input
              id="pp-c-cta"
              value={chapter.ctaText || ""}
              onChange={(e) => updateChapter({ ctaText: e.target.value })}
              className="mt-1"
              placeholder="Reply to this DM"
            />
          </div>

          <p className="text-[11px] text-gray-500 leading-relaxed bg-cyan-50/50 border border-cyan-200 rounded p-2.5">
            <b>Tip:</b> Cover-level partner name, logo, colour, theme, header, and
            footer apply to every chapter. Only the slide-specific text + images
            change per chapter.
          </p>
        </div>
      )}

      {/* ─── COVER FIELDS (existing partner pitch form) ───────────────── */}
      {isCover && (
        <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="pp-partner-name">Partner name</Label>
          <Input
            id="pp-partner-name"
            value={content.partnerName}
            onChange={(e) => update("partnerName", e.target.value)}
            placeholder="Blue Café"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="pp-partner-color">Partner brand color</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              id="pp-partner-color"
              type="color"
              value={content.partnerColor || "#1E40AF"}
              onChange={(e) => update("partnerColor", e.target.value)}
              className="h-10 w-14 p-1 cursor-pointer"
            />
            <Input
              value={content.partnerColor || "#1E40AF"}
              onChange={(e) => update("partnerColor", e.target.value)}
              className="font-mono"
              placeholder="#1E40AF"
            />
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            Drives the right half of the <b>Collab split</b> theme.
          </p>
        </div>
      </div>

      <div>
        <Label>Partner logo</Label>
        <p className="text-[11px] text-gray-500 mt-0.5 mb-1.5">
          Transparent PNG works best. Sits next to the FBK mark in the co-brand bar.
        </p>
        <ImageUploadField
          value={content.partnerLogoUrl || undefined}
          onChange={(next) => update("partnerLogoUrl", next || "")}
        />
      </div>

      <div>
        <Label htmlFor="pp-eyebrow">Eyebrow tag</Label>
        <Input
          id="pp-eyebrow"
          value={content.eyebrow}
          onChange={(e) => update("eyebrow", e.target.value.toUpperCase())}
          className="font-mono uppercase mt-1"
          placeholder="FBK FOR CAFÉS"
        />
      </div>

      <div>
        <Label htmlFor="pp-headline">Headline</Label>
        <Textarea
          id="pp-headline"
          rows={2}
          value={content.headline}
          onChange={(e) => update("headline", e.target.value)}
          className="mt-1"
          placeholder="Run a Fantasy League"
        />
      </div>

      <div>
        <Label htmlFor="pp-gold">Closing line (gold) — optional</Label>
        <Input
          id="pp-gold"
          value={content.goldHighlight || ""}
          onChange={(e) => update("goldHighlight", e.target.value)}
          className="mt-1"
          placeholder="for your regulars."
        />
      </div>

      <div className="rounded-lg border border-cyan-200 bg-cyan-50/40 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <Label>How it works · {content.steps.length}/3 steps</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addStep}
            disabled={content.steps.length >= 3}
            className="gap-1 h-7 text-xs"
          >
            <Plus className="h-3 w-3" />
            Add step
          </Button>
        </div>
        {content.steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="text-cyan-700 font-bold text-sm w-6 text-center">
              {["①", "②", "③"][i]}
            </div>
            <Input
              value={s.text}
              onChange={(e) => updateStep(i, e.target.value)}
              placeholder="We set it up in 10 min."
            />
            <button
              type="button"
              onClick={() => removeStep(i)}
              className="text-red-500 hover:text-red-700 p-1"
              title="Remove step"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <Label>Why it works · benefits</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addBenefit}
            className="gap-1 h-7 text-xs"
          >
            <Plus className="h-3 w-3" />
            Add benefit
          </Button>
        </div>
        {content.benefits.map((b, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="text-emerald-700 font-bold text-sm w-6 text-center">
              ✓
            </div>
            <Input
              value={b.text}
              onChange={(e) => updateBenefit(i, e.target.value)}
              placeholder="Zero setup cost"
            />
            <button
              type="button"
              onClick={() => removeBenefit(i)}
              className="text-red-500 hover:text-red-700 p-1"
              title="Remove benefit"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <div>
        <Label htmlFor="pp-cta">CTA pill text — optional</Label>
        <Input
          id="pp-cta"
          value={content.ctaText || ""}
          onChange={(e) => update("ctaText", e.target.value)}
          className="mt-1"
          placeholder="Start your league"
        />
      </div>

      <ThemePicker
        cardType="PARTNER_PITCH"
        theme={content.theme}
        monoColor={content.monoColor}
        onThemeChange={(v) => update("theme", v)}
        onMonoColorChange={(v) => update("monoColor", v)}
      />

      <FooterFields
        idPrefix="pp"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("PARTNER_PITCH")}
        onFooterLeftChange={(v) => update("footerLeft", v)}
        onHashtagsChange={(v) => update("hashtags", v)}
        footer={content.footer}
        onFooterChange={(v) => update("footer", v)}
      />

      <HeaderFields
        idPrefix="pp"
        value={content.header}
        onChange={(v) => update("header", v)}
      />
        </div>
      )}
    </div>
  );
}

// ─── League Launch form (customer-facing co-brand) ───────────────────────────

function LeagueLaunchForm({
  content,
  onChange,
}: {
  content: LeagueLaunchCardContent;
  onChange: (next: LeagueLaunchCardContent) => void;
}) {
  const update = <K extends keyof LeagueLaunchCardContent>(
    key: K,
    value: LeagueLaunchCardContent[K],
  ) => onChange({ ...content, [key]: value });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="ll-partner-name">Partner name</Label>
          <Input
            id="ll-partner-name"
            value={content.partnerName}
            onChange={(e) => update("partnerName", e.target.value)}
            placeholder="Blue Café"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="ll-partner-color">Partner brand color</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              id="ll-partner-color"
              type="color"
              value={content.partnerColor || "#1E40AF"}
              onChange={(e) => update("partnerColor", e.target.value)}
              className="h-10 w-14 p-1 cursor-pointer"
            />
            <Input
              value={content.partnerColor || "#1E40AF"}
              onChange={(e) => update("partnerColor", e.target.value)}
              className="font-mono"
              placeholder="#1E40AF"
            />
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            Drives the right half of the <b>Collab split</b> theme.
          </p>
        </div>
      </div>

      <div>
        <Label>Partner logo (hero)</Label>
        <p className="text-[11px] text-gray-500 mt-0.5 mb-1.5">
          Transparent PNG. Renders LARGE at the top — this is the visual hero.
        </p>
        <ImageUploadField
          value={content.partnerLogoUrl || undefined}
          onChange={(next) => update("partnerLogoUrl", next || "")}
        />
      </div>

      <div>
        <Label htmlFor="ll-eyebrow">Eyebrow (partner · matchweek)</Label>
        <Input
          id="ll-eyebrow"
          value={content.eyebrow}
          onChange={(e) => update("eyebrow", e.target.value.toUpperCase())}
          className="font-mono uppercase mt-1"
          placeholder="BLUE CAFÉ · MATCHWEEK 04"
        />
      </div>

      <div>
        <Label htmlFor="ll-headline">Headline</Label>
        <Textarea
          id="ll-headline"
          rows={3}
          value={content.headline}
          onChange={(e) => update("headline", e.target.value)}
          className="mt-1"
          placeholder="PLAY.&#10;WIN."
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Use line-breaks to stack words. Put the closer in the gold field below.
        </p>
      </div>

      <div>
        <Label htmlFor="ll-gold">Closing line in gold — optional</Label>
        <Input
          id="ll-gold"
          value={content.goldHighlight || ""}
          onChange={(e) => update("goldHighlight", e.target.value)}
          placeholder="SIP."
          className="mt-1"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Renders on its own line under the headline, in the partner-accent gold.
        </p>
      </div>

      <div>
        <Label htmlFor="ll-tagline">Tagline — optional</Label>
        <Input
          id="ll-tagline"
          value={content.tagline || ""}
          onChange={(e) => update("tagline", e.target.value)}
          className="mt-1"
          placeholder="The Blue Café Fantasy League is live."
        />
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-3">
        <Label className="text-amber-900">🏆 Prize block</Label>
        <div>
          <Label htmlFor="ll-prize-title" className="text-xs">
            Prize title
          </Label>
          <Input
            id="ll-prize-title"
            value={content.prizeTitle}
            onChange={(e) => update("prizeTitle", e.target.value.toUpperCase())}
            placeholder="TOP 3 WIN"
            className="font-mono uppercase mt-1"
          />
        </div>
        <div>
          <Label htmlFor="ll-prize-text" className="text-xs">
            Prize text (line-breaks ok)
          </Label>
          <Textarea
            id="ll-prize-text"
            rows={3}
            value={content.prizeText}
            onChange={(e) => update("prizeText", e.target.value)}
            placeholder="₹500 café voucher&#10;+ free coffee for a month"
            className="mt-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="ll-join-code">Join code</Label>
          <Input
            id="ll-join-code"
            value={content.joinCode || ""}
            onChange={(e) => update("joinCode", e.target.value.toUpperCase())}
            placeholder="BLUE04"
            className="font-mono uppercase mt-1"
          />
        </div>
      </div>

      <div>
        <Label>QR code image — optional</Label>
        <p className="text-[11px] text-gray-500 mt-0.5 mb-1.5">
          Generate the QR externally (qrcode-monkey.com etc.) and upload here.
        </p>
        <ImageUploadField
          value={content.qrImageUrl || undefined}
          onChange={(next) => update("qrImageUrl", next || "")}
        />
      </div>

      <div>
        <Label htmlFor="ll-small-print">Small print — optional</Label>
        <Input
          id="ll-small-print"
          value={content.smallPrint || ""}
          onChange={(e) => update("smallPrint", e.target.value)}
          className="mt-1"
          placeholder="Free to join · Open only to Blue Café customers"
        />
      </div>

      <ThemePicker
        cardType="LEAGUE_LAUNCH"
        theme={content.theme}
        monoColor={content.monoColor}
        onThemeChange={(v) => update("theme", v)}
        onMonoColorChange={(v) => update("monoColor", v)}
      />

      <FooterFields
        idPrefix="ll"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("LEAGUE_LAUNCH")}
        onFooterLeftChange={(v) => update("footerLeft", v)}
        onHashtagsChange={(v) => update("hashtags", v)}
        footer={content.footer}
        onFooterChange={(v) => update("footer", v)}
      />

      <HeaderFields
        idPrefix="ll"
        value={content.header}
        onChange={(v) => update("header", v)}
      />
    </div>
  );
}

// ─── FBK League form (FBK's own league hype card) ────────────────────────────

function FbkLeagueForm({
  content,
  onChange,
}: {
  content: FbkLeagueCardContent;
  onChange: (next: FbkLeagueCardContent) => void;
}) {
  const update = <K extends keyof FbkLeagueCardContent>(
    key: K,
    value: FbkLeagueCardContent[K],
  ) => onChange({ ...content, [key]: value });

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="fl-eyebrow">Eyebrow chip</Label>
        <Input
          id="fl-eyebrow"
          value={content.eyebrow}
          onChange={(e) => update("eyebrow", e.target.value.toUpperCase())}
          className="font-mono uppercase mt-1"
          placeholder="FBK LEAGUE · MATCHWEEK 01"
        />
      </div>

      <div>
        <Label htmlFor="fl-headline">Headline — line-break for stacked chant</Label>
        <Textarea
          id="fl-headline"
          rows={3}
          value={content.headline}
          onChange={(e) => update("headline", e.target.value)}
          placeholder={"PLAY.\nWIN."}
          className="mt-1"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Each line stacks vertically. Aim for 1-3 short verbs.
        </p>
      </div>

      <div>
        <Label htmlFor="fl-gold">Closing line in gold — optional</Label>
        <Input
          id="fl-gold"
          value={content.goldHighlight || ""}
          onChange={(e) => update("goldHighlight", e.target.value)}
          placeholder="REPEAT."
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="fl-tagline">Tagline / subhead — optional</Label>
        <Input
          id="fl-tagline"
          value={content.tagline || ""}
          onChange={(e) => update("tagline", e.target.value)}
          placeholder="The official FBK League is open."
          className="mt-1"
        />
      </div>

      {/* ─── Prize block (mystery vs reveal) ───────────────────────────── */}
      <div className="rounded-lg border border-amber-300 bg-amber-50/50 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-amber-900">🏆 Prize block</Label>
          <label className="flex items-center gap-2 text-xs font-bold text-amber-900 cursor-pointer">
            <input
              type="checkbox"
              checked={!!content.mysteryMode}
              onChange={(e) => update("mysteryMode", e.target.checked)}
              className="h-4 w-4 accent-amber-600"
            />
            Mystery mode
          </label>
        </div>

        <div>
          <Label htmlFor="fl-prize-title" className="text-xs">
            Prize title
          </Label>
          <Input
            id="fl-prize-title"
            value={content.prizeTitle}
            onChange={(e) => update("prizeTitle", e.target.value.toUpperCase())}
            placeholder="🏆 TOP 3 WIN"
            className="font-mono uppercase mt-1"
          />
        </div>

        {content.mysteryMode ? (
          <>
            <div>
              <Label htmlFor="fl-mystery-when" className="text-xs">
                Revealed when?
              </Label>
              <Input
                id="fl-mystery-when"
                value={content.mysteryRevealAt || ""}
                onChange={(e) => update("mysteryRevealAt", e.target.value.toUpperCase())}
                placeholder="SUNDAY"
                className="font-mono uppercase mt-1"
              />
            </div>
            <div>
              <Label htmlFor="fl-mystery-teaser" className="text-xs">
                Teaser line — italic quote under the ??
              </Label>
              <Input
                id="fl-mystery-teaser"
                value={content.mysteryTeaser || ""}
                onChange={(e) => update("mysteryTeaser", e.target.value)}
                placeholder="Something legendary."
                className="mt-1"
              />
            </div>
            <p className="text-[11px] text-amber-900 bg-white/60 border border-amber-200 rounded p-2">
              <b>Mystery mode is ON.</b> The card will show "?? ?? ??" + the reveal
              date instead of the prize text below. Flip off to reveal.
            </p>
          </>
        ) : (
          <div>
            <Label htmlFor="fl-prize-text" className="text-xs">
              Prize text (multi-line OK)
            </Label>
            <Textarea
              id="fl-prize-text"
              rows={3}
              value={content.prizeText}
              onChange={(e) => update("prizeText", e.target.value)}
              placeholder={"₹5,000 cash pool\nsplit across the podium"}
              className="mt-1"
            />
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="fl-players">Player count strip — social proof</Label>
        <Input
          id="fl-players"
          value={content.playerCountLabel || ""}
          onChange={(e) => update("playerCountLabel", e.target.value)}
          placeholder="🔥 12,438 PLAYERS ALREADY IN"
          className="mt-1"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Update this number weekly as the league grows. FOMO is the point.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="fl-code">Join code</Label>
          <Input
            id="fl-code"
            value={content.joinCode}
            onChange={(e) => update("joinCode", e.target.value.toUpperCase())}
            placeholder="FBK01"
            className="font-mono uppercase mt-1"
          />
        </div>
        <div>
          <Label htmlFor="fl-cta">CTA pill</Label>
          <Input
            id="fl-cta"
            value={content.ctaText || ""}
            onChange={(e) => update("ctaText", e.target.value)}
            placeholder="Tap to play"
            className="mt-1"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="fl-small-print">Small print — optional</Label>
        <Input
          id="fl-small-print"
          value={content.smallPrint || ""}
          onChange={(e) => update("smallPrint", e.target.value)}
          placeholder="Free · Open to every FBK user"
          className="mt-1"
        />
      </div>

      <ThemePicker
        cardType="FBK_LEAGUE"
        theme={content.theme}
        monoColor={content.monoColor}
        onThemeChange={(v) => update("theme", v)}
        onMonoColorChange={(v) => update("monoColor", v)}
      />

      <FooterFields
        idPrefix="fl"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("FBK_LEAGUE")}
        onFooterLeftChange={(v) => update("footerLeft", v)}
        onHashtagsChange={(v) => update("hashtags", v)}
        footer={content.footer}
        onFooterChange={(v) => update("footer", v)}
      />

      <HeaderFields
        idPrefix="fl"
        value={content.header}
        onChange={(v) => update("header", v)}
      />
    </div>
  );
}

// ─── Theme picker (shared — curated subset per card type) ────────────────────

function ThemePicker({
  cardType,
  theme,
  monoColor,
  onThemeChange,
  onMonoColorChange,
}: {
  cardType: CardType;
  theme: CardTheme | undefined;
  monoColor: string | undefined;
  onThemeChange: (next: CardTheme | undefined) => void;
  onMonoColorChange: (next: string | undefined) => void;
}) {
  // Defensive: tolerate an unknown cardType (shouldn't happen, but the page-level
  // crash from earlier suggests something here might receive a non-array)
  const availableThemes = Array.isArray(THEMES_PER_CARD_TYPE[cardType])
    ? THEMES_PER_CARD_TYPE[cardType]
    : [];
  const current = theme ?? "signature";
  if (availableThemes.length <= 1) return null;
  const ctx = { monoColor: monoColor || "#0D8C3C" };

  // Monochrome / halftone themes accept a color override; show pickers if relevant
  const themeUsesMonoColor = current === "monochrome" || current === "halftone";

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-emerald-900">Visual theme</Label>
        {theme && (
          <button
            type="button"
            onClick={() => onThemeChange(undefined)}
            className="text-[11px] text-emerald-700 hover:text-emerald-900"
          >
            Reset to default
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {availableThemes.map((t) => {
          const meta = THEME_META[t];
          const swatch = themeSwatch(t, ctx);
          const active = current === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => onThemeChange(t)}
              className={cn(
                "p-2.5 rounded-lg border text-left transition flex flex-col gap-2",
                active
                  ? "border-emerald-600 bg-white ring-2 ring-emerald-600/15"
                  : "border-gray-200 bg-white hover:border-gray-300",
              )}
            >
              <div
                className="w-full h-8 rounded border border-gray-300/50"
                style={{ background: swatch }}
                aria-hidden
              />
              <div>
                <div className="font-bold text-xs text-gray-900">{meta.label}</div>
                <div className="text-[10px] text-gray-500 leading-snug">
                  {meta.desc}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {themeUsesMonoColor && (
        <div className="pt-2 border-t border-emerald-200">
          <Label className="text-xs text-emerald-900">
            {current === "monochrome" ? "Background color" : "Base color"}
          </Label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="color"
              value={monoColor || "#0D8C3C"}
              onChange={(e) => onMonoColorChange(e.target.value)}
              className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
            />
            <Input
              value={monoColor || "#0D8C3C"}
              onChange={(e) => onMonoColorChange(e.target.value)}
              className="h-8 text-xs font-mono"
            />
          </div>
          <div className="flex gap-1 mt-1.5 flex-wrap">
            {BRAND_COLORS.slice(0, 6).map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => onMonoColorChange(c.value)}
                className={cn(
                  "w-5 h-5 rounded-full border-2 transition",
                  (monoColor || "#0D8C3C").toLowerCase() === c.value.toLowerCase()
                    ? "border-emerald-600 scale-110"
                    : "border-gray-200",
                )}
                style={{ background: c.value }}
                title={c.label}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Header fields (shared by every form) ────────────────────────────────────

function HeaderFields({
  value,
  onChange,
  idPrefix,
}: {
  value: HeaderSettings | undefined;
  onChange: (next: HeaderSettings | undefined) => void;
  idPrefix: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const v = value ?? {};
  const hideHeader = v.hide === true;

  const update = (patch: Partial<HeaderSettings>) => {
    const next = { ...v, ...patch };
    Object.keys(next).forEach((k) => {
      const val = next[k as keyof HeaderSettings];
      if (val === undefined || val === "") delete next[k as keyof HeaderSettings];
    });
    onChange(Object.keys(next).length === 0 ? undefined : next);
  };

  const handleFile = (file: File | null) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert("Logo image must be under 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => update({ logoUrl: reader.result as string });
    reader.readAsDataURL(file);
  };

  return (
    <details className="rounded-lg border border-gray-200 bg-gray-50/40 group">
      <summary className="px-3 py-2.5 cursor-pointer flex items-center justify-between text-xs font-bold text-gray-700 uppercase tracking-wider list-none">
        <span>Header</span>
        <span className="text-[10px] font-normal text-gray-500 normal-case tracking-normal group-open:hidden">
          Logo, wordmark, pill — click to customize
        </span>
        <span className="text-[10px] font-normal text-gray-500 normal-case tracking-normal hidden group-open:inline">
          {hideHeader ? "hidden" : "click to collapse"}
        </span>
      </summary>
      <div className="border-t border-gray-200 p-3 space-y-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hideHeader}
            onChange={(e) => update({ hide: e.target.checked || undefined })}
            className="rounded accent-emerald-600"
          />
          <span className="text-xs font-semibold text-gray-700">
            Hide the entire header (full-bleed)
          </span>
        </label>

        {!hideHeader && (
          <>
            <div>
              <Label htmlFor={`${idPrefix}-wordmark`} className="text-xs">
                Wordmark text
              </Label>
              <Input
                id={`${idPrefix}-wordmark`}
                value={v.wordmark || ""}
                onChange={(e) => update({ wordmark: e.target.value })}
                placeholder="FansBall Knowledge"
                className="mt-1 h-8 text-xs"
              />
            </div>

            <div>
              <Label htmlFor={`${idPrefix}-pill`} className="text-xs">
                Pill text (right side)
              </Label>
              <Input
                id={`${idPrefix}-pill`}
                value={v.pillText || ""}
                onChange={(e) => update({ pillText: e.target.value })}
                placeholder="WORLD CUP 2026"
                className="mt-1 h-8 text-xs font-mono uppercase"
              />
              <p className="text-[10px] text-gray-500 mt-1">
                Leave blank to hide the pill.
              </p>
            </div>

            <div>
              <Label className="text-xs">Custom logo — optional</Label>
              {v.logoUrl ? (
                <div className="mt-1 flex items-center gap-2">
                  <img
                    src={v.logoUrl}
                    alt="Logo"
                    className="w-10 h-10 rounded border border-gray-200 object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => update({ logoUrl: undefined })}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Remove (use default /logo.png)
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="mt-1 w-full h-12 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-xs text-gray-500 hover:border-emerald-600 hover:text-emerald-700 transition gap-2"
                >
                  <ImageIcon className="h-4 w-4" />
                  Upload a custom logo
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] || null)}
              />
            </div>
          </>
        )}
      </div>
    </details>
  );
}

// ─── Footer fields (shared by every form) ────────────────────────────────────

function FooterFields({
  footerLeft,
  hashtags,
  defaultFooter,
  onFooterLeftChange,
  onHashtagsChange,
  footer,
  onFooterChange,
  idPrefix,
}: {
  footerLeft: string | undefined;
  hashtags: string | undefined;
  defaultFooter: string;
  onFooterLeftChange: (next: string) => void;
  onHashtagsChange: (next: string) => void;
  footer?: FooterSettings | undefined;
  onFooterChange?: (next: FooterSettings | undefined) => void;
  idPrefix: string;
}) {
  const hideFooter = footer?.hide === true;
  const setHide = (hide: boolean) => {
    if (!onFooterChange) return;
    const next: FooterSettings = { ...(footer ?? {}), hide: hide || undefined };
    onFooterChange(next.hide ? next : undefined);
  };

  return (
    <div className="space-y-3">
      {onFooterChange && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hideFooter}
            onChange={(e) => setHide(e.target.checked)}
            className="rounded accent-emerald-600"
          />
          <span className="text-xs font-semibold text-gray-700">
            Hide the entire footer (full-bleed)
          </span>
        </label>
      )}

      {!hideFooter && (
        <>
          <div>
            <Label htmlFor={`${idPrefix}-footer`}>Footer text (left)</Label>
            <Input
              id={`${idPrefix}-footer`}
              value={footerLeft || ""}
              onChange={(e) => onFooterLeftChange(e.target.value)}
              placeholder={defaultFooter}
              className="mt-1"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Leave blank to use the default: <code>{defaultFooter}</code>
            </p>
          </div>
          <div>
            <Label htmlFor={`${idPrefix}-hashtags`}>Hashtags (right)</Label>
            <Input
              id={`${idPrefix}-hashtags`}
              value={hashtags || ""}
              onChange={(e) => onHashtagsChange(e.target.value)}
              className="mt-1 font-mono"
              placeholder="#WC2026 · #FBK"
            />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Countdown form ──────────────────────────────────────────────────────────

const COUNTDOWN_THEMES: {
  value: CountdownTheme;
  label: string;
  mood: string;
  swatch: string;
  /** Suggested day range — drives the "we suggest…" hint. */
  range: [number, number];
}[] = [
  {
    value: "horizon",
    label: "Horizon",
    mood: "Calm · distant · star-flecked",
    swatch:
      "linear-gradient(135deg, #050a1e 0%, #1a2855 60%, #F5E6B8 100%)",
    range: [100, Number.POSITIVE_INFINITY],
  },
  {
    value: "signature",
    label: "Signature",
    mood: "FBK brand · classic green + gold",
    swatch:
      "linear-gradient(135deg, #0D8C3C 0%, #0D5C2E 50%, #FFD700 100%)",
    range: [50, 100],
  },
  {
    value: "pulse",
    label: "Pulse",
    mood: "Spotlight · gold halo rising",
    swatch:
      "radial-gradient(circle at 50% 50%, #1f3a25 0%, #0d1f12 50%, #000 100%)",
    range: [20, 50],
  },
  {
    value: "blaze",
    label: "Blaze",
    mood: "Sunset · sunburst · heat rising",
    swatch:
      "linear-gradient(180deg, #FFB02E 0%, #FF6B1A 50%, #E03C16 100%)",
    range: [7, 20],
  },
  {
    value: "ignite",
    label: "Ignite",
    mood: "Peak hype · pure gold · motion",
    swatch:
      "linear-gradient(135deg, #FFD700 0%, #FFC93C 50%, #F2B600 100%)",
    range: [0, 7],
  },
];

/** Pick the theme whose range bracket contains the day count. */
function suggestedTheme(days: number): CountdownTheme {
  for (const t of COUNTDOWN_THEMES) {
    if (days >= t.range[0] && days < t.range[1]) return t.value;
  }
  return "signature";
}

function CountdownForm({
  content,
  onChange,
}: {
  content: CountdownCardContent;
  onChange: (next: CountdownCardContent) => void;
}) {
  const update = <K extends keyof CountdownCardContent>(
    key: K,
    value: CountdownCardContent[K],
  ) => onChange({ ...content, [key]: value });

  const suggested = suggestedTheme(content.days);
  const isUsingSuggested = content.theme === suggested;

  return (
    <div className="space-y-4">
      {/* Days input + auto-suggest */}
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-3">
        <div>
          <Label htmlFor="countdown-days" className="text-indigo-900">
            Days remaining
          </Label>
          <Input
            id="countdown-days"
            type="number"
            min={0}
            value={content.days}
            onChange={(e) => {
              const next = Math.max(0, Number(e.target.value) || 0);
              // Auto-bump the theme to match the new bracket IF the admin
              // was on the previous bracket's auto suggestion. If they've
              // manually overridden, respect that.
              const wasAuto = content.theme === suggestedTheme(content.days);
              onChange({
                ...content,
                days: next,
                theme: wasAuto ? suggestedTheme(next) : content.theme,
                unitLabel: next === 1 ? "DAY" : "DAYS",
              });
            }}
            className="mt-1 font-mono text-2xl h-12"
          />
        </div>
        <p className="text-[11px] text-indigo-800 leading-relaxed">
          <Sparkles className="h-3 w-3 inline mr-1" />
          {isUsingSuggested ? (
            <>
              Using the suggested theme for {content.days} day
              {content.days === 1 ? "" : "s"} out:{" "}
              <b>{COUNTDOWN_THEMES.find((t) => t.value === suggested)?.label}</b>
              .
            </>
          ) : (
            <>
              We'd usually suggest{" "}
              <b>
                {COUNTDOWN_THEMES.find((t) => t.value === suggested)?.label}
              </b>{" "}
              for {content.days} day{content.days === 1 ? "" : "s"} out — your
              pick overrides this.
            </>
          )}
        </p>
      </div>

      {/* Theme swatch grid */}
      <div>
        <Label>Theme</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
          {COUNTDOWN_THEMES.map((t) => {
            const active = content.theme === t.value;
            const isAuto = t.value === suggested;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => update("theme", t.value)}
                className={cn(
                  "relative flex flex-col items-stretch rounded-lg border overflow-hidden transition text-left",
                  active
                    ? "border-indigo-600 ring-2 ring-indigo-600/20"
                    : "border-gray-200 hover:border-gray-300",
                )}
              >
                <div
                  className="h-16"
                  style={{ background: t.swatch }}
                />
                <div className="p-2 bg-white">
                  <div className="flex items-center gap-1.5">
                    <div className="text-xs font-bold text-gray-900">
                      {t.label}
                    </div>
                    {isAuto && (
                      <span className="text-[9px] font-bold text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded">
                        AUTO
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5 truncate">
                    {t.mood}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <Label htmlFor="countdown-eyebrow">Eyebrow tag</Label>
        <Input
          id="countdown-eyebrow"
          value={content.eyebrow}
          onChange={(e) => update("eyebrow", e.target.value.toUpperCase())}
          className="mt-1 font-mono uppercase tracking-widest"
          placeholder="WORLD CUP IN"
        />
      </div>

      <div>
        <Label htmlFor="countdown-unit">Unit label</Label>
        <Input
          id="countdown-unit"
          value={content.unitLabel}
          onChange={(e) => update("unitLabel", e.target.value.toUpperCase())}
          className="mt-1 font-mono uppercase tracking-widest"
          placeholder="DAYS"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Auto-switches to "DAY" when the count is 1.
        </p>
      </div>

      <div>
        <Label htmlFor="countdown-sub">Subtext (optional)</Label>
        <Textarea
          id="countdown-sub"
          value={content.subText ?? ""}
          onChange={(e) => update("subText", e.target.value)}
          className="mt-1"
          rows={2}
          placeholder="Until the world stops for thirty days."
        />
      </div>

      <div>
        <Label htmlFor="countdown-date">Date chip</Label>
        <Input
          id="countdown-date"
          value={content.dateLabel ?? ""}
          onChange={(e) => update("dateLabel", e.target.value)}
          className="mt-1 font-mono"
          placeholder="KICKOFF · 11 JUN 2026"
        />
      </div>

      <div>
        <Label htmlFor="countdown-cta">CTA pill (optional)</Label>
        <Input
          id="countdown-cta"
          value={content.ctaText ?? ""}
          onChange={(e) => update("ctaText", e.target.value)}
          className="mt-1"
          placeholder="Build your XI"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Leave blank to hide the CTA pill.
        </p>
      </div>

      <HeaderFields
        idPrefix="cd"
        value={content.header}
        onChange={(v) => update("header", v)}
      />

      <FooterFields
        idPrefix="cd"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("COUNTDOWN")}
        onFooterLeftChange={(v) => update("footerLeft", v)}
        onHashtagsChange={(v) => update("hashtags", v)}
        footer={content.footer}
        onFooterChange={(v) => update("footer", v)}
      />
    </div>
  );
}

// ─── Match Story form (3-page deck with match autofill + Wikipedia bio) ─────

const MATCH_STORY_PAGE_LABELS: { value: number; label: string; tone: string }[] = [
  { value: 0, label: "Cover", tone: "Flags · date · venue" },
  { value: 1, label: "Result", tone: "Scoreline · goalscorers" },
  { value: 2, label: "Story", tone: "Backstory paragraph" },
];

/** Map MatchEvent[] (GOAL + OWN_GOAL) → MatchResultGoal[] for the Result page. */
function eventsToStoryGoals(
  events: MatchEvent[],
  homeNationCode: string,
): MatchResultGoal[] {
  const goalEvents = events.filter(
    (e) => e.eventType === "GOAL" || e.eventType === "OWN_GOAL",
  );
  goalEvents.sort((a, b) => a.minute - b.minute);
  return goalEvents.map((e) => {
    const isOwnGoal = e.eventType === "OWN_GOAL";
    const playerCode = e.player.nation.code.toLowerCase();
    // GOAL: counts for the scorer's own team. OWN_GOAL: counts for the OPPOSING team.
    const playerSide: "home" | "away" =
      playerCode === homeNationCode.toLowerCase() ? "home" : "away";
    const credited: "home" | "away" = isOwnGoal
      ? playerSide === "home"
        ? "away"
        : "home"
      : playerSide;
    const surname =
      e.player.name.split(/\s+/).slice(-1)[0] || e.player.name;
    const minuteLabel = `${e.minute}'${isOwnGoal ? " (OG)" : ""}`;
    return {
      scorer: surname,
      minute: minuteLabel,
      side: credited,
    };
  });
}

/**
 * Fetch a player's short summary from the public Wikipedia REST API.
 * CORS-friendly, no key needed. Returns null on miss / network error.
 */
async function fetchWikipediaSummary(
  query: string,
): Promise<{ extract: string; url: string; thumbnailUrl?: string } | null> {
  if (!query.trim()) return null;
  const title = query.trim().replace(/\s+/g, "_");
  try {
    const res = await fetch(
      `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`,
    );
    if (!res.ok) return null;
    const json = (await res.json()) as {
      extract?: string;
      content_urls?: { desktop?: { page?: string } };
      thumbnail?: { source?: string };
      type?: string;
    };
    if (json.type === "disambiguation" || !json.extract) return null;
    return {
      extract: json.extract,
      url: json.content_urls?.desktop?.page || "",
      thumbnailUrl: json.thumbnail?.source,
    };
  } catch {
    return null;
  }
}

function MatchStoryForm({
  content,
  onChange,
  activePage,
  onActivePageChange,
}: {
  content: MatchStoryCardContent;
  onChange: (next: MatchStoryCardContent) => void;
  activePage: number;
  onActivePageChange: (next: number) => void;
}) {
  const update = <K extends keyof MatchStoryCardContent>(
    key: K,
    value: MatchStoryCardContent[K],
  ) => onChange({ ...content, [key]: value });

  // Match picker pulls the most recent ~100 matches across the tournament.
  const { data: matchesPage, isFetching: matchesFetching } = useMatchesAdmin({
    limit: 100,
    sortBy: "kickoff",
  });
  const allMatches: Match[] = useMemo(
    () => matchesPage?.matches ?? [],
    [matchesPage],
  );

  const { data: matchEvents } = useMatchEvents(content.matchId ?? 0);

  const [embeddingFlag, setEmbeddingFlag] = useState<"home" | "away" | null>(
    null,
  );
  const [fetchingWiki, setFetchingWiki] = useState(false);

  /** Apply a picked Match record to the content. Auto-fills cover + result fields. */
  const applyMatch = useCallback(
    (match: Match) => {
      const kickoff = new Date(match.kickoffTime);
      const validDate = !Number.isNaN(kickoff.getTime());
      const dateLabel = validDate
        ? kickoff
            .toLocaleDateString(undefined, {
              day: "2-digit",
              month: "short",
              weekday: "short",
            })
            .toUpperCase()
            .replace(",", " ·")
        : "";
      const eyebrow = match.stage
        ? `${match.phase.replace(/_/g, " ")} · ${match.stage.toUpperCase()}`
        : match.phase.replace(/_/g, " ");
      const venueLine = match.venue ?? "";
      onChange({
        ...content,
        matchId: match.id,
        eyebrow,
        home: {
          name: match.homeNation.name,
          code: match.homeNation.code,
          flagUrl: match.homeNation.flagUrl || undefined,
        },
        away: {
          name: match.awayNation.name,
          code: match.awayNation.code,
          flagUrl: match.awayNation.flagUrl || undefined,
        },
        dateLabel,
        venue: venueLine,
        homeScore: match.homeScore,
        awayScore: match.awayScore,
        // Goals will be re-derived when matchEvents loads (see effect below).
        goals: [],
        hashtags: `#WC2026 · #${match.homeNation.code.toUpperCase()}${match.awayNation.code.toUpperCase()} · #FBK`,
      });
    },
    [content, onChange],
  );

  // When events arrive (or the match changes), refill the goalscorer strip.
  useEffect(() => {
    if (!content.matchId || !matchEvents) return;
    const goals = eventsToStoryGoals(matchEvents, content.home.code);
    // Avoid feedback loop: only update if the derived list differs.
    const sameLength = (content.goals?.length ?? 0) === goals.length;
    const sameRows =
      sameLength &&
      goals.every(
        (g, i) =>
          content.goals?.[i]?.scorer === g.scorer &&
          content.goals?.[i]?.minute === g.minute &&
          content.goals?.[i]?.side === g.side,
      );
    if (!sameRows) onChange({ ...content, goals });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchEvents, content.matchId]);

  // Auto-embed flag images as data URLs so PNG export doesn't taint the canvas.
  useEffect(() => {
    let cancelled = false;
    const tryEmbed = async (side: "home" | "away") => {
      const team = side === "home" ? content.home : content.away;
      if (!team.flagUrl) return;
      if (team.flagUrl.startsWith("data:")) return;
      setEmbeddingFlag(side);
      const result = await autoEmbedImage(team.flagUrl);
      if (cancelled) return;
      setEmbeddingFlag(null);
      if (result.success) {
        if (side === "home") {
          onChange({ ...content, home: { ...content.home, flagUrl: result.dataUrl } });
        } else {
          onChange({ ...content, away: { ...content.away, flagUrl: result.dataUrl } });
        }
      }
    };
    if (content.home.flagUrl && !content.home.flagUrl.startsWith("data:")) {
      void tryEmbed("home");
    } else if (content.away.flagUrl && !content.away.flagUrl.startsWith("data:")) {
      void tryEmbed("away");
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.home.flagUrl, content.away.flagUrl]);

  const handleFetchBio = async () => {
    if (!content.featuredPlayerName?.trim()) {
      toast.warning("Type a featured player name first");
      return;
    }
    setFetchingWiki(true);
    const result = await fetchWikipediaSummary(content.featuredPlayerName);
    setFetchingWiki(false);
    if (!result) {
      toast.error("No Wikipedia summary found for that name.");
      return;
    }
    let photoDataUrl: string | undefined;
    if (result.thumbnailUrl) {
      const embed = await autoEmbedImage(result.thumbnailUrl);
      photoDataUrl = embed.success ? embed.dataUrl : result.thumbnailUrl;
    }
    onChange({
      ...content,
      featuredPlayerWikiSummary: result.extract,
      featuredPlayerPhotoUrl: photoDataUrl || content.featuredPlayerPhotoUrl,
    });
    toast.success("Wikipedia summary loaded.");
  };

  return (
    <div className="space-y-4">
      {/* Page navigator */}
      <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3">
        <div className="text-[11px] font-bold text-rose-900 uppercase tracking-wider mb-2">
          Editing page
        </div>
        <div className="flex gap-2">
          {MATCH_STORY_PAGE_LABELS.map((p) => {
            const active = activePage === p.value;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => onActivePageChange(p.value)}
                className={cn(
                  "flex-1 px-3 py-2 rounded-lg text-left transition border",
                  active
                    ? "border-rose-700 bg-white shadow ring-2 ring-rose-700/15"
                    : "border-rose-200 bg-white/60 hover:border-rose-300",
                )}
              >
                <div className="text-xs font-bold text-gray-900">
                  {p.value + 1}. {p.label}
                </div>
                <div className="text-[10px] text-gray-500 truncate">
                  {p.tone}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Match picker (drives Cover + Result autofill) */}
      <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-rose-900">Source match</Label>
          {(matchesFetching || embeddingFlag) && (
            <span className="text-[11px] text-rose-700 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              {embeddingFlag ? "Embedding flag" : "Loading matches"}
            </span>
          )}
        </div>
        <Select
          value={content.matchId ? String(content.matchId) : ""}
          onValueChange={(val) => {
            const m = allMatches.find((x) => String(x.id) === val);
            if (m) applyMatch(m);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Pick a match to auto-fill cover + result" />
          </SelectTrigger>
          <SelectContent>
            {allMatches.map((m) => (
              <SelectItem key={m.id} value={String(m.id)}>
                {m.homeNation.name} {m.homeScore}–{m.awayScore} {m.awayNation.name}
                {" · "}
                {m.phase.replace(/_/g, " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-rose-800 leading-relaxed">
          <Sparkles className="h-3 w-3 inline mr-1" />
          Picking a match auto-fills the cover (flags · date · venue) and the
          result (scoreline · goalscorers from MatchEvent). The Story page is
          all yours.
        </p>
      </div>

      {/* PAGE 0 — Cover fields */}
      {activePage === 0 && (
        <>
          <div>
            <Label htmlFor="ms-eyebrow">Eyebrow</Label>
            <Input
              id="ms-eyebrow"
              value={content.eyebrow}
              onChange={(e) => update("eyebrow", e.target.value)}
              className="mt-1 font-mono uppercase tracking-widest"
              placeholder="GROUP A · MATCHDAY 1"
            />
          </div>
          <div>
            <Label htmlFor="ms-date">Date label</Label>
            <Input
              id="ms-date"
              value={content.dateLabel ?? ""}
              onChange={(e) => update("dateLabel", e.target.value)}
              className="mt-1 font-mono"
              placeholder="FRI · JUN 12"
            />
          </div>
          <div>
            <Label htmlFor="ms-venue">Venue</Label>
            <Input
              id="ms-venue"
              value={content.venue ?? ""}
              onChange={(e) => update("venue", e.target.value)}
              className="mt-1"
              placeholder="Estadio Azteca · Mexico City"
            />
          </div>
          <div>
            <Label htmlFor="ms-cover-tag">Cover tagline (optional)</Label>
            <Textarea
              id="ms-cover-tag"
              value={content.coverTagline ?? ""}
              onChange={(e) => update("coverTagline", e.target.value)}
              className="mt-1"
              rows={2}
              placeholder="A hero returns. A nation roars."
            />
          </div>
        </>
      )}

      {/* PAGE 1 — Result fields */}
      {activePage === 1 && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ms-home-score">
                {content.home.name} score
              </Label>
              <Input
                id="ms-home-score"
                type="number"
                min={0}
                value={content.homeScore}
                onChange={(e) =>
                  update("homeScore", Math.max(0, Number(e.target.value) || 0))
                }
                className="mt-1 font-mono text-2xl h-12"
              />
            </div>
            <div>
              <Label htmlFor="ms-away-score">
                {content.away.name} score
              </Label>
              <Input
                id="ms-away-score"
                type="number"
                min={0}
                value={content.awayScore}
                onChange={(e) =>
                  update("awayScore", Math.max(0, Number(e.target.value) || 0))
                }
                className="mt-1 font-mono text-2xl h-12"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="ms-result-headline">Result page hook (optional)</Label>
            <Textarea
              id="ms-result-headline"
              value={content.resultHeadline ?? ""}
              onChange={(e) => update("resultHeadline", e.target.value)}
              className="mt-1"
              rows={2}
              placeholder="Jiménez crowns Mexico's opening night."
            />
          </div>
          <div>
            <Label htmlFor="ms-aggregate">Aggregate note (optional)</Label>
            <Input
              id="ms-aggregate"
              value={content.aggregateNote ?? ""}
              onChange={(e) => update("aggregateNote", e.target.value)}
              className="mt-1 font-mono uppercase"
              placeholder="AET · Pens 4-2"
            />
          </div>

          {/* Goal editor — auto-filled from MatchEvent, admin can tweak. */}
          <details className="rounded-lg border border-gray-200 bg-gray-50/40 group" open>
            <summary className="px-3 py-2.5 cursor-pointer flex items-center justify-between text-xs font-bold text-gray-700 uppercase tracking-wider list-none">
              <span>Goalscorers ({content.goals?.length ?? 0})</span>
              <span className="text-[10px] font-normal text-gray-500 normal-case tracking-normal group-open:hidden">
                Click to edit · auto-fills from match events
              </span>
            </summary>
            <div className="border-t border-gray-200 p-3 space-y-2 max-h-72 overflow-y-auto">
              {(content.goals ?? []).map((g, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-2 items-center bg-white border border-gray-200 rounded p-2"
                >
                  <Input
                    className="col-span-5 h-8 text-xs"
                    value={g.scorer}
                    onChange={(e) => {
                      const copy = [...(content.goals ?? [])];
                      copy[i] = { ...g, scorer: e.target.value };
                      update("goals", copy);
                    }}
                    placeholder="Surname"
                  />
                  <Input
                    className="col-span-3 h-8 text-xs font-mono"
                    value={g.minute}
                    onChange={(e) => {
                      const copy = [...(content.goals ?? [])];
                      copy[i] = { ...g, minute: e.target.value };
                      update("goals", copy);
                    }}
                    placeholder="23'"
                  />
                  <Select
                    value={g.side}
                    onValueChange={(val) => {
                      const copy = [...(content.goals ?? [])];
                      copy[i] = { ...g, side: val as "home" | "away" };
                      update("goals", copy);
                    }}
                  >
                    <SelectTrigger className="col-span-3 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="home">{content.home.code.toUpperCase()}</SelectItem>
                      <SelectItem value="away">{content.away.code.toUpperCase()}</SelectItem>
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={() => {
                      const copy = [...(content.goals ?? [])];
                      copy.splice(i, 1);
                      update("goals", copy);
                    }}
                    className="col-span-1 text-red-500 hover:text-red-700"
                    title="Remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  const copy = [
                    ...(content.goals ?? []),
                    { scorer: "", minute: "", side: "home" as const },
                  ];
                  update("goals", copy);
                }}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add a goal
              </Button>
            </div>
          </details>
        </>
      )}

      {/* PAGE 2 — Story fields */}
      {activePage === 2 && (
        <>
          <div>
            <Label htmlFor="ms-story-eyebrow">Story eyebrow</Label>
            <Input
              id="ms-story-eyebrow"
              value={content.storyEyebrow ?? ""}
              onChange={(e) => update("storyEyebrow", e.target.value.toUpperCase())}
              className="mt-1 font-mono uppercase tracking-widest"
              placeholder="THE COMEBACK"
            />
          </div>
          <div>
            <Label htmlFor="ms-story-headline">Story headline</Label>
            <Input
              id="ms-story-headline"
              value={content.storyHeadline}
              onChange={(e) => update("storyHeadline", e.target.value)}
              className="mt-1"
              placeholder="Nine months ago"
            />
          </div>
          <div>
            <Label htmlFor="ms-story-gold">Gold closing (optional)</Label>
            <Input
              id="ms-story-gold"
              value={content.storyGoldHighlight ?? ""}
              onChange={(e) => update("storyGoldHighlight", e.target.value)}
              className="mt-1"
              placeholder="Raúl Jiménez was in a coma."
            />
          </div>
          <div>
            <Label htmlFor="ms-story-body">Story paragraph</Label>
            <Textarea
              id="ms-story-body"
              value={content.storyBody}
              onChange={(e) => update("storyBody", e.target.value)}
              className="mt-1"
              rows={6}
              placeholder={"Nine months ago, a fractured skull put him in a coma.\n\nTonight he came off the bench and headed home Mexico's second."}
            />
            <p className="text-[11px] text-gray-500 mt-1">
              80–160 words is the sweet spot. Use blank lines for paragraph breaks.
            </p>
          </div>

          {/* Featured player chip + Wikipedia bio fetcher */}
          <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3 space-y-3">
            <div>
              <Label htmlFor="ms-featured-name" className="text-rose-900">
                Featured player
              </Label>
              <Input
                id="ms-featured-name"
                value={content.featuredPlayerName ?? ""}
                onChange={(e) => update("featuredPlayerName", e.target.value)}
                className="mt-1"
                placeholder="Raúl Jiménez"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={handleFetchBio}
                disabled={fetchingWiki || !content.featuredPlayerName?.trim()}
                className="bg-rose-700 hover:bg-rose-800 text-white"
              >
                {fetchingWiki ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Fetch Wikipedia bio
              </Button>
              {content.featuredPlayerPhotoUrl && (
                <button
                  type="button"
                  onClick={() => update("featuredPlayerPhotoUrl", undefined)}
                  className="text-xs text-rose-700 hover:text-rose-900 underline"
                >
                  Remove photo
                </button>
              )}
            </div>
            {content.featuredPlayerWikiSummary && (
              <details className="rounded border border-rose-200 bg-white">
                <summary className="px-2 py-1.5 text-[11px] font-bold text-rose-900 uppercase tracking-wider cursor-pointer">
                  Wikipedia summary — paste from this into the story body
                </summary>
                <div className="p-3 text-xs text-gray-700 leading-relaxed border-t border-rose-100 whitespace-pre-wrap">
                  {content.featuredPlayerWikiSummary}
                </div>
              </details>
            )}
            <p className="text-[11px] text-rose-800 leading-relaxed">
              <Sparkles className="h-3 w-3 inline mr-1" />
              Wikipedia is your free fact-source. The summary loads into the
              panel above — copy the parts that fit, then write the on-brand
              FBK paragraph yourself.
            </p>
          </div>
        </>
      )}

      <HeaderFields
        idPrefix="ms"
        value={content.header}
        onChange={(v) => update("header", v)}
      />

      <FooterFields
        idPrefix="ms"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("MATCH_STORY")}
        onFooterLeftChange={(v) => update("footerLeft", v)}
        onHashtagsChange={(v) => update("hashtags", v)}
        footer={content.footer}
        onFooterChange={(v) => update("footer", v)}
      />
    </div>
  );
}

// ─── Captain Picks form (editable ranked list, optional gameweek seed) ──────

function CaptainPicksForm({
  mode,
  content,
  onChange,
}: {
  /**
   * "top-scorers" — seeds from gameweek eligible-players, sorted by raw points.
   *   The C armband is hidden on the card; pick counts represent points.
   * "captain-picks" — seeds from the global Top Captains endpoint, sorted by
   *   how many user squads actually captained each player. C armband visible.
   */
  mode: "top-scorers" | "captain-picks";
  content: CaptainPicksCardContent;
  onChange: (next: CaptainPicksCardContent) => void;
}) {
  const update = <K extends keyof CaptainPicksCardContent>(
    key: K,
    value: CaptainPicksCardContent[K],
  ) => onChange({ ...content, [key]: value });

  // ─── Seeders ───────────────────────────────────────────────────────────
  // TOP_SCORERS uses the per-gameweek eligible-players endpoint, sorted by
  // raw points. CAPTAIN_PICKS uses the global Top Captains endpoint, which
  // aggregates how many user squads have each player as captain — the same
  // data the /fantasy-leaderboard "Top Captains" tab renders.
  const { data: gameweeksData } = useGameweeks();
  const [seedGameweekId, setSeedGameweekId] = useState<number | null>(null);
  const { data: eligible, isFetching: eligibleFetching } =
    useGameweekEligiblePlayers(mode === "top-scorers" ? seedGameweekId : null);
  const { data: topCaptains, isFetching: captainsFetching } = useTopCaptains(
    mode === "captain-picks" ? 10 : 0,
  );

  const seedFromGameweek = async () => {
    if (!eligible?.players?.length) {
      toast.warning("No eligible players for that gameweek yet.");
      return;
    }
    const top = [...eligible.players]
      .sort((a, b) => b.stats.rawPoints - a.stats.rawPoints)
      .slice(0, 10);

    const newPicks: CaptainPick[] = await Promise.all(
      top.map(async (p): Promise<CaptainPick> => {
        // Drop non-image URLs to null so the tile falls back to initials
        // instead of dragging a broken URL into the PNG export.
        const embed = async (url: string | null | undefined) => {
          if (!url) return null;
          if (url.startsWith("data:")) return url;
          const r = await autoEmbedImage(url);
          if (r.success) return r.dataUrl;
          console.warn("[seed:gw] dropping non-embeddable image:", url, r.reason);
          return null;
        };
        const [photo, club, flag] = await Promise.all([
          embed(p.player.photo),
          embed(p.player.club?.logoUrl),
          embed(p.player.nation?.flagUrl),
        ]);
        return {
          name: p.player.name,
          photoUrl: photo,
          clubName: p.player.club?.name,
          clubLogoUrl: club,
          nationFlagUrl: flag,
          position: p.player.position,
          pickValue: p.stats.rawPoints,
        };
      }),
    );
    onChange({ ...content, picks: newPicks, displayMode: "count" });
    toast.success(`Seeded ${newPicks.length} players from gameweek points`);
  };

  /**
   * Seed from the global Top Captains endpoint — same data source the
   * fantasy-leaderboard "Top Captains" tab uses. The endpoint doesn't return
   * photos or club info, so rows are name + nation + position + count.
   */
  const seedFromTopCaptains = async () => {
    const list = topCaptains?.captains ?? [];
    if (!list.length) {
      toast.warning("No captain selections yet — no squads have picked one.");
      return;
    }
    const VALID_POS: ReadonlyArray<NonNullable<CaptainPick["position"]>> = [
      "GK",
      "DEF",
      "MID",
      "FWD",
    ];

    // The /top-captains endpoint is lean — only playerId, name, nation,
    // position, count. To get the photo + club crest + nation flag, fetch
    // the full Player record by ID in parallel and auto-embed every image
    // so export doesn't taint the canvas. Non-image URLs are dropped to
    // null so the tile falls back to initials.
    const embed = async (url: string | null | undefined) => {
      if (!url) return null;
      if (url.startsWith("data:")) return url;
      const r = await autoEmbedImage(url);
      if (r.success) return r.dataUrl;
      console.warn("[seed:captains] dropping non-embeddable image:", url, r.reason);
      return null;
    };

    const newPicks: CaptainPick[] = await Promise.all(
      list.slice(0, 10).map(async (c): Promise<CaptainPick> => {
        // Tolerate missing players (deleted, bad ID) — fall back to lean data.
        let player: Awaited<ReturnType<typeof playersService.getPlayer>> | null = null;
        try {
          player = await playersService.getPlayer(c.playerId);
        } catch {
          player = null;
        }
        const [photo, clubLogo, flag] = await Promise.all([
          embed(player?.photo),
          embed(player?.club?.logoUrl),
          embed(player?.nation?.flagUrl),
        ]);
        return {
          name: c.playerName,
          // Prefer the resolved club name for richer copy; fall back to nation.
          clubName: player?.club?.name || c.nation,
          clubLogoUrl: clubLogo,
          nationCode: player?.nation?.code?.toLowerCase(),
          nationFlagUrl: flag,
          photoUrl: photo,
          position: VALID_POS.includes(
            c.position as NonNullable<CaptainPick["position"]>,
          )
            ? (c.position as NonNullable<CaptainPick["position"]>)
            : undefined,
          pickValue: c.selectedAsCaptain,
        };
      }),
    );
    onChange({
      ...content,
      picks: newPicks,
      displayMode: "count",
      showCaptainArmband: true,
    });
    toast.success(`Seeded ${newPicks.length} captains from user squads`);
  };

  const setRow = (i: number, patch: Partial<CaptainPick>) => {
    const next = [...content.picks];
    next[i] = { ...next[i], ...patch };
    update("picks", next);
  };

  const removeRow = (i: number) => {
    const next = [...content.picks];
    next.splice(i, 1);
    update("picks", next);
  };

  const moveRow = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= content.picks.length) return;
    const next = [...content.picks];
    [next[i], next[j]] = [next[j], next[i]];
    update("picks", next);
  };

  const addRow = () => {
    if (content.picks.length >= 15) {
      toast.info("Maximum 15 rows.");
      return;
    }
    update("picks", [
      ...content.picks,
      { name: "", pickValue: 0 },
    ]);
  };

  /**
   * Drop every inherited image on a row — photo, club crest, nation
   * flag. Used when the admin wholesale replaces a player in an
   * existing row (e.g. seeded data → manual override) and doesn't want
   * the previous identity's imagery hanging around.
   */
  const resetRowImages = (i: number) => {
    setRow(i, {
      photoUrl: null,
      clubLogoUrl: null,
      nationFlagUrl: null,
    });
  };

  /**
   * Read a chosen image file into a data URL and store it on the row.
   * Data URL is the simplest path here: it embeds the image directly
   * into the card state, so PNG export works without any extra upload
   * step or CORS dance. Used for player photo, club crest, and nation
   * flag — all three accept the same upload UX.
   */
  const handleRowImage = (
    i: number,
    file: File | null,
    field: "photoUrl" | "clubLogoUrl" | "nationFlagUrl",
  ) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.warning("Image must be under 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () =>
      setRow(i, { [field]: reader.result as string });
    reader.onerror = () => toast.error("Failed to read image.");
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="cp-eyebrow">Eyebrow</Label>
        <Input
          id="cp-eyebrow"
          value={content.eyebrow}
          onChange={(e) => update("eyebrow", e.target.value.toUpperCase())}
          className="mt-1 font-mono uppercase tracking-widest"
          placeholder="MOST CAPTAINED · MATCHWEEK 3"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="cp-headline">Title</Label>
          <Input
            id="cp-headline"
            value={content.headline}
            onChange={(e) => update("headline", e.target.value)}
            className="mt-1"
            placeholder="Captain Picks"
          />
        </div>
        <div>
          <Label htmlFor="cp-gold">Gold accent</Label>
          <Input
            id="cp-gold"
            value={content.goldHighlight ?? ""}
            onChange={(e) => update("goldHighlight", e.target.value)}
            className="mt-1"
            placeholder="Top 10"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="cp-tagline">Tagline (optional)</Label>
        <Input
          id="cp-tagline"
          value={content.tagline ?? ""}
          onChange={(e) => update("tagline", e.target.value)}
          className="mt-1"
          placeholder="Where the crowd put their armbands."
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Number display</Label>
          <Select
            value={content.displayMode}
            onValueChange={(v) =>
              update("displayMode", v as "percent" | "count")
            }
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percent">% of squads (e.g. 23%)</SelectItem>
              <SelectItem value="count">Raw count (e.g. 1,240)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="cp-total">Footer label (optional)</Label>
          <Input
            id="cp-total"
            value={content.totalLabel ?? ""}
            onChange={(e) => update("totalLabel", e.target.value)}
            className="mt-1 uppercase tracking-widest"
            placeholder="Across 14,372 squads · MW3"
          />
        </div>
      </div>

      {/* Mode-aware seeder block. Top Scorers uses the per-gameweek
          eligible-players endpoint; Captain Picks uses the global Top
          Captains endpoint (no gameweek param needed — it's cross-tournament). */}
      {mode === "top-scorers" ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-amber-900">Seed from gameweek (optional)</Label>
            {eligibleFetching && (
              <span className="text-[11px] text-amber-700 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading players
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Select
              value={seedGameweekId == null ? "" : String(seedGameweekId)}
              onValueChange={(v) => setSeedGameweekId(v ? Number(v) : null)}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Pick a gameweek" />
              </SelectTrigger>
              <SelectContent>
                {gameweeksData?.map((gw) => (
                  <SelectItem key={gw.id} value={String(gw.id)}>
                    {gw.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              onClick={seedFromGameweek}
              disabled={!seedGameweekId || eligibleFetching}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Seed top 10
            </Button>
          </div>
          <p className="text-[11px] text-amber-800 leading-relaxed">
            Picks the 10 highest-scoring players from the gameweek. Auto-embeds
            photos + club crests + flags for export. You can edit every row
            afterwards.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-amber-900">Seed from Top Captains</Label>
            {captainsFetching && (
              <span className="text-[11px] text-amber-700 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading captains
              </span>
            )}
          </div>
          <Button
            type="button"
            onClick={seedFromTopCaptains}
            disabled={captainsFetching || !topCaptains?.captains?.length}
            className="bg-amber-600 hover:bg-amber-700 text-white w-full"
          >
            <Crown className="h-4 w-4 mr-2" />
            Seed top 10 captains
          </Button>
          <p className="text-[11px] text-amber-800 leading-relaxed">
            Pulls the 10 players most often selected as captain across user
            squads — same data as the <code>/fantasy-leaderboard</code> "Top
            Captains" tab. Edit any row afterwards.
          </p>
        </div>
      )}

      {/* Per-row editor */}
      <div className="rounded-lg border border-gray-200 bg-gray-50/40">
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
          <div className="text-xs font-bold text-gray-700 uppercase tracking-wider">
            Players ({content.picks.length})
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addRow}
            disabled={content.picks.length >= 15}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add row
          </Button>
        </div>
        <div className="p-3 space-y-2 max-h-[480px] overflow-y-auto">
          {content.picks.map((p, i) => (
            <div
              key={i}
              className="bg-white border border-gray-200 rounded p-2 space-y-2"
            >
              <div className="flex items-center gap-2">
                <div className="w-4 text-center text-[10px] font-bold text-gray-400">
                  {i + 1}
                </div>
                {/* Photo thumbnail / upload — click to choose a file */}
                <label
                  className={cn(
                    "w-8 h-8 rounded-full overflow-hidden flex-shrink-0 cursor-pointer border-2 transition flex items-center justify-center",
                    p.photoUrl
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-dashed border-gray-300 hover:border-emerald-500 bg-gray-50 hover:bg-emerald-50",
                  )}
                  title={p.photoUrl ? "Change photo" : "Upload photo"}
                >
                  {p.photoUrl ? (
                    <img
                      src={p.photoUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="h-3.5 w-3.5 text-gray-400" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) =>
                      handleRowImage(i, e.target.files?.[0] || null, "photoUrl")
                    }
                  />
                </label>
                <Input
                  className="flex-1 h-8 text-xs"
                  value={p.name}
                  onChange={(e) => setRow(i, { name: e.target.value })}
                  placeholder="Player name"
                />
                {p.photoUrl && (
                  <button
                    type="button"
                    onClick={() => setRow(i, { photoUrl: null })}
                    className="text-gray-400 hover:text-gray-700 text-[10px]"
                    title="Remove photo"
                  >
                    clear
                  </button>
                )}
                {/* One-click wipe for ALL images on this row — photo, club
                    crest, nation flag. Use when replacing a seeded player
                    with a manual entry so previous imagery doesn't leak. */}
                {(p.photoUrl || p.clubLogoUrl || p.nationFlagUrl) && (
                  <button
                    type="button"
                    onClick={() => resetRowImages(i)}
                    className="text-amber-600 hover:text-amber-800"
                    title="Reset all images on this row (photo, crest, flag)"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => moveRow(i, -1)}
                  disabled={i === 0}
                  className="text-gray-500 hover:text-gray-800 disabled:opacity-30"
                  title="Move up"
                >
                  ▲
                </button>
                <button
                  type="button"
                  onClick={() => moveRow(i, +1)}
                  disabled={i === content.picks.length - 1}
                  className="text-gray-500 hover:text-gray-800 disabled:opacity-30"
                  title="Move down"
                >
                  ▼
                </button>
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="text-red-500 hover:text-red-700"
                  title="Remove"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-12 gap-2 pl-8">
                {/* Club crest upload — small clickable thumbnail. Required
                    for manually-added rows since useTopCaptains seed doesn't
                    bring crests, and the iso2 fallback can't render one. */}
                <label
                  className={cn(
                    "col-span-1 h-8 w-8 rounded overflow-hidden cursor-pointer border flex items-center justify-center transition shrink-0",
                    p.clubLogoUrl
                      ? "border-emerald-400 bg-white"
                      : "border-dashed border-gray-300 hover:border-emerald-500 bg-gray-50 hover:bg-emerald-50",
                  )}
                  title={p.clubLogoUrl ? "Change club crest" : "Upload club crest"}
                >
                  {p.clubLogoUrl ? (
                    <img
                      src={p.clubLogoUrl}
                      alt=""
                      className="w-full h-full object-contain p-0.5"
                    />
                  ) : (
                    <ImageIcon className="h-3 w-3 text-gray-400" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) =>
                      handleRowImage(
                        i,
                        e.target.files?.[0] || null,
                        "clubLogoUrl",
                      )
                    }
                  />
                </label>
                <Input
                  className="col-span-3 h-8 text-xs"
                  value={p.clubName ?? ""}
                  onChange={(e) => setRow(i, { clubName: e.target.value })}
                  placeholder={mode === "captain-picks" ? "Nation" : "Club"}
                />
                <Input
                  className="col-span-2 h-8 text-xs font-mono"
                  value={p.nationCode ?? ""}
                  onChange={(e) =>
                    // Drop the inherited flag data URL when the code changes
                    // so the flagcdn fallback resolves to the NEW iso2.
                    // Otherwise the previous player's flag stays rendered.
                    setRow(i, {
                      nationCode: e.target.value.toLowerCase(),
                      nationFlagUrl: null,
                    })
                  }
                  placeholder="iso2"
                />
                <Select
                  value={p.position ?? "none"}
                  onValueChange={(v) =>
                    setRow(i, {
                      position:
                        v === "none"
                          ? undefined
                          : (v as NonNullable<CaptainPick["position"]>),
                    })
                  }
                >
                  <SelectTrigger className="col-span-3 h-8 text-xs">
                    <SelectValue placeholder="Pos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    <SelectItem value="GK">GK</SelectItem>
                    <SelectItem value="DEF">DEF</SelectItem>
                    <SelectItem value="MID">MID</SelectItem>
                    <SelectItem value="FWD">FWD</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  className="col-span-3 h-8 text-xs font-mono"
                  type="number"
                  value={p.pickValue}
                  onChange={(e) =>
                    setRow(i, { pickValue: Number(e.target.value) || 0 })
                  }
                  placeholder="Value"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <HeaderFields
        idPrefix="cp"
        value={content.header}
        onChange={(v) => update("header", v)}
      />

      <FooterFields
        idPrefix="cp"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("CAPTAIN_PICKS")}
        onFooterLeftChange={(v) => update("footerLeft", v)}
        onHashtagsChange={(v) => update("hashtags", v)}
        footer={content.footer}
        onFooterChange={(v) => update("footer", v)}
      />
    </div>
  );
}

// ─── Preview frame ───────────────────────────────────────────────────────────

function PreviewFrame({
  label,
  format,
  onClick,
  children,
}: {
  label: string;
  format: CardFormat;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const spec = CARD_FORMATS[format];
  const scale = format === "story" ? 0.19 : 0.24;
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
        {label}
      </div>
      <button
        type="button"
        onClick={onClick}
        className="group relative cursor-zoom-in transition-transform hover:scale-[1.02]"
        style={{
          width: spec.width * scale,
          height: spec.height * scale,
          overflow: "hidden",
          borderRadius: 12,
          boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
        }}
        title="Click to zoom"
      >
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            width: spec.width,
            height: spec.height,
            pointerEvents: "none",
          }}
        >
          {children}
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="bg-white/95 text-gray-900 rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 shadow-lg">
            <Maximize2 className="h-3.5 w-3.5" />
            Zoom
          </div>
        </div>
      </button>
    </div>
  );
}

// ─── MATCH RESULT form (post-match recap, 8 themes) ──────────────────────────

const MATCH_RESULT_THEMES: { value: MatchResultTheme; label: string; desc: string; swatch: string }[] = [
  {
    value: "victory",
    label: "Victory",
    desc: "Winner's colour floods one side, loser fades to shadow",
    swatch: "linear-gradient(115deg, #006847 0%, #006847 48%, #FFD700 49%, #FFD700 51%, #0a0a0a 52%, #0a0a0a 100%)",
  },
  {
    value: "stadium",
    label: "Stadium",
    desc: "Floodlit night + LED-scoreboard score",
    swatch: "radial-gradient(ellipse 80% 60% at 50% 30%, #1a2e35 0%, #07121a 60%, #000000 100%)",
  },
  {
    value: "newspaper",
    label: "Newspaper",
    desc: "Cream paper + bold serif headline",
    swatch: "#F4EDD9",
  },
  {
    value: "half_half",
    label: "Half & Half",
    desc: "Diagonal split filled with each team's colour",
    swatch: "linear-gradient(115deg, #006847 0%, #006847 48%, #FFD700 49%, #FFD700 51%, #006400 52%, #006400 100%)",
  },
  {
    value: "neon",
    label: "Neon",
    desc: "Synthwave grid floor + cyan/pink glow",
    swatch: "linear-gradient(180deg, #0a0118 0%, #2d0a4a 60%, #ff00aa 100%)",
  },
  {
    value: "minimal",
    label: "Minimal",
    desc: "Off-white, one massive score, whisper labels",
    swatch: "#FAFAFA",
  },
  {
    value: "cinema",
    label: "Cinema",
    desc: "Black + gold spotlight on the winner — goosebumps",
    swatch: "radial-gradient(ellipse 65% 50% at 50% 45%, #2a2a1a 0%, #0a0a0a 50%, #000000 100%)",
  },
  {
    value: "ticker",
    label: "Ticker",
    desc: "Sky Sports / ESPN broadcast lower-third",
    swatch: "linear-gradient(180deg, #0D8C3C 0%, #0A4724 100%)",
  },
];

function MatchResultForm({
  content,
  onChange,
}: {
  content: MatchResultCardContent;
  onChange: (next: MatchResultCardContent) => void;
}) {
  const update = <K extends keyof MatchResultCardContent>(
    key: K,
    value: MatchResultCardContent[K],
  ) => onChange({ ...content, [key]: value });

  const updateHome = (patch: Partial<MatchResultCardContent["home"]>) =>
    update("home", { ...content.home, ...patch });
  const updateAway = (patch: Partial<MatchResultCardContent["away"]>) =>
    update("away", { ...content.away, ...patch });

  const goals = content.goals ?? [];
  const updateGoal = (i: number, patch: Partial<(typeof goals)[number]>) => {
    const copy = [...goals];
    copy[i] = { ...copy[i], ...patch };
    update("goals", copy);
  };
  const addGoal = (side: "home" | "away") =>
    update("goals", [...goals, { scorer: "", minute: "", side }]);
  const removeGoal = (i: number) =>
    update("goals", goals.filter((_, j) => j !== i));

  const homeGoals = goals.map((g, i) => ({ goal: g, index: i })).filter((x) => x.goal.side === "home");
  const awayGoals = goals.map((g, i) => ({ goal: g, index: i })).filter((x) => x.goal.side === "away");

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="mr-eyebrow">Eyebrow tag</Label>
        <Input
          id="mr-eyebrow"
          value={content.eyebrow}
          onChange={(e) => update("eyebrow", e.target.value.toUpperCase())}
          className="font-mono uppercase mt-1"
          placeholder="MATCHWEEK 1 · GROUP A"
        />
      </div>

      {/* HOME team */}
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-3">
        <Label className="text-emerald-900">🏠 Home team</Label>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Label htmlFor="mr-home-name" className="text-xs">
              Name
            </Label>
            <Input
              id="mr-home-name"
              value={content.home.name}
              onChange={(e) => updateHome({ name: e.target.value })}
              placeholder="Mexico"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="mr-home-code" className="text-xs">
              ISO-2 code
            </Label>
            <Input
              id="mr-home-code"
              value={content.home.code}
              onChange={(e) => updateHome({ code: e.target.value.toLowerCase().slice(0, 3) })}
              placeholder="mx"
              className="font-mono lowercase mt-1"
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Label htmlFor="mr-home-color" className="text-xs">
              Brand colour (optional)
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                id="mr-home-color"
                type="color"
                value={content.home.color || "#006847"}
                onChange={(e) => updateHome({ color: e.target.value })}
                className="h-10 w-14 p-1 cursor-pointer"
              />
              <Input
                value={content.home.color || ""}
                onChange={(e) => updateHome({ color: e.target.value || undefined })}
                placeholder="auto from ISO-2"
                className="font-mono"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="mr-home-score" className="text-xs">
              Score
            </Label>
            <Input
              id="mr-home-score"
              type="number"
              min={0}
              max={99}
              value={content.homeScore}
              onChange={(e) => update("homeScore", parseInt(e.target.value, 10) || 0)}
              className="font-mono text-center mt-1 text-lg font-bold"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Flag image (overrides flagcdn) — optional</Label>
          <ImageUploadField
            value={content.home.flagUrl || undefined}
            onChange={(next) => updateHome({ flagUrl: next || undefined })}
          />
        </div>
      </div>

      {/* AWAY team */}
      <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3 space-y-3">
        <Label className="text-rose-900">✈️ Away team</Label>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Label htmlFor="mr-away-name" className="text-xs">
              Name
            </Label>
            <Input
              id="mr-away-name"
              value={content.away.name}
              onChange={(e) => updateAway({ name: e.target.value })}
              placeholder="South Africa"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="mr-away-code" className="text-xs">
              ISO-2 code
            </Label>
            <Input
              id="mr-away-code"
              value={content.away.code}
              onChange={(e) => updateAway({ code: e.target.value.toLowerCase().slice(0, 3) })}
              placeholder="za"
              className="font-mono lowercase mt-1"
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Label htmlFor="mr-away-color" className="text-xs">
              Brand colour (optional)
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                id="mr-away-color"
                type="color"
                value={content.away.color || "#006400"}
                onChange={(e) => updateAway({ color: e.target.value })}
                className="h-10 w-14 p-1 cursor-pointer"
              />
              <Input
                value={content.away.color || ""}
                onChange={(e) => updateAway({ color: e.target.value || undefined })}
                placeholder="auto from ISO-2"
                className="font-mono"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="mr-away-score" className="text-xs">
              Score
            </Label>
            <Input
              id="mr-away-score"
              type="number"
              min={0}
              max={99}
              value={content.awayScore}
              onChange={(e) => update("awayScore", parseInt(e.target.value, 10) || 0)}
              className="font-mono text-center mt-1 text-lg font-bold"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Flag image (overrides flagcdn) — optional</Label>
          <ImageUploadField
            value={content.away.flagUrl || undefined}
            onChange={(next) => updateAway({ flagUrl: next || undefined })}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="mr-headline">Headline — used heavily by Newspaper / Cinema themes</Label>
        <Input
          id="mr-headline"
          value={content.headline || ""}
          onChange={(e) => update("headline", e.target.value)}
          placeholder="HOSTS STUN"
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="mr-tagline">Tagline / hot take — optional</Label>
        <Input
          id="mr-tagline"
          value={content.tagline || ""}
          onChange={(e) => update("tagline", e.target.value)}
          placeholder="Lozano and Vega open the World Cup with a flourish."
          className="mt-1"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="mr-date">Date label</Label>
          <Input
            id="mr-date"
            value={content.dateLabel || ""}
            onChange={(e) => update("dateLabel", e.target.value.toUpperCase())}
            className="font-mono uppercase mt-1"
            placeholder="FRI · JUN 12"
          />
        </div>
        <div>
          <Label htmlFor="mr-venue">Venue</Label>
          <Input
            id="mr-venue"
            value={content.venue || ""}
            onChange={(e) => update("venue", e.target.value)}
            className="mt-1"
            placeholder="Estadio Azteca"
          />
        </div>
      </div>

      {/* GOALS */}
      <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <Label>⚽ Goals — optional</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addGoal("home")}
              className="gap-1 h-7 text-xs"
            >
              <Plus className="h-3 w-3" />
              Home goal
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addGoal("away")}
              className="gap-1 h-7 text-xs"
            >
              <Plus className="h-3 w-3" />
              Away goal
            </Button>
          </div>
        </div>
        {goals.length === 0 && (
          <p className="text-[11px] text-gray-500 mt-1">
            Goals are shown only on Stadium, Newspaper, and Ticker themes.
          </p>
        )}
        {homeGoals.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-[11px] font-bold text-emerald-900 uppercase tracking-wider">Home</div>
            {homeGoals.map(({ goal, index }) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={goal.scorer}
                  onChange={(e) => updateGoal(index, { scorer: e.target.value })}
                  placeholder="Lozano"
                  className="flex-1"
                />
                <Input
                  value={goal.minute}
                  onChange={(e) => updateGoal(index, { minute: e.target.value })}
                  placeholder="23'"
                  className="w-24 font-mono"
                />
                <button
                  type="button"
                  onClick={() => removeGoal(index)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        {awayGoals.length > 0 && (
          <div className="space-y-1.5 pt-2 border-t border-amber-200">
            <div className="text-[11px] font-bold text-rose-900 uppercase tracking-wider">Away</div>
            {awayGoals.map(({ goal, index }) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={goal.scorer}
                  onChange={(e) => updateGoal(index, { scorer: e.target.value })}
                  placeholder="Khune"
                  className="flex-1"
                />
                <Input
                  value={goal.minute}
                  onChange={(e) => updateGoal(index, { minute: e.target.value })}
                  placeholder="56'"
                  className="w-24 font-mono"
                />
                <button
                  type="button"
                  onClick={() => removeGoal(index)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="mr-motm">MOTM — optional</Label>
          <Input
            id="mr-motm"
            value={content.motm || ""}
            onChange={(e) => update("motm", e.target.value)}
            placeholder="Hirving Lozano"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="mr-agg">Note (AET / Pens 4-2) — optional</Label>
          <Input
            id="mr-agg"
            value={content.aggregateNote || ""}
            onChange={(e) => update("aggregateNote", e.target.value.toUpperCase())}
            placeholder="AET"
            className="font-mono uppercase mt-1"
          />
        </div>
      </div>

      {/* THEME PICKER — 8 bespoke themes */}
      <div className="rounded-lg border border-gray-200 bg-gray-50/40 p-3 space-y-2">
        <Label>🎨 Theme · {MATCH_RESULT_THEMES.length} hand-tuned layouts</Label>
        <div className="grid grid-cols-2 gap-2">
          {MATCH_RESULT_THEMES.map((t) => {
            const active = content.theme === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => update("theme", t.value)}
                className={cn(
                  "text-left rounded-lg border-2 p-2 transition flex gap-3 items-center",
                  active
                    ? "border-emerald-500 bg-white shadow-sm"
                    : "border-gray-200 bg-white hover:border-gray-300",
                )}
              >
                <div
                  className="w-12 h-12 rounded-md shrink-0 border border-black/10"
                  style={{ background: t.swatch }}
                />
                <div className="min-w-0">
                  <div className="font-bold text-xs text-gray-900 truncate">
                    {t.label}
                  </div>
                  <div className="text-[10px] text-gray-500 leading-tight">
                    {t.desc}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-gray-500">
          Winner emphasis (gold spotlight on the winning side) only applies to
          <b> Victory</b> and <b>Cinema</b>. All others render both teams equally.
        </p>
      </div>

      <FooterFields
        idPrefix="mr"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("MATCH_RESULT")}
        onFooterLeftChange={(v) => update("footerLeft", v)}
        onHashtagsChange={(v) => update("hashtags", v)}
        footer={content.footer}
        onFooterChange={(v) => update("footer", v)}
      />

      <HeaderFields
        idPrefix="mr"
        value={content.header}
        onChange={(v) => update("header", v)}
      />
    </div>
  );
}

// ─── Factsheet form (multi-slide deck editor — cover + N chapter slides) ────

const VARIANT_LABEL: Record<FactsheetVariant, string> = {
  cinematic: "Cinematic — full-bleed photo + centered headline",
  newsroom: "Newsroom — photo top, typography panel bottom",
  pill: "Pill — full-bleed photo, gold pill above headline",
  memorial:
    "Memorial — quiet tribute (serif name + dates, black & white, no CTA)",
  split: "Split — image band on top, text below on solid (best legibility)",
};

function FactsheetForm({
  content,
  onChange,
  activePage,
  onActivePageChange,
}: {
  content: FactsheetCardContent;
  onChange: (next: FactsheetCardContent) => void;
  activePage: number;
  onActivePageChange: (i: number) => void;
}) {
  const allSlides = [content.cover, ...content.extraSlides];
  const slide = allSlides[Math.min(activePage, allSlides.length - 1)];
  const isCover = activePage === 0;

  const updateSlide = (patch: Partial<FactsheetSlide>) => {
    if (isCover) {
      onChange({ ...content, cover: { ...content.cover, ...patch } });
      return;
    }
    const i = activePage - 1;
    const next = content.extraSlides.slice();
    next[i] = { ...next[i], ...patch };
    onChange({ ...content, extraSlides: next });
  };

  const addSlide = () => {
    const newSlide: FactsheetSlide = {
      variant: "newsroom",
      eyebrow: `RULE ${String(content.extraSlides.length + 1).padStart(2, "0")} / 09`,
      headline: "NEW RULE\nHEADLINE",
      accentWord: "NEW",
      body: "Short body paragraph explaining this rule in 10–25 words.",
    };
    const nextSlides = [...content.extraSlides, newSlide];
    onChange({ ...content, extraSlides: nextSlides });
    onActivePageChange(nextSlides.length); // jump to the new slide
  };

  const removeSlide = () => {
    if (isCover || content.extraSlides.length === 0) return;
    const i = activePage - 1;
    const next = content.extraSlides.slice();
    next.splice(i, 1);
    onChange({ ...content, extraSlides: next });
    onActivePageChange(Math.max(0, activePage - 1));
  };

  const onImageUpload = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") updateSlide({ imageUrl: result });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      {/* Slide tabs */}
      <div className="rounded-md border bg-slate-50 p-3">
        <div className="text-xs font-semibold text-slate-700 mb-2">
          Slides ({allSlides.length} total) — pick one to edit
        </div>
        <div className="flex flex-wrap gap-2">
          {allSlides.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onActivePageChange(i)}
              className={`px-3 py-1.5 rounded text-xs font-semibold border transition-colors ${
                i === activePage
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
              }`}
            >
              {i === 0 ? "Cover" : `Slide ${i}`}
              <span className="ml-1.5 opacity-70">· {s.variant}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={addSlide}
            className="px-3 py-1.5 rounded text-xs font-semibold border-2 border-dashed border-emerald-400 text-emerald-700 hover:bg-emerald-50"
          >
            + Add slide
          </button>
        </div>
        {!isCover && (
          <button
            type="button"
            onClick={removeSlide}
            className="mt-2 text-[11px] text-red-600 hover:underline"
          >
            Remove slide {activePage}
          </button>
        )}
      </div>

      {/* Variant picker */}
      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Layout variant
        </label>
        <select
          value={slide.variant}
          onChange={(e) =>
            updateSlide({ variant: e.target.value as FactsheetVariant })
          }
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        >
          {(Object.keys(VARIANT_LABEL) as FactsheetVariant[]).map((v) => (
            <option key={v} value={v}>
              {VARIANT_LABEL[v]}
            </option>
          ))}
        </select>
      </div>

      {/* Hero image */}
      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Hero photo (full-bleed background)
        </label>
        <div className="flex items-center gap-2">
          {slide.imageUrl ? (
            <img
              src={slide.imageUrl}
              alt=""
              className="w-16 h-16 rounded object-cover border"
            />
          ) : (
            <div className="w-16 h-16 rounded border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-[10px] text-slate-400">
              none
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onImageUpload(e.target.files?.[0] ?? null)}
            className="text-xs"
          />
          {slide.imageUrl && (
            <button
              type="button"
              onClick={() => updateSlide({ imageUrl: undefined })}
              className="text-[11px] text-red-600 hover:underline"
            >
              Clear
            </button>
          )}
        </div>
        {slide.imageUrl && (
          <div className="mt-3 space-y-3">
            {/* How the image maps onto the card */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-slate-600 block mb-1">
                  Fit
                </label>
                <select
                  value={slide.imageFit ?? "cover"}
                  onChange={(e) =>
                    updateSlide({
                      imageFit: e.target.value as "cover" | "contain",
                    })
                  }
                  className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs"
                >
                  <option value="cover">Fill card (crops edges)</option>
                  <option value="contain">Show whole image</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] text-slate-600 block mb-1">
                  Keep in frame
                </label>
                <select
                  value={slide.imageFocal ?? "center"}
                  onChange={(e) =>
                    updateSlide({
                      imageFocal: e.target.value as
                        | "top"
                        | "center"
                        | "bottom",
                    })
                  }
                  disabled={slide.imageFit === "contain"}
                  className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs disabled:bg-slate-100 disabled:text-slate-400"
                >
                  <option value="top">Top (faces / heads)</option>
                  <option value="center">Centre</option>
                  <option value="bottom">Bottom</option>
                </select>
              </div>
            </div>
            <p className="text-[10px] text-slate-500 -mt-1">
              {slide.imageFit === "contain"
                ? "Whole image is visible; the gaps fill with a blurred copy of it."
                : "Image fills the card — pick what to protect when the edges crop."}
            </p>

            {/* Overall photo brightness */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-slate-600">
                  Photo brightness
                </span>
                <span className="text-[10px] font-mono text-slate-500">
                  {Math.round((slide.imageOpacity ?? 1) * 100)}%
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={slide.imageOpacity ?? 1}
                onChange={(e) =>
                  updateSlide({ imageOpacity: Number(e.target.value) })
                }
                className="w-full accent-emerald-600"
              />
            </div>

            {/* Text readability panel — the important pair. Hidden on split,
                which has no text-over-image overlap to solve. */}
            {slide.variant !== "split" && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 space-y-2.5">
                <div className="text-[11px] font-semibold text-slate-700">
                  Text readability panel
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-slate-600">
                      Darkness behind text
                    </span>
                    <span className="text-[10px] font-mono text-slate-500">
                      {Math.round(
                        (slide.scrimStrength ??
                          FACTSHEET_SCRIM_DEFAULTS[slide.variant].strength) *
                          100,
                      )}
                      %
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={
                      slide.scrimStrength ??
                      FACTSHEET_SCRIM_DEFAULTS[slide.variant].strength
                    }
                    onChange={(e) =>
                      updateSlide({ scrimStrength: Number(e.target.value) })
                    }
                    className="w-full accent-emerald-600"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-slate-600">
                      How far up it reaches
                    </span>
                    <span className="text-[10px] font-mono text-slate-500">
                      {Math.round(
                        (slide.scrimHeight ??
                          FACTSHEET_SCRIM_DEFAULTS[slide.variant].height) *
                          100,
                      )}
                      %
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={
                      slide.scrimHeight ??
                      FACTSHEET_SCRIM_DEFAULTS[slide.variant].height
                    }
                    onChange={(e) =>
                      updateSlide({ scrimHeight: Number(e.target.value) })
                    }
                    className="w-full accent-emerald-600"
                  />
                </div>
                <p className="text-[10px] text-slate-500">
                  Darkens only the band your text sits in, so the rest of the
                  photo stays at full brightness.
                </p>
                <button
                  type="button"
                  onClick={() =>
                    updateSlide({
                      scrimStrength: undefined,
                      scrimHeight: undefined,
                    })
                  }
                  className="text-[10px] text-slate-600 hover:underline"
                >
                  Reset to this layout's default
                </button>
              </div>
            )}

            {/* Split-only: where the image band ends */}
            {slide.variant === "split" && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] text-slate-600">
                    Image band height
                  </span>
                  <span className="text-[10px] font-mono text-slate-500">
                    {Math.round((slide.splitRatio ?? 0.58) * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min={0.25}
                  max={0.8}
                  step={0.02}
                  value={slide.splitRatio ?? 0.58}
                  onChange={(e) =>
                    updateSlide({ splitRatio: Number(e.target.value) })
                  }
                  className="w-full accent-emerald-600"
                />
                <p className="text-[10px] text-slate-500 mt-0.5">
                  How much of the card the photo occupies before the text
                  block starts.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {slide.variant === "pill" && (
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            Pill label (e.g. RULE 02 / 09)
          </label>
          <input
            type="text"
            value={slide.pillLabel ?? ""}
            onChange={(e) => updateSlide({ pillLabel: e.target.value })}
            placeholder="RULE 02 / 09"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
      )}

      {slide.variant === "memorial" && (
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            Dates line (below the name)
          </label>
          <input
            type="text"
            value={slide.datesLine ?? ""}
            onChange={(e) => updateSlide({ datesLine: e.target.value })}
            placeholder="1996 — 2025   ·   or   ·   3 JULY 2025 · ONE YEAR ON"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
          <p className="text-[10px] text-slate-500 mt-1">
            For memorial only. Rendered in small caps under a thin white rule.
            The Eyebrow field above serves as the top "IN LOVING MEMORY" line;
            the Body field takes an optional short tribute quote (italic serif).
          </p>
        </div>
      )}

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Eyebrow (small uppercase tag above headline)
        </label>
        <input
          type="text"
          value={slide.eyebrow ?? ""}
          onChange={(e) => updateSlide({ eyebrow: e.target.value })}
          placeholder="WORLD CUP 2026"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Headline (use \n line breaks for multi-line)
        </label>
        <textarea
          value={slide.headline}
          onChange={(e) => updateSlide({ headline: e.target.value })}
          rows={3}
          placeholder={"FIFA HAS OFFICIALLY\nCONFIRMED THESE\nNEW WORLD CUP RULES"}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
        />
      </div>

      {/* Accent word + colour don't apply to the memorial variant — that's
          intentionally black-and-white. Hide the controls so admin doesn't
          think they'll do something. */}
      {slide.variant !== "memorial" && (
        <>
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">
              Accent word (highlighted in gold inside the headline)
            </label>
            <input
              type="text"
              value={slide.accentWord ?? ""}
              onChange={(e) => updateSlide({ accentWord: e.target.value })}
              placeholder="CONFIRMED"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Wraps the FIRST match (case-insensitive) inside the headline in
              the accent colour. Leave blank for all-white.
            </p>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">
              Accent colour
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={slide.accentColor || "#FFD700"}
                onChange={(e) => updateSlide({ accentColor: e.target.value })}
                className="h-9 w-12 rounded border border-slate-300"
              />
              <input
                type="text"
                value={slide.accentColor || "#FFD700"}
                onChange={(e) =>
                  updateSlide({ accentColor: e.target.value })
                }
                className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
              />
              <button
                type="button"
                onClick={() => updateSlide({ accentColor: undefined })}
                className="text-[11px] text-slate-600 hover:underline"
              >
                Reset
              </button>
            </div>
          </div>
        </>
      )}

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Body paragraph (optional, 10–25 words)
        </label>
        <textarea
          value={slide.body ?? ""}
          onChange={(e) => updateSlide({ body: e.target.value })}
          rows={2}
          placeholder="If a player deliberately delays restarting play, the throw-in is awarded to the opposing team."
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </div>

      <FooterFields
        idPrefix="fs"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("FACTSHEET")}
        onFooterLeftChange={(v) => onChange({ ...content, footerLeft: v })}
        onHashtagsChange={(v) => onChange({ ...content, hashtags: v })}
        footer={content.footer}
        onFooterChange={(v) => onChange({ ...content, footer: v })}
      />

      <HeaderFields
        idPrefix="fs"
        value={content.header}
        onChange={(v) => onChange({ ...content, header: v })}
      />
    </div>
  );
}

// ─── Knockout Bracket form (round picker + seed-from-API + manual rows) ─────

function BracketForm({
  content,
  onChange,
}: {
  content: BracketCardContent;
  onChange: (next: BracketCardContent) => void;
}) {
  const expectedCount = BRACKET_ROUND_MATCH_COUNT[content.round];
  const { data: bracket, isLoading: bracketLoading } = useBracket();

  /** Replace the entire matchups list — also pads to the round's expected count. */
  const setMatchups = (next: BracketMatchup[]) => {
    const padded: BracketMatchup[] = Array.from(
      { length: expectedCount },
      (_, i) => next[i] ?? { homeName: "TBD", awayName: "TBD" },
    );
    onChange({ ...content, matchups: padded });
  };

  const updateMatchup = (i: number, patch: Partial<BracketMatchup>) => {
    const next = content.matchups.slice();
    next[i] = { ...(next[i] ?? { homeName: "TBD", awayName: "TBD" }), ...patch };
    setMatchups(next);
  };

  /** Swap row i with row i+dir. dir = -1 (up) or +1 (down). */
  const moveRow = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= expectedCount) return;
    const padded: BracketMatchup[] = Array.from(
      { length: expectedCount },
      (_, k) => content.matchups[k] ?? { homeName: "TBD", awayName: "TBD" },
    );
    const tmp = padded[i];
    padded[i] = padded[j];
    padded[j] = tmp;
    setMatchups(padded);
  };

  /** Flip home and away within a single row (in case the API has them backwards). */
  const swapHomeAway = (i: number) => {
    const m = content.matchups[i] ?? { homeName: "TBD", awayName: "TBD" };
    updateMatchup(i, {
      homeName: m.awayName,
      homeFlagUrl: m.awayFlagUrl,
      awayName: m.homeName,
      awayFlagUrl: m.homeFlagUrl,
    });
  };

  /**
   * Pull this round's matchups from the backend bracket endpoint and replace
   * the current list. Padded to the expected count — if the backend has
   * fewer matches than the round expects (e.g. R32 not yet scheduled),
   * the remainder stays "TBD" so admin can fill manually.
   */
  const seedFromApi = () => {
    if (!bracket) return;
    const stage = bracket.find((b) => b.phase === content.round);
    if (!stage) {
      alert(
        `No ${BRACKET_ROUND_LABEL[content.round]} matches found in the backend yet.`,
      );
      return;
    }
    const fromApi: BracketMatchup[] = stage.matches.map((m) => ({
      homeName: m.homeNation.name,
      homeFlagUrl: m.homeNation.flagUrl ?? undefined,
      awayName: m.awayNation.name,
      awayFlagUrl: m.awayNation.flagUrl ?? undefined,
    }));
    setMatchups(fromApi);
  };

  const onCenterImageUpload = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string")
        onChange({ ...content, centerImageUrl: result });
    };
    reader.readAsDataURL(file);
  };

  const onRowFlagUpload = (
    i: number,
    side: "home" | "away",
    file: File | null,
  ) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") return;
      updateMatchup(i, {
        [side === "home" ? "homeFlagUrl" : "awayFlagUrl"]: result,
      } as Partial<BracketMatchup>);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      {/* Round picker */}
      <div className="grid grid-cols-1 gap-2">
        <label className="text-xs font-semibold text-slate-700">
          Round (drives row count + seed source)
        </label>
        <select
          value={content.round}
          onChange={(e) => {
            const next = e.target.value as BracketRound;
            // Clear matchups when round changes so the user is forced to
            // re-seed — otherwise a 16-row R32 list would render incorrectly
            // as the top 8 of an R16 card.
            onChange({ ...content, round: next, matchups: [] });
          }}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        >
          {(Object.keys(BRACKET_ROUND_LABEL) as BracketRound[]).map((r) => (
            <option key={r} value={r}>
              {BRACKET_ROUND_LABEL[r]} · {BRACKET_ROUND_MATCH_COUNT[r]} match
              {BRACKET_ROUND_MATCH_COUNT[r] === 1 ? "" : "es"}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            Big title
          </label>
          <input
            type="text"
            value={content.title}
            onChange={(e) => onChange({ ...content, title: e.target.value })}
            placeholder="KO STAGE"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            Subtitle
          </label>
          <input
            type="text"
            value={content.subtitle}
            onChange={(e) =>
              onChange({ ...content, subtitle: e.target.value })
            }
            placeholder="WC 2026"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Center emblem upload */}
      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Center emblem (defaults to FBK shield)
        </label>
        <div className="flex items-center gap-2">
          <img
            src={content.centerImageUrl || "/logo.png"}
            alt=""
            className="w-12 h-12 rounded object-contain border bg-emerald-900"
          />
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onCenterImageUpload(e.target.files?.[0] ?? null)}
            className="text-xs"
          />
          {content.centerImageUrl && (
            <button
              type="button"
              onClick={() => onChange({ ...content, centerImageUrl: undefined })}
              className="text-[11px] text-red-600 hover:underline"
            >
              Reset to FBK shield
            </button>
          )}
        </div>
      </div>

      {/* Seed from API */}
      <div className="rounded-md border bg-emerald-50 border-emerald-200 p-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold text-emerald-900">
              Seed matchups from backend
            </div>
            <p className="text-[11px] text-emerald-800 mt-0.5">
              Pulls the {BRACKET_ROUND_LABEL[content.round].toLowerCase()}{" "}
              fixtures from the matches API. Empty rows stay TBD.
            </p>
          </div>
          <button
            type="button"
            onClick={seedFromApi}
            disabled={bracketLoading || !bracket}
            className="px-3 py-1.5 rounded text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {bracketLoading ? "Loading…" : "Seed from API"}
          </button>
        </div>
      </div>

      {/* Manual matchup rows */}
      <div>
        <div className="text-xs font-semibold text-slate-700 mb-2">
          Matchups ({expectedCount} rows) — rows 1–
          {expectedCount === 1 ? 1 : expectedCount / 2} render in the{" "}
          <span className="text-emerald-700 font-bold">LEFT</span> column;{" "}
          {expectedCount > 1 && (
            <>
              rows {expectedCount / 2 + 1}–{expectedCount} render in the{" "}
              <span className="text-amber-700 font-bold">RIGHT</span> column.
            </>
          )}{" "}
          Use ↑/↓ to fix the order after seeding from API.
        </div>
        <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
          {Array.from({ length: expectedCount }, (_, i) => {
            const m: BracketMatchup =
              content.matchups[i] ?? { homeName: "TBD", awayName: "TBD" };
            const splitAt = expectedCount === 1 ? 0 : expectedCount / 2;
            const isLeftColumn = i < splitAt;
            // Insert a vivid divider exactly at the LEFT/RIGHT split so the
            // admin can see at a glance which matches will land where.
            const showDivider = expectedCount > 1 && i === splitAt;
            return (
              <div key={i}>
                {showDivider && (
                  <div className="my-3 flex items-center gap-2">
                    <div className="flex-1 h-px bg-gradient-to-r from-emerald-200 to-amber-200" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 px-2 py-0.5 rounded-full bg-slate-100">
                      ↑ left column · right column ↓
                    </span>
                    <div className="flex-1 h-px bg-gradient-to-r from-amber-200 to-emerald-200" />
                  </div>
                )}
                <div
                  className={`rounded border bg-white p-2 ${
                    isLeftColumn
                      ? "border-l-4 border-l-emerald-500 border-y-slate-200 border-r-slate-200"
                      : "border-r-4 border-r-amber-500 border-y-slate-200 border-l-slate-200"
                  }`}
                >
                  {/* Row header — index + reorder controls */}
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider ${
                        isLeftColumn ? "text-emerald-700" : "text-amber-700"
                      }`}
                    >
                      #{i + 1} · {isLeftColumn ? "LEFT" : "RIGHT"}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => moveRow(i, -1)}
                        disabled={i === 0}
                        title="Move up"
                        className="p-1 rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveRow(i, 1)}
                        disabled={i === expectedCount - 1}
                        title="Move down"
                        className="p-1 rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => swapHomeAway(i)}
                        title="Swap home ↔ away"
                        className="p-1 rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      >
                        <ArrowLeftRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center text-xs">
                {/* Home side */}
                <div className="flex items-center gap-1.5">
                  {m.homeFlagUrl ? (
                    <img
                      src={m.homeFlagUrl}
                      alt=""
                      className="w-7 h-7 rounded-full object-cover border"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full border border-dashed border-slate-300 bg-slate-50" />
                  )}
                  <input
                    type="text"
                    value={m.homeName}
                    onChange={(e) =>
                      updateMatchup(i, { homeName: e.target.value })
                    }
                    placeholder="Home"
                    className="flex-1 min-w-0 rounded border border-slate-300 px-2 py-1 text-xs"
                  />
                  <label className="text-[10px] text-emerald-700 cursor-pointer hover:underline shrink-0">
                    flag
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        onRowFlagUpload(i, "home", e.target.files?.[0] ?? null)
                      }
                      className="hidden"
                    />
                  </label>
                </div>
                <span className="text-[10px] font-bold text-slate-400">VS</span>
                {/* Away side */}
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={m.awayName}
                    onChange={(e) =>
                      updateMatchup(i, { awayName: e.target.value })
                    }
                    placeholder="Away"
                    className="flex-1 min-w-0 rounded border border-slate-300 px-2 py-1 text-xs"
                  />
                  <label className="text-[10px] text-emerald-700 cursor-pointer hover:underline shrink-0">
                    flag
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        onRowFlagUpload(i, "away", e.target.files?.[0] ?? null)
                      }
                      className="hidden"
                    />
                  </label>
                  {m.awayFlagUrl ? (
                    <img
                      src={m.awayFlagUrl}
                      alt=""
                      className="w-7 h-7 rounded-full object-cover border"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full border border-dashed border-slate-300 bg-slate-50" />
                  )}
                </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <FooterFields
        idPrefix="bk"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("BRACKET")}
        onFooterLeftChange={(v) => onChange({ ...content, footerLeft: v })}
        onHashtagsChange={(v) => onChange({ ...content, hashtags: v })}
        footer={content.footer}
        onFooterChange={(v) => onChange({ ...content, footer: v })}
      />

      <HeaderFields
        idPrefix="bk"
        value={content.header}
        onChange={(v) => onChange({ ...content, header: v })}
      />
    </div>
  );
}

// ─── Champions form (one-click winner swap + celebration copy) ─────────────

const CHAMPIONS_VARIANT_LABEL: Record<ChampionsVariant, string> = {
  glory: "Glory — nation-colour stage, trophy, light rays (no photo needed)",
  portrait: "Portrait — full-bleed celebration photo, lockup in lower third",
};

function ChampionsForm({
  content,
  onChange,
}: {
  content: ChampionsCardContent;
  onChange: (next: ChampionsCardContent) => void;
}) {
  const update = <K extends keyof ChampionsCardContent>(
    key: K,
    value: ChampionsCardContent[K],
  ) => onChange({ ...content, [key]: value });

  /**
   * Apply a finalist preset. Rewrites every nation-derived field in one go —
   * colours, name, code, star count, title label. Deliberately does NOT
   * touch scoreLine / message / flagUrl / heroImageUrl, so an admin who has
   * already written the copy and uploaded a flag doesn't lose that work
   * when flipping between winners while the match is still live.
   */
  const applyPreset = (p: ChampionsNationPreset) =>
    onChange({
      ...content,
      nationName: p.name,
      nationCode: p.code,
      primaryColor: p.primary,
      secondaryColor: p.secondary,
      accentColor: p.accent,
      starCount: p.titlesAfterWin,
      titleCountLabel: p.titleLabel,
    });

  const uploadImage = (
    field: "flagUrl" | "heroImageUrl",
    file: File | null,
  ) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") update(field, result);
    };
    reader.readAsDataURL(file);
  };

  const isActivePreset = (p: ChampionsNationPreset) =>
    content.nationCode === p.code;

  return (
    <div className="space-y-4">
      {/* Winner picker — the whole point of this template */}
      <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-3">
        <div className="text-xs font-bold text-amber-900 mb-1">
          🏆 Who won?
        </div>
        <p className="text-[11px] text-amber-800 mb-2.5">
          One click re-skins the card — colours, flag border, stars and title
          count all follow. Your score line and message are kept.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {CHAMPIONS_NATION_PRESETS.map((p) => {
            const active = isActivePreset(p);
            return (
              <button
                key={p.code}
                type="button"
                onClick={() => applyPreset(p)}
                className={`rounded-lg border-2 p-2.5 text-left transition-all ${
                  active
                    ? "border-amber-500 bg-white shadow-md"
                    : "border-slate-200 bg-white/60 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span
                    className="inline-block w-4 h-4 rounded-sm border border-black/10"
                    style={{ backgroundColor: p.primary }}
                  />
                  <span className="text-sm font-bold text-slate-900">
                    {p.name}
                  </span>
                  {active && (
                    <span className="ml-auto text-[9px] font-extrabold text-amber-700 uppercase tracking-wide">
                      Selected
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500">
                  {"★".repeat(p.titlesAfterWin)} · {p.titleLabel}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Layout
        </label>
        <select
          value={content.variant}
          onChange={(e) =>
            update("variant", e.target.value as ChampionsVariant)
          }
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        >
          {(Object.keys(CHAMPIONS_VARIANT_LABEL) as ChampionsVariant[]).map(
            (v) => (
              <option key={v} value={v}>
                {CHAMPIONS_VARIANT_LABEL[v]}
              </option>
            ),
          )}
        </select>
      </div>

      {/* Score — the one field that can't be pre-filled */}
      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Final score
        </label>
        <input
          type="text"
          value={content.scoreLine ?? ""}
          onChange={(e) => update("scoreLine", e.target.value)}
          placeholder="ARGENTINA 2 — 1 SPAIN"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
        <p className="text-[10px] text-slate-500 mt-1">
          Leave blank to hide the score chip entirely — useful if you want the
          poster up before the result is official.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            Eyebrow
          </label>
          <input
            type="text"
            value={content.eyebrow}
            onChange={(e) => update("eyebrow", e.target.value)}
            placeholder="FIFA WORLD CUP 2026 · FINAL"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            Hero word
          </label>
          <input
            type="text"
            value={content.championLabel}
            onChange={(e) => update("championLabel", e.target.value)}
            placeholder="CHAMPIONS"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
          <p className="text-[10px] text-slate-500 mt-1">
            Try "CAMPEONES" for a Spanish-language edit.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            Nation name
          </label>
          <input
            type="text"
            value={content.nationName}
            onChange={(e) => update("nationName", e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            Title line
          </label>
          <input
            type="text"
            value={content.titleCountLabel ?? ""}
            onChange={(e) => update("titleCountLabel", e.target.value)}
            placeholder="4TH WORLD TITLE"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Stars ({content.starCount ?? 0})
        </label>
        <input
          type="range"
          min={0}
          max={8}
          step={1}
          value={content.starCount ?? 0}
          onChange={(e) => update("starCount", Number(e.target.value))}
          className="w-full accent-amber-500"
        />
        <p className="text-[10px] text-slate-500 mt-0.5">
          One gold star per World Cup held after this win. Set to 0 to hide
          the row.
        </p>
      </div>

      {/* Flag */}
      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Winner's flag
        </label>
        <div className="flex items-center gap-2">
          {content.flagUrl ? (
            <img
              src={content.flagUrl}
              alt=""
              className="w-14 h-10 rounded object-cover border"
            />
          ) : (
            <div className="w-14 h-10 rounded border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-[9px] text-slate-400">
              none
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => uploadImage("flagUrl", e.target.files?.[0] ?? null)}
            className="text-xs"
          />
          {content.flagUrl && (
            <button
              type="button"
              onClick={() => update("flagUrl", undefined)}
              className="text-[11px] text-red-600 hover:underline"
            >
              Clear
            </button>
          )}
        </div>
        <p className="text-[10px] text-slate-500 mt-1">
          Tip: upload both finalists' flags to your desktop before kick-off so
          you can drop the right one in instantly.
        </p>
      </div>

      {/* Hero image */}
      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          {content.variant === "portrait"
            ? "Celebration photo (full-bleed background)"
            : "Trophy image (centred above the wordmark)"}
        </label>
        <div className="flex items-center gap-2">
          {content.heroImageUrl ? (
            <img
              src={content.heroImageUrl}
              alt=""
              className="w-14 h-14 rounded object-contain border bg-slate-900"
            />
          ) : (
            <div className="w-14 h-14 rounded border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-[9px] text-slate-400">
              none
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              uploadImage("heroImageUrl", e.target.files?.[0] ?? null)
            }
            className="text-xs"
          />
          {content.heroImageUrl && (
            <button
              type="button"
              onClick={() => update("heroImageUrl", undefined)}
              className="text-[11px] text-red-600 hover:underline"
            >
              Clear
            </button>
          )}
        </div>
        {content.variant === "portrait" && content.heroImageUrl && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-slate-600">
                Photo brightness
              </span>
              <span className="text-[10px] font-mono text-slate-500">
                {Math.round((content.heroImageOpacity ?? 0.85) * 100)}%
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={content.heroImageOpacity ?? 0.85}
              onChange={(e) =>
                update("heroImageOpacity", Number(e.target.value))
              }
              className="w-full accent-amber-500"
            />
          </div>
        )}
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Message
        </label>
        <textarea
          value={content.message ?? ""}
          onChange={(e) => update("message", e.target.value)}
          rows={2}
          placeholder="Champions of the world. Every tackle, every goal, every heartbeat — earned."
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Date / venue line
        </label>
        <input
          type="text"
          value={content.dateLine ?? ""}
          onChange={(e) => update("dateLine", e.target.value)}
          placeholder="19 JULY 2026 · METLIFE STADIUM"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </div>

      {/* Manual colour overrides — presets cover the finalists, but a
          future final might need hand-tuning. */}
      <details className="rounded-md border border-slate-200 p-3">
        <summary className="text-xs font-semibold text-slate-700 cursor-pointer">
          Advanced — override nation colours
        </summary>
        <div className="mt-3 space-y-2">
          {(
            [
              ["primaryColor", "Primary (stage gradient)"],
              ["secondaryColor", "Secondary"],
              ["accentColor", "Accent (wordmark + stars)"],
            ] as const
          ).map(([key, label]) => (
            <div key={key} className="flex items-center gap-2">
              <span className="text-[11px] text-slate-600 w-44 shrink-0">
                {label}
              </span>
              <input
                type="color"
                value={content[key]}
                onChange={(e) => update(key, e.target.value)}
                className="h-8 w-11 rounded border border-slate-300"
              />
              <input
                type="text"
                value={content[key]}
                onChange={(e) => update(key, e.target.value)}
                className="flex-1 rounded border border-slate-300 px-2 py-1 text-xs font-mono"
              />
            </div>
          ))}
        </div>
      </details>

      <FooterFields
        idPrefix="ch"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("CHAMPIONS")}
        onFooterLeftChange={(v) => update("footerLeft", v)}
        onHashtagsChange={(v) => update("hashtags", v)}
        footer={content.footer}
        onFooterChange={(v) => update("footer", v)}
      />

      <HeaderFields
        idPrefix="ch"
        value={content.header}
        onChange={(v) => update("header", v)}
      />
    </div>
  );
}

// ─── Thank You form (community wrap — seeds real stats + username wall) ────

const THANK_YOU_THEME_LABEL: Record<ThankYouTheme, string> = {
  gratitude: "Gratitude — FBK green + gold, drifting confetti (default)",
  rollcall: "Roll call — real usernames tiled behind the headline",
  wrapped: "Wrapped — oversized stat numbers lead, bold colour blocking",
  curtain: "Curtain call — cinematic black, single spotlight, restrained",
};

function ThankYouForm({
  content,
  onChange,
}: {
  content: ThankYouCardContent;
  onChange: (next: ThankYouCardContent) => void;
}) {
  const update = <K extends keyof ThankYouCardContent>(
    key: K,
    value: ThankYouCardContent[K],
  ) => onChange({ ...content, [key]: value });

  const [seeding, setSeeding] = useState(false);

  const updateStat = (i: number, patch: Partial<ThankYouStat>) => {
    const next = content.stats.slice();
    next[i] = { ...(next[i] ?? { value: "", label: "" }), ...patch };
    update("stats", next);
  };

  /**
   * Pull the real community numbers so the post says something specific
   * instead of a generic thank-you. Sources:
   *   - /admin/dashboard/stats     → totalUsers
   *   - /fantasy-leaderboard/stats → totalTeams, highestPoints, averagePoints
   *
   * Each stat is written independently, so a partial failure (one endpoint
   * down) still fills what it can rather than aborting the whole seed.
   */
  const seedStats = async () => {
    setSeeding(true);
    try {
      const [dash, fantasy] = await Promise.allSettled([
        apiClient.get(API_ENDPOINTS.DASHBOARD.STATS) as Promise<any>,
        apiClient.get(API_ENDPOINTS.FANTASYLEADERBOARD.STATS) as Promise<any>,
      ]);

      const fmt = (n: unknown) =>
        typeof n === "number" ? n.toLocaleString() : "—";

      const dashData = dash.status === "fulfilled" ? dash.value : null;
      const fantasyData =
        fantasy.status === "fulfilled" ? fantasy.value : null;

      const next: ThankYouStat[] = [
        { value: fmt(dashData?.totalUsers), label: "Managers" },
        { value: fmt(fantasyData?.totalTeams), label: "Teams picked" },
        { value: fmt(fantasyData?.highestPoints), label: "Top score" },
        { value: fmt(fantasyData?.averagePoints), label: "Avg points" },
      ];
      update("stats", next);

      if (dash.status === "rejected" && fantasy.status === "rejected") {
        toast.error("Couldn't reach either stats endpoint.");
      } else {
        toast.success("Stats seeded from live data.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to seed stats.");
    } finally {
      setSeeding(false);
    }
  };

  /**
   * Fetch usernames for the roll-call wall. Grabs a single large page —
   * the wall repeats whatever it gets to fill the canvas, so ~200 names
   * already reads as dense without paginating the whole user table.
   */
  const seedNames = async () => {
    setSeeding(true);
    try {
      const res: any = await apiClient.get(
        `${API_ENDPOINTS.USERS.LIST}?page=1&limit=200`,
      );
      const users: any[] = Array.isArray(res?.users) ? res.users : [];
      const names = users
        .map((u) => u.username || u.name)
        .filter((n: unknown): n is string => typeof n === "string" && !!n.trim())
        .map((n) => n.trim());
      if (names.length === 0) {
        toast.error("No usernames came back.");
        return;
      }
      update("names", names);
      toast.success(`Seeded ${names.length} names into the wall.`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load usernames.");
    } finally {
      setSeeding(false);
    }
  };

  const onHeroUpload = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") update("heroImageUrl", result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      {/* Seed panel — the thing that makes this post land */}
      <div className="rounded-md border-2 border-rose-200 bg-rose-50 p-3 space-y-2">
        <div className="text-xs font-bold text-rose-900">
          💗 Make it specific
        </div>
        <p className="text-[11px] text-rose-800">
          A thank-you with real numbers beats a generic one. Seed the stat row
          from live data, and (for the Roll call theme) pull real usernames
          into the background wall.
        </p>
        <div className="flex flex-wrap gap-2 pt-0.5">
          <button
            type="button"
            onClick={seedStats}
            disabled={seeding}
            className="px-3 py-1.5 rounded text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {seeding ? "Loading…" : "Seed stats"}
          </button>
          <button
            type="button"
            onClick={seedNames}
            disabled={seeding}
            className="px-3 py-1.5 rounded text-xs font-semibold bg-white text-rose-700 border border-rose-300 hover:bg-rose-100 disabled:opacity-50"
          >
            {seeding ? "Loading…" : "Seed usernames"}
          </button>
          {content.names.length > 0 && (
            <span className="text-[11px] text-rose-700 self-center">
              {content.names.length} names loaded
            </span>
          )}
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Theme
        </label>
        <select
          value={content.theme}
          onChange={(e) => update("theme", e.target.value as ThankYouTheme)}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        >
          {(Object.keys(THANK_YOU_THEME_LABEL) as ThankYouTheme[]).map((t) => (
            <option key={t} value={t}>
              {THANK_YOU_THEME_LABEL[t]}
            </option>
          ))}
        </select>
        {content.theme === "rollcall" && content.names.length === 0 && (
          <p className="text-[10px] text-amber-700 mt-1">
            Roll call needs usernames — hit “Seed usernames” above, or the
            background will be empty.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            Eyebrow
          </label>
          <input
            type="text"
            value={content.eyebrow}
            onChange={(e) => update("eyebrow", e.target.value)}
            placeholder="WORLD CUP 2026 · THAT'S A WRAP"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            Headline
          </label>
          <input
            type="text"
            value={content.headline}
            onChange={(e) => update("headline", e.target.value)}
            placeholder="THANK YOU"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Sub-headline
        </label>
        <input
          type="text"
          value={content.subheadline ?? ""}
          onChange={(e) => update("subheadline", e.target.value)}
          placeholder="FOR MAKING THIS UNFORGETTABLE"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Message
        </label>
        <textarea
          value={content.message ?? ""}
          onChange={(e) => update("message", e.target.value)}
          rows={4}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </div>

      {/* Stat tiles */}
      <div>
        <div className="text-xs font-semibold text-slate-700 mb-2">
          Stat row (up to 4 — leave a value blank to drop the tile)
        </div>
        <div className="space-y-2">
          {Array.from({ length: 4 }, (_, i) => {
            const s = content.stats[i] ?? { value: "", label: "" };
            return (
              <div key={i} className="grid grid-cols-[1fr_1.4fr] gap-2">
                <input
                  type="text"
                  value={s.value}
                  onChange={(e) => updateStat(i, { value: e.target.value })}
                  placeholder="12,480"
                  className="rounded border border-slate-300 px-2 py-1.5 text-sm font-bold"
                />
                <input
                  type="text"
                  value={s.label}
                  onChange={(e) => updateStat(i, { label: e.target.value })}
                  placeholder="Managers"
                  className="rounded border border-slate-300 px-2 py-1.5 text-sm"
                />
              </div>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() =>
            update(
              "stats",
              content.stats.filter((s) => s.value.trim() && s.label.trim()),
            )
          }
          className="mt-2 text-[11px] text-slate-600 hover:underline"
        >
          Remove empty tiles
        </button>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Signature
        </label>
        <input
          type="text"
          value={content.signature ?? ""}
          onChange={(e) => update("signature", e.target.value)}
          placeholder="— Sandeep & the FBK team"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
        <p className="text-[10px] text-slate-500 mt-1">
          Signing with your own name reads far warmer than a faceless brand
          sign-off.
        </p>
      </div>

      {/* Optional background photo */}
      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Background photo (optional)
        </label>
        <div className="flex items-center gap-2">
          {content.heroImageUrl ? (
            <img
              src={content.heroImageUrl}
              alt=""
              className="w-16 h-16 rounded object-cover border"
            />
          ) : (
            <div className="w-16 h-16 rounded border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-[10px] text-slate-400">
              none
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onHeroUpload(e.target.files?.[0] ?? null)}
            className="text-xs"
          />
          {content.heroImageUrl && (
            <button
              type="button"
              onClick={() => update("heroImageUrl", undefined)}
              className="text-[11px] text-red-600 hover:underline"
            >
              Clear
            </button>
          )}
        </div>
        {content.heroImageUrl && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-slate-600">
                Photo brightness
              </span>
              <span className="text-[10px] font-mono text-slate-500">
                {Math.round((content.heroImageOpacity ?? 0.35) * 100)}%
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={content.heroImageOpacity ?? 0.35}
              onChange={(e) =>
                update("heroImageOpacity", Number(e.target.value))
              }
              className="w-full accent-rose-500"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Kept low by default — this is a texture behind the type, not the
              subject.
            </p>
          </div>
        )}
      </div>

      <FooterFields
        idPrefix="ty"
        footerLeft={content.footerLeft}
        hashtags={content.hashtags}
        defaultFooter={defaultFooterLeft("THANK_YOU")}
        onFooterLeftChange={(v) => update("footerLeft", v)}
        onHashtagsChange={(v) => update("hashtags", v)}
        footer={content.footer}
        onFooterChange={(v) => update("footer", v)}
      />

      <HeaderFields
        idPrefix="ty"
        value={content.header}
        onChange={(v) => update("header", v)}
      />
    </div>
  );
}
